import { Fragment, useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from 'react-select';
import "./Style.css";
import { FaPlay, FaPause, FaPlus, FaHistory, FaArrowLeft, FaUser, FaForward, FaBackward, FaMoneyBillWave, FaUsers, FaSearch, FaTrash, FaToggleOn, FaToggleOff, FaDollarSign, FaTimes, FaArrowCircleUp, FaInfo, FaRegTrashAlt, FaCoffee } from "react-icons/fa";
import { TournamentActivityList, formatTournamentTransactionType } from "./TournamentActivityList";

const calculatePool = (cost, rake, tip, extra = 0) => {
    return (Number(cost) || 0) - (Number(rake) || 0) - (Number(tip) || 0) - (Number(extra) || 0);
};

const GAME_TYPE_OPTIONS = ["NLH", "PLO"];
const DEFAULT_DISPLAY_SETTINGS = {
    playersLeft: true,
    avgStack: true,
    entries: true,
    buyinsNum: true,
    rebuysNum: true,
    addonsNum: true,
    bonusNum: true,
    totalChips: true,
    totalPot: true,
    placesPaid: true,
    prizes: true
};
const CUSTOM_ALLOCATION_ACTIONS = [
    "buyin",
    "buyin_addon",
    "bonus",
    "rebuy",
    "rebuy_addon",
    "addon",
];

const parseCustomAllocations = (value) => {
    if (!value) return [];
    let parsed = value;
    if (typeof value === "string") {
        try {
            parsed = JSON.parse(value);
        } catch (err) {
            return [];
        }
    }
    if (!Array.isArray(parsed)) return [];
    return parsed.map((allocation, index) => ({
        id: String(allocation?.id || `allocation_${index + 1}`),
        name: String(allocation?.name || `Allocation ${index + 1}`),
        amounts: CUSTOM_ALLOCATION_ACTIONS.reduce((acc, action) => {
            acc[action] = Number(allocation?.amounts?.[action]) || 0;
            return acc;
        }, {}),
        total: Number(allocation?.total) || 0,
    }));
};

const getCustomAllocationActionTotal = (allocations, action) =>
    (allocations || []).reduce((sum, allocation) => sum + (Number(allocation.amounts?.[action]) || 0), 0);

const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const input = String(phone).replace(/\D/g, "");
    if (input.length > 3 && input.length <= 6) {
        return `${input.slice(0, 3)}-${input.slice(3)}`;
    }
    if (input.length > 6) {
        return `${input.slice(0, 3)}-${input.slice(3, 6)}-${input.slice(6, 10)}`;
    }
    return input;
};

const normalizePlayerSearchValue = (value) =>
    String(value || "").toLowerCase().trim();

const getPlayerSearchText = (player = {}) =>
    [
        player.name,
        player.nickname,
        player.first_name,
        player.last_name,
        player.firstName,
        player.lastName,
    ]
        .map(normalizePlayerSearchValue)
        .filter(Boolean)
        .join(" ");

const getPlayerFullName = (player = {}) =>
    [player.first_name || player.firstName, player.last_name || player.lastName]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(" ");

const getPlayerLastPlayedTournament = (player = {}) =>
    player.last_played_tournament || player.lastPlayedTournament || null;

const getPlayerOption = (player = {}, { showTournamentHistoryWarning = false } = {}) => ({
    label: player.name,
    value: player.id ?? player.player_id,
    fullName: getPlayerFullName(player),
    searchText: getPlayerSearchText(player),
    lastPlayedTournament: getPlayerLastPlayedTournament(player),
    showTournamentHistoryWarning,
});

const filterPlayerOption = ({ data }, inputValue) => {
    const searchValue = normalizePlayerSearchValue(inputValue);
    if (!searchValue) return true;
    return data.searchText?.includes(searchValue);
};

const formatPlayerOptionLabel = ({ label, fullName, lastPlayedTournament, showTournamentHistoryWarning }) => {
    const showFullName =
        fullName &&
        normalizePlayerSearchValue(fullName) !== normalizePlayerSearchValue(label);
    const hasTournamentHistory = Boolean(lastPlayedTournament);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.18rem", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", minWidth: 0 }}>
                <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {label}
                </span>
                {showFullName && (
                    <span style={{ color: "#a0aec0", fontSize: "0.82rem", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        ({fullName})
                    </span>
                )}
            </div>
            {showTournamentHistoryWarning && !hasTournamentHistory && (
                <span style={{ color: "#f6ad55", fontSize: "0.74rem", fontWeight: 700 }}>
                    No tournament history
                </span>
            )}
        </div>
    );
};

const getDefaultPlayerNickname = (firstName = "", lastName = "") => {
    const first = String(firstName || "").trim();
    const last = String(lastName || "").trim();
    if (!first) return "Nickname";
    return last ? `${first} ${last.slice(0, 2)}` : first;
};

const handleNumberFocus = (e) => {
    if (String(e.target.value) === "0") {
        e.target.value = "";
        return;
    }
    e.target.select();
};

const preventNumberInputWheel = (e) => {
    if (e.target?.tagName === "INPUT" && e.target.type === "number") {
        e.preventDefault();
        e.target.blur();
    }
};

const tournamentSettingFieldStyle = { minWidth: 0 };
const tournamentSettingInputStyle = { width: "100%", minWidth: 0, boxSizing: "border-box" };

// Defined completely outside the main component so it never loses reference
const SettingRow = ({ title, prefix, action, settingValues, handleSettingChange, customAllocations, handleCustomAllocationChange, bountyAmount = 0 }) => (
    <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--game-text-secondary)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {title}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '1rem', alignItems: 'end' }}>
            <div style={tournamentSettingFieldStyle}>
                <label className="game-label" style={{ fontSize: '0.75rem' }}>Total Cost ($)</label>
                <input type="number" className="game-input" style={tournamentSettingInputStyle} value={settingValues[`${prefix}Cost`] ?? 0} onFocus={handleNumberFocus} onWheel={preventNumberInputWheel} onChange={e => handleSettingChange(`${prefix}Cost`, e.target.value)} />
            </div>
            <div style={tournamentSettingFieldStyle}>
                <label className="game-label" style={{ fontSize: '0.75rem' }}>Rake ($)</label>
                <input type="number" className="game-input" style={tournamentSettingInputStyle} value={settingValues[`${prefix}Rake`] ?? 0} onFocus={handleNumberFocus} onWheel={preventNumberInputWheel} onChange={e => handleSettingChange(`${prefix}Rake`, e.target.value)} />
            </div>
            <div style={tournamentSettingFieldStyle}>
                <label className="game-label" style={{ fontSize: '0.75rem' }}>Staff Tip ($)</label>
                <input type="number" className="game-input" style={tournamentSettingInputStyle} value={settingValues[`${prefix}Tips`] ?? 0} onFocus={handleNumberFocus} onWheel={preventNumberInputWheel} onChange={e => handleSettingChange(`${prefix}Tips`, e.target.value)} />
            </div>
            {(customAllocations || []).map((allocation) => (
                <div key={allocation.id} style={tournamentSettingFieldStyle}>
                    <label className="game-label" style={{ fontSize: '0.75rem' }}>{allocation.name} ($)</label>
                    <input
                        type="number"
                        className="game-input"
                        style={tournamentSettingInputStyle}
                        value={allocation.amounts?.[action] ?? 0}
                        onFocus={handleNumberFocus}
                        onWheel={preventNumberInputWheel}
                        onChange={e => handleCustomAllocationChange(allocation.id, action, e.target.value)}
                    />
                </div>
            ))}
            {Number(bountyAmount || 0) > 0 && (action === "buyin" || action === "rebuy") && (
                <div style={tournamentSettingFieldStyle}>
                    <label className="game-label" style={{ fontSize: '0.75rem' }}>Bounty ($)</label>
                    <input
                        type="number"
                        className="game-input"
                        value={Number(bountyAmount) || 0}
                        readOnly
                        tabIndex={-1}
                        style={{
                            ...tournamentSettingInputStyle,
                            background: "rgba(244,63,94,0.1)",
                            borderColor: "rgba(244,63,94,0.35)",
                            color: "#fecdd3",
                            cursor: "not-allowed"
                        }}
                        onWheel={preventNumberInputWheel}
                    />
                </div>
            )}
            <div style={tournamentSettingFieldStyle}>
                <label className="game-label" style={{ fontSize: '0.75rem' }}>Chips Given</label>
                <input type="number" className="game-input" style={tournamentSettingInputStyle} value={settingValues[`${prefix}Chips`] ?? 0} onFocus={handleNumberFocus} onWheel={preventNumberInputWheel} onChange={e => handleSettingChange(`${prefix}Chips`, e.target.value)} />
            </div>
            <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '65%', padding: '0.5rem', background: 'rgba(72,187,120,0.1)', borderRadius: '4px', textAlign: 'center', border: '1px solid rgba(72,187,120,0.2)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: '#48bb78', textTransform: 'uppercase', marginBottom: '0.1rem' }}>To Prize Pool</div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#48bb78' }}>
                        ${calculatePool(
                            settingValues[`${prefix}Cost`],
                            settingValues[`${prefix}Rake`],
                            settingValues[`${prefix}Tips`],
                            getCustomAllocationActionTotal(customAllocations, action) + ((action === "buyin" || action === "rebuy") ? bountyAmount : 0)
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export default function TournamentManager() {
  const { cardroom, id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  
  // Data
  const [activeTab, setActiveTab] = useState('dashboard');
  const [playerSearch, setPlayerSearch] = useState('');
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]); // For search mainly, but usually we search dynamically
  const [currSession, setCurrSession] = useState(null);
  const [tournamentWallets, setTournamentWallets] = useState([]); // NEW
  
  // Timer
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);
  const isAdvancingRef = useRef(false);
  const displaySettingsSaveTimerRef = useRef(null);
  const pendingDisplaySettingsRef = useRef(null);

  // Transaction Form
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddContext, setQuickAddContext] = useState('transaction');
  const [qFirstName, setQFirstName] = useState('');
  const [qLastName, setQLastName] = useState('');
  const [qNickname, setQNickname] = useState('');
  const [qPhone, setQPhone] = useState('');
  const [quickAddDuplicatePlayers, setQuickAddDuplicatePlayers] = useState([]);
  const [transType, setTransType] = useState('buyin'); // buyin, rebuy, addon
  const [isBonus, setIsBonus] = useState(false);
  const [isAddOn, setIsAddOn] = useState(false);
  const [isCredit, setIsCredit] = useState(false);
  const [isGiveaway, setIsGiveaway] = useState(false);
  const [rebuyEliminatedById, setRebuyEliminatedById] = useState('');
  const [buyinPreset, setBuyinPreset] = useState({
      bonus: false,
      addOn: false,
      credit: false,
      giveaway: false
  });
  const [rebuyPreset, setRebuyPreset] = useState({
      addOn: false,
      credit: false,
      giveaway: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactionMethod, setTransactionMethod] = useState('cash'); // NEW
  const [transactionWalletId, setTransactionWalletId] = useState(''); // NEW
  const [creditPaybackModal, setCreditPaybackModal] = useState({ isOpen: false, playerId: null, playerName: '', maxCredit: 0, currentCredit: 0, amount: '', method: 'cash', walletId: '' });
  const [winnerTipModal, setWinnerTipModal] = useState({ isOpen: false, playerId: '', amount: '', payment: 'cash' });
  const [cashRefillModal, setCashRefillModal] = useState({ isOpen: false, walletId: '', amount: '' });
  
  // Table Interactions & Modal
  const [newTableName, setNewTableName] = useState('');
  const [tableModal, setTableModal] = useState({ isOpen: false, type: 'buyin', playerId: null, presetTable: null, presetSeat: null, method: 'cash', walletId: '' });
  const [eliminationModal, setEliminationModal] = useState({ isOpen: false, playerId: null, playerName: '', isCashout: false, removeOnly: false, payoutAmount: 0, method: 'cash', walletId: '', settleCredit: false, playerCredit: 0, eliminatedById: '' });
  const [deleteTableModal, setDeleteTableModal] = useState({ isOpen: false, tableId: null, tableName: '' });
  const [undoTransactionModal, setUndoTransactionModal] = useState({ isOpen: false, transaction: null });
  const [finalTableModal, setFinalTableModal] = useState({ isOpen: false, tableId: null, tableName: '', isRandomizeOnly: false });
  const [endTournamentModal, setEndTournamentModal] = useState({ isOpen: false });
  
  // Bulk Add-ons Selection
  const [isAddonMode, setIsAddonMode] = useState(false);
  const [selectedAddonPlayers, setSelectedAddonPlayers] = useState([]);
  const [addonModal, setAddonModal] = useState({ isOpen: false, isCredit: false });
  const [walletHistoryModal, setWalletHistoryModal] = useState({ isOpen: false, walletId: null, walletName: '' });
  const [playerHistoryModal, setPlayerHistoryModal] = useState({ isOpen: false, playerId: null, playerName: '' });
  const [playerToRegister, setPlayerToRegister] = useState(null);
  const [playersToRegister, setPlayersToRegister] = useState([]);
  const [hasTableHorizontalScroll, setHasTableHorizontalScroll] = useState(false);
  const [tablesPlayerListMode, setTablesPlayerListMode] = useState('unseated');

  // Tournament Costs
  const [costs, setCosts] = useState([]);
  const [showCostsModal, setShowCostsModal] = useState(false);
  const [costAmount, setCostAmount] = useState('');
  const [costDescription, setCostDescription] = useState('');
  const [costPayment, setCostPayment] = useState('cash');

  // Payout Edit Modal
  const [payoutEditModal, setPayoutEditModal] = useState({ isOpen: false });
  const [editablePayouts, setEditablePayouts] = useState([]);
  const [icmChopModal, setIcmChopModal] = useState({ isOpen: false, stacks: {} });

  // Structure & Settings Modals
  const [structureModal, setStructureModal] = useState({ isOpen: false });
  const [editLevels, setEditLevels] = useState([]);
  const [matchAnteToBB, setMatchAnteToBB] = useState(false);
  const [mixedGames, setMixedGames] = useState(false);
  const [draggingLevelIndex, setDraggingLevelIndex] = useState(null);
  const [dragOriginIndex, setDragOriginIndex] = useState(null);
  const [dragOverLevelIndex, setDragOverLevelIndex] = useState(null);
  const [isCashBagHistoryOpen, setIsCashBagHistoryOpen] = useState(false);

  const [settingsModal, setSettingsModal] = useState({ isOpen: false });
  const [settingValues, setSettingValues] = useState({});

  const scrollContainerRef = useRef(null);
  const scrollRef = useRef({ speed: 0, animationFrameId: null });
  const draggingLevelIndexRef = useRef(null);
  const bodyScrollLockRef = useRef(null);

  const scrollTablesBy = (amount) => {
      if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollBy({ left: amount, behavior: 'smooth' });
          requestAnimationFrame(updateTableScrollAvailability);
      }
  };

  const updateTableScrollAvailability = () => {
      const container = scrollContainerRef.current;
      if (!container) {
          setHasTableHorizontalScroll(false);
          return;
      }
      setHasTableHorizontalScroll(container.scrollWidth > container.clientWidth + 8);
  };

  const startScrolling = () => {
      if (!scrollRef.current.animationFrameId) {
          const scroll = () => {
              if (scrollRef.current.speed !== 0 && scrollContainerRef.current) {
                  scrollContainerRef.current.scrollLeft += scrollRef.current.speed;
                  scrollRef.current.animationFrameId = requestAnimationFrame(scroll);
              } else {
                  scrollRef.current.animationFrameId = null;
              }
          };
          scrollRef.current.animationFrameId = requestAnimationFrame(scroll);
      }
  };

  const stopScrolling = () => {
      scrollRef.current.speed = 0;
      if (scrollRef.current.animationFrameId) {
          cancelAnimationFrame(scrollRef.current.animationFrameId);
          scrollRef.current.animationFrameId = null;
      }
  };

  const isAnyModalOpen =
      showQuickAddModal ||
      creditPaybackModal.isOpen ||
      winnerTipModal.isOpen ||
      cashRefillModal.isOpen ||
      tableModal.isOpen ||
      eliminationModal.isOpen ||
      deleteTableModal.isOpen ||
      undoTransactionModal.isOpen ||
      finalTableModal.isOpen ||
      endTournamentModal.isOpen ||
      addonModal.isOpen ||
      walletHistoryModal.isOpen ||
      playerHistoryModal.isOpen ||
      showCostsModal ||
      payoutEditModal.isOpen ||
      icmChopModal.isOpen ||
      structureModal.isOpen ||
      settingsModal.isOpen ||
      isCashBagHistoryOpen;

  const unlockBodyScroll = () => {
      const lock = bodyScrollLockRef.current;
      if (!lock) return;

      document.body.style.overflow = lock.bodyOverflow;
      document.body.style.position = lock.bodyPosition;
      document.body.style.top = lock.bodyTop;
      document.body.style.left = lock.bodyLeft;
      document.body.style.right = lock.bodyRight;
      document.body.style.width = lock.bodyWidth;
      document.documentElement.style.overflow = lock.htmlOverflow;
      window.scrollTo(0, lock.scrollY);
      bodyScrollLockRef.current = null;
  };

  const lockBodyScroll = () => {
      if (bodyScrollLockRef.current) return;

      const scrollY = window.scrollY || window.pageYOffset || 0;
      bodyScrollLockRef.current = {
          scrollY,
          bodyOverflow: document.body.style.overflow,
          bodyPosition: document.body.style.position,
          bodyTop: document.body.style.top,
          bodyLeft: document.body.style.left,
          bodyRight: document.body.style.right,
          bodyWidth: document.body.style.width,
          htmlOverflow: document.documentElement.style.overflow,
      };

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
  };

  // Auth & Load
  useEffect(() => {
    if (!token) { navigate("/"); return; }
    if (dealerMode === 2) { navigate(`/${cardroom}/dealerdashboard`); return; }
    if (!permission?.includes("O") && !permission?.includes("Y")) { navigate(`/${cardroom}/game`); return; }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    loadData();
    
    // Polling for updates (primitive sync)
    //const interval = setInterval(loadData, 5000); 
    //return () => clearInterval(interval);
  }, [token, id, cardroom, navigate, dealerMode, permission]);

  useEffect(() => {
      return () => {
          if (displaySettingsSaveTimerRef.current) {
              clearTimeout(displaySettingsSaveTimerRef.current);
          }
          const settingsToSave = pendingDisplaySettingsRef.current;
          if (settingsToSave) {
              axios.post(`/${cardroom}/api/tournaments/${id}/display-settings`, {
                  display_settings: settingsToSave
              }).catch(err => console.error("Display settings error:", err));
          }
      };
  }, [cardroom, id]);

  // Prevent page scroll while any tournament manager modal is open.
  useEffect(() => {
      if (isAnyModalOpen) {
          lockBodyScroll();
      } else {
          unlockBodyScroll();
      }

      return () => {
          unlockBodyScroll();
      };
  }, [isAnyModalOpen]);

  // Bulk Add-ons Escape Key listener
  useEffect(() => {
      const handleKeyDown = (e) => {
          if (e.key === 'Escape') {
              setIsAddonMode(false);
              setSelectedAddonPlayers([]);
          } else if (e.key === 'Enter' && isAddonMode && selectedAddonPlayers.length > 0 && !addonModal.isOpen) {
              setAddonModal(prev => ({ ...prev, isOpen: true }));
          }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAddonMode, selectedAddonPlayers, addonModal.isOpen]);

  // Clear Addon Mode on Tab Change
  useEffect(() => {
      setIsAddonMode(false);
      setSelectedAddonPlayers([]);
  }, [activeTab]);

  useEffect(() => {
      if (activeTab !== 'tables') {
          setHasTableHorizontalScroll(false);
          return;
      }

      const frame = requestAnimationFrame(updateTableScrollAvailability);
      window.addEventListener('resize', updateTableScrollAvailability);

      return () => {
          cancelAnimationFrame(frame);
          window.removeEventListener('resize', updateTableScrollAvailability);
      };
  }, [activeTab, tournament?.tables?.length, tournament?.players?.length, selectedAddonPlayers.length]);

  // Timer Tick
  useEffect(() => {
    if (tournament && tournament.state === 'running' && tournament.timer_start_at) {
        timerRef.current = setInterval(() => {
            const start = new Date(tournament.timer_start_at).getTime();
            const now = Date.now();
            const elapsed = (now - start) / 1000;
            const remaining = Math.max(0, tournament.timer_remaining_seconds - elapsed);
            
            setTimeLeft(remaining);

            if (remaining <= 0 && !isAdvancingRef.current) {
                // Auto Advance Logic - prevent multiple triggers
                isAdvancingRef.current = true;
                clearInterval(timerRef.current);
                handleLevelAdvance(); 
            }
        }, 1000);
    } else {
        if (tournament) setTimeLeft(tournament.timer_remaining_seconds || 0);
        clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [tournament]);

  const loadData = async () => {
    try {
        const { data } = await axios.get(`/${cardroom}/api/tournaments/${id}`);
        // data includes config_json as object (handled by our backend fix)
        setTournament(data);

        // Load all players for dropdown
        const { data: playersData } = await axios.get(`/${cardroom}/api/players`);
        setPlayers(playersData);

        // Load tournament wallets
        const { data: walletsData } = await axios.get(`/${cardroom}/api/tournament_wallets`);
        setTournamentWallets(walletsData);

        // Load tournament costs
        const { data: costsData } = await axios.get(`/${cardroom}/api/tournaments/${id}/costs`);
        setCosts(costsData);

    } catch (err) {
        console.error("Load error", err);
    } finally {
        setLoading(false);
    }
  };

  const loadTournamentPlayerOptions = async () => {
      try {
          const { data } = await axios.get(`/${cardroom}/api/players`);
          setPlayers(data);
      } catch (err) {
          console.error('Load players failed', err);
      }
  };

  const handleControl = async (action) => {
      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/control`, {
              action,
              remainingSeconds: timeLeft 
          });
          loadData();
      } catch(err) { console.error(err); }
  };

  const handleLevelAdvance = async () => {
      // Prevents multiple triggers
      if (!tournament) {
          isAdvancingRef.current = false;
          return;
      }
      
      const config = tournament.config_json || {};
      const levels = config.levels || [];
      const nextIndex = (tournament.current_level || 0) + 1; // current_level is 1-based usually in UI but stored?
      // Wait, ensure we know if stored 0-indexed or 1-indexed. 
      // DB usually stores 'current_level'. Let's assume 1-based for display, but array index is level-1.
      
      if (nextIndex <= levels.length) {
          // Send update
          // We also need to reset the timer for the new level!
          // This logic might be better in backend, but user asked for frontend driven loosely.
          // Getting new duration
          const nextLevel = levels[nextIndex - 1];
          const newDuration = (nextLevel.duration || 20) * 60; 

          // 1. Update Level
          await axios.post(`/${cardroom}/api/tournaments/${id}/level`, { levelIndex: nextIndex });
          
          // 2. Reset Timer with new level duration
          await axios.post(`/${cardroom}/api/tournaments/${id}/control`, {
              action: 'pause',
              remainingSeconds: newDuration
          });
          
          // 3. Auto-start the timer for the new level
          await axios.post(`/${cardroom}/api/tournaments/${id}/control`, {
              action: 'start'
          });
          
          isAdvancingRef.current = false;
          loadData();
      } else {
          isAdvancingRef.current = false;
      }
  };
  
  const handleManualLevelChange = async (delta) => {
      if (!tournament) return;
      
      const config = tournament.config_json || {};
      const levels = config.levels || [];
      let newLevel = Number(tournament.current_level || 0);

      if (delta !== 0) {
          newLevel = Math.max(0, newLevel + delta);
          // Optional: Check if newLevel > levels.length? 
          // For now allow going up, but safeguard access
          if (newLevel > levels.length) return; 
      }

      // Get duration for the target level
      // current_level is 1-based, with 0 reserved for countdown.
      const targetLevelIndex = newLevel - 1;
      const levelData = newLevel === 0 ? null : levels[targetLevelIndex];
      // If we don't have data (unexpected), fallback or return
      const newDuration = newLevel === 0
          ? (Number(config.countdownSeconds) || 60)
          : (levelData && levelData.duration ? levelData.duration : 20) * 60;

      // Update level if changed
      if (delta !== 0) {
           await axios.post(`/${cardroom}/api/tournaments/${id}/level`, { levelIndex: newLevel });
      }

      // Reset Timer (always pause and reset on manual change/reset)
      await axios.post(`/${cardroom}/api/tournaments/${id}/control`, {
          action: 'pause',
          remainingSeconds: newDuration
      });

      loadData();
  };

  // Helper: Calculate Payouts â€” WSOP Shifted Inverse Power Law (matches TournamentCreate)
  const calculatePayouts = (prizepool, numPlaces, exponent = 1.5, buyIn = 0, minCashPercent = 0) => {
      if (numPlaces <= 0 || prizepool <= 0) return [];

      const minCash = buyIn * (minCashPercent / 100);
      let totalMinCash = minCash * numPlaces;
      let actualMinCash = minCash;
      if (totalMinCash > prizepool) {
          actualMinCash = prizepool / numPlaces;
          totalMinCash = prizepool;
      }

      const PRESETS = [
          { exp: 1.0, k: 2.50, c: 6.5 },
          { exp: 1.2, k: 2.50, c: 5.0 },
          { exp: 1.5, k: 2.50, c: 4.5 },
          { exp: 2.0, k: 2.41, c: 3.0 },
      ];
      let left = PRESETS[0], right = PRESETS[PRESETS.length - 1];
      for (let i = 0; i < PRESETS.length - 1; i++) {
          if (exponent >= PRESETS[i].exp && exponent <= PRESETS[i + 1].exp) {
              left = PRESETS[i]; right = PRESETS[i + 1]; break;
          }
      }
      const t = right.exp === left.exp ? 0 : (exponent - left.exp) / (right.exp - left.exp);
      const k = left.k + t * (right.k - left.k);
      const c = left.c + t * (right.c - left.c);

      const weights = [];
      let totalWeight = 0;
      for (let i = 1; i <= numPlaces; i++) {
          const w = 1 / Math.pow(i + c, k);
          weights.push(w);
          totalWeight += w;
      }
      if (totalWeight <= 0) totalWeight = 1;

      const remainingPool = prizepool - actualMinCash * numPlaces;
      let payouts = weights.map((w, i) => ({
          place: i + 1,
          amount: actualMinCash + (w / totalWeight) * remainingPool
      }));

      // Step-function grouping for lower places (WSOP brackets)
      const tiers = [
          { start: 10, end: 27,        step: 3  },
          { start: 28, end: 99,        step: 9  },
          { start: 100, end: Infinity, step: 10 },
      ];
      for (const tier of tiers) {
          if (numPlaces < tier.start) continue;
          const tierEnd = Math.min(numPlaces, tier.end);
          for (let i = tier.start; i <= tierEnd; i += tier.step) {
              const groupEnd = Math.min(i + tier.step - 1, tierEnd);
              if (groupEnd > i) {
                  let sum = 0;
                  for (let j = i; j <= groupEnd; j++) sum += payouts[j - 1].amount;
                  const avg = sum / (groupEnd - i + 1);
                  for (let j = i; j <= groupEnd; j++) payouts[j - 1].amount = avg;
              }
          }
      }

      // Round and fix rounding error on 1st place
      let totalAssigned = 0;
      for (let i = 0; i < payouts.length; i++) {
          payouts[i].amount = Math.round(payouts[i].amount);
          totalAssigned += payouts[i].amount;
      }
      if (payouts.length > 0) payouts[0].amount += prizepool - totalAssigned;

      for (let i = 0; i < payouts.length; i++) {
          payouts[i].percentage = (payouts[i].amount / prizepool) * 100;
      }

      return payouts;
  };

  const earlyActivePlayers = (tournament && tournament.players) ? tournament.players.filter(p => p.status === 'playing') : [];
  const filteredActivePlayers = earlyActivePlayers.filter(p => p.name.toLowerCase().includes(playerSearch.toLowerCase()));
  
  const activePlayerIds = new Set(earlyActivePlayers.map(p => p.player_id));
  const availableBuyinPlayers = players.filter(p => !activePlayerIds.has(p.id));
  
  // Payout Logic
  const config = tournament ? (tournament.config_json || {}) : {};
  const levels = config.levels || [];
  const isMixedGamesConfig = Boolean(config.mixedGames);
  const currentLevelNumber = tournament ? Number(tournament.current_level || 0) : 0;
  const isCountdownLevel = currentLevelNumber === 0;
  const currentLvlIdx = tournament ? Math.max(0, (currentLevelNumber || 1) - 1) : 0;
  const currentLevelData = levels[currentLvlIdx] || {};
  const nextLevelData = isCountdownLevel ? (levels[0] || null) : (levels[currentLvlIdx + 1] || null);
  const prizepool = tournament ? (tournament.prizepool || 0) : 0;
  const payoutMode = config.payoutMode || 'percent';
  const payoutValue = config.payoutValue || 15;
  const payoutExponent = config.payoutExponent || 1.5;
  const payoutMinCashPercent = config.minCashPercent || 0;
  const buyin = tournament ? (tournament.buyin || 0) : 0;
  const formatLevelGameType = (level) => isMixedGamesConfig && level?.gameType ? ` (${level.gameType})` : '';
  const currentLevelLabel = isCountdownLevel
      ? `Countdown${Number(config.countdownSeconds) > 0 ? ` ${Math.round(Number(config.countdownSeconds) / 60)} min` : ''}`
      : `Level ${currentLevelNumber} ${currentLevelData.isBreak ? 'BREAK' : `${currentLevelData.sb || 0} / ${currentLevelData.bb || 0}${formatLevelGameType(currentLevelData)}`}`;

  let placesPaid = 0;
  const hasCustomPayouts = Array.isArray(config.customPayouts) && config.customPayouts.length > 0;
  if (tournament) {
      if (hasCustomPayouts) {
          placesPaid = Array.isArray(config.customPayouts) ? config.customPayouts.length : 0;
      } else if (payoutMode === 'fixed') {
          placesPaid = payoutValue;
      } else {
          placesPaid = Math.floor((tournament.total_entries || 0) * (payoutValue / 100));
      }
      if (placesPaid < 1 && (tournament.total_entries || 0) > 0) placesPaid = 1;
  }

  // Use custom payouts if stored, otherwise auto-calculate
  const autoPayouts = calculatePayouts(prizepool, placesPaid, payoutExponent, buyin, payoutMinCashPercent);
  const hasEditedAmountPayouts = Array.isArray(config.customPayouts)
      && config.customPayouts.length > 0
      && config.customPayouts.some(p => p.amount !== undefined);
  const percentageCustomPayouts = Array.isArray(config.customPayouts)
      ? config.customPayouts.map((p, idx) => {
          const percentage = p.percentage !== undefined
              ? Number(p.percentage) || 0
              : (prizepool > 0 ? ((Number(p.amount) || 0) / prizepool) * 100 : 0);
          const place = Number(p.place) || idx + 1;
          return {
              place,
              percentage,
              amount: p.amount !== undefined && place > earlyActivePlayers.length
                  ? Number(p.amount) || 0
                  : Math.round(prizepool * (percentage / 100))
          };
      })
      : [];
  const payouts = (percentageCustomPayouts.length > 0)
      ? percentageCustomPayouts
      : (hasEditedAmountPayouts ? config.customPayouts : autoPayouts);

  const handleEliminate = (playerId) => {
      const player = earlyActivePlayers.find(p => p.player_id === playerId) || (tournament.players || []).find(p => p.player_id === playerId);
      const playerName = player ? player.name : 'Unknown';
      const playerCredit = player ? (player.tournament_credit || 0) : 0;
      const hasTournamentRecord = (tournament.player_records || []).some(record => Number(record.player_id) === Number(playerId));

      if (!hasTournamentRecord) {
          setEliminationModal({
              isOpen: true,
              playerId,
              playerName,
              isCashout: false,
              removeOnly: true,
              payoutAmount: 0,
              method: 'cash',
              walletId: '',
              settleCredit: false,
              playerCredit,
              eliminatedById: ''
          });
          return;
      }
      
      const rank = earlyActivePlayers.length; 
      const payout = payouts.find(p => p.place === rank);
      const isCashout = !!payout;
      
      setEliminationModal({
          isOpen: true,
          playerId,
          playerName,
          isCashout,
          removeOnly: false,
          payoutAmount: isCashout ? payout.amount : 0,
          method: 'cash',
          walletId: '',
          settleCredit: false,
          playerCredit,
          eliminatedById: ''
      });
  };
  
  const handleEndTournament = () => {
      setEndTournamentModal({ isOpen: true });
  };
  
  const confirmEndTournament = async () => {
       try {
          if (hasNoTournamentPlayers) {
              await axios.delete(`/${cardroom}/api/tournaments/${id}`);
          } else {
              await axios.post(`/${cardroom}/api/tournaments/${id}/control`, {
                  action: 'finish'
              });
          }
          setEndTournamentModal({ isOpen: false });
          navigate(`/${cardroom}/tournamentsHome`);
      } catch(err) { console.error(err); }
  };
  
  const handleToggleDisplay = (key) => {
      setTournament(prev => {
          if (!prev) return prev;
          let prevConfig = prev.config_json || {};
          if (typeof prevConfig === 'string') {
              try {
                  prevConfig = JSON.parse(prevConfig);
              } catch {
                  prevConfig = {};
              }
          }

          const currentSettings = {
              ...DEFAULT_DISPLAY_SETTINGS,
              ...(prevConfig.display_settings || {})
          };
          const newSettings = {
              ...currentSettings,
              [key]: currentSettings[key] === false
          };

          pendingDisplaySettingsRef.current = newSettings;
          if (displaySettingsSaveTimerRef.current) {
              clearTimeout(displaySettingsSaveTimerRef.current);
          }
          displaySettingsSaveTimerRef.current = setTimeout(async () => {
              const settingsToSave = pendingDisplaySettingsRef.current;
              if (!settingsToSave) return;
              try {
                  await axios.post(`/${cardroom}/api/tournaments/${id}/display-settings`, {
                      display_settings: settingsToSave
                  });
                  if (pendingDisplaySettingsRef.current === settingsToSave) {
                      pendingDisplaySettingsRef.current = null;
                  }
              } catch (err) {
                  console.error("Display settings error:", err);
              } finally {
                  displaySettingsSaveTimerRef.current = null;
              }
          }, 250);

          return {
              ...prev,
              config_json: {
                  ...prevConfig,
                  display_settings: newSettings
              }
          };
      });
  };
  
  const handleDragStart = (e, playerId, isRegistered = false) => {
      e.dataTransfer.setData('playerId', playerId);
      e.dataTransfer.setData('isRegistered', isRegistered);
  };

  const openTransactionModal = (modalConfig, playerOption = null) => {
      const modalType = modalConfig.type || 'buyin';
      const canUseBonus = modalType === 'buyin' && Number(tournament?.bonus_starting_chips || 0) > 0;
      const canUseBuyinAddon = modalType === 'buyin' && Number(tournament?.buyin_addon_chips || 0) > 0;
      const canUseRebuyAddon = modalType === 'rebuy' && Number(tournament?.rebuys_add_on_chips || 0) > 0;
      if (modalType === 'buyin' && !playerOption) {
          loadTournamentPlayerOptions();
      }
      const preset = modalType === 'buyin' ? buyinPreset : rebuyPreset;
      setIsBonus(canUseBonus ? !!preset.bonus : false);
      setIsAddOn(modalType === 'buyin' ? (canUseBuyinAddon ? !!preset.addOn : false) : (canUseRebuyAddon ? !!preset.addOn : false));
      setIsCredit(!!preset.credit && !preset.giveaway);
      setIsGiveaway(!!preset.giveaway);
      setRebuyEliminatedById('');
      setTableModal({
          isOpen: true,
          type: modalType,
          playerId: modalConfig.playerId ?? null,
          presetTable: modalConfig.presetTable ?? null,
          presetSeat: modalConfig.presetSeat ?? null,
          method: modalConfig.method || 'cash',
          walletId: modalConfig.walletId || ''
      });
      setSelectedPlayer(playerOption);
  };

  const handleDrop = async (e, targetTableId, targetSeatNumber) => {
      e.preventDefault();
      stopScrolling();
      const playerId = e.dataTransfer.getData('playerId');
      const isRegistered = e.dataTransfer.getData('isRegistered') === 'true';
      if (!playerId) return;

      // Prevent dragging to occupied seat
      if (targetTableId && targetSeatNumber) {
          const isOccupied = tournament.players.some(p => p.table_id === targetTableId && p.seat_number === targetSeatNumber && p.status === 'playing');
          if (isOccupied) return;
      }

      if (isRegistered) {
          // Open Buy-In modal instead of directly seating
          const player = registeredPlayers.find(p => p.player_id === parseInt(playerId));
          if (player) {
              openTransactionModal(
                  { type: 'buyin', playerId: player.player_id, presetTable: targetTableId, presetSeat: targetSeatNumber },
                  { label: player.name, value: player.player_id }
              );
          }
          return;
      }

      // Optimistic update
      setTournament(prev => {
          const updatedPlayers = prev.players.map(p => {
              if (p.player_id === parseInt(playerId)) {
                  return { ...p, table_id: targetTableId, seat_number: targetSeatNumber };
              }
              // If someone was already in that exact seat on that exact table, unseat them (swap logic simple)
              if (targetTableId && targetSeatNumber && p.table_id === targetTableId && p.seat_number === targetSeatNumber) {
                  return { ...p, table_id: null, seat_number: null };
              }
              return p;
          });
          return { ...prev, players: updatedPlayers };
      });

      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/seat`, {
              playerId,
              tableId: targetTableId,
              seatNumber: targetSeatNumber
          });
          loadData();
      } catch (err) {
          console.error(err);
          alert("Failed to assign seat");
          loadData(); // revert
      }
  };

  const handleDragOver = (e) => {
      e.preventDefault(); // necessary to allow drop
      if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const { left, right, width } = container.getBoundingClientRect();
          const threshold = width * 0.05; // 5% of width
          
          let speed = 0;
          const maxSpeed = 15;

          if (e.clientX < left + threshold) {
              const distance = left + threshold - e.clientX;
              speed = -Math.max(3, (distance / threshold) * maxSpeed);
          } else if (e.clientX > right - threshold) {
              const distance = e.clientX - (right - threshold);
              speed = Math.max(3, (distance / threshold) * maxSpeed);
          }

          if (speed !== 0) {
              scrollRef.current.speed = speed;
              startScrolling();
          } else {
              stopScrolling();
          }
      }
  };

  const handleAddTable = async () => {
      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/tables`, { name: newTableName });
          setNewTableName('');
          loadData();
      } catch(err) { console.error(err); alert("Failed to add table"); }
  };

  const handleUnseat = async (playerId) => {
      if (!playerId) return;

      // Optimistic update
      setTournament(prev => {
          const updatedPlayers = prev.players.map(p => {
              if (p.player_id === playerId) {
                  return { ...p, table_id: null, seat_number: null };
              }
              return p;
          });
          return { ...prev, players: updatedPlayers };
      });

      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/seat`, {
              playerId,
              tableId: null,
              seatNumber: null
          });
          loadData();
      } catch (err) {
          console.error(err);
          alert("Failed to unseat player");
          loadData(); // revert
      }
  };

  const handleRemoveTable = async (tableId) => {
      try {
          await axios.delete(`/${cardroom}/api/tournaments/${id}/tables/${tableId}`);
          setDeleteTableModal({ isOpen: false, tableId: null, tableName: '' });
          loadData();
      } catch(err) {
          console.error(err);
          alert(err.response?.data?.error || "Failed to remove table"); 
      }
  };

  const openFinalTableModal = (table) => {
      const isRandomizeOnly = tables.length === 1;
      setFinalTableModal({
          isOpen: true,
          tableId: table.id,
          tableName: table.name || `Table ${table.table_number}`,
          isRandomizeOnly
      });
  };

  const confirmMakeFinalTable = async () => {
      if (!finalTableModal.tableId || isSubmitting) return;
      setIsSubmitting(true);
      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/tables/${finalTableModal.tableId}/final-table`);
          setFinalTableModal({ isOpen: false, tableId: null, tableName: '', isRandomizeOnly: false });
          setIsAddonMode(false);
          setSelectedAddonPlayers([]);
          loadData();
      } catch (err) {
          console.error(err);
          alert(err.response?.data?.error || "Failed to make final table");
      } finally {
          setIsSubmitting(false);
      }
  };

  const [editingTableId, setEditingTableId] = useState(null);
  const [editingTableName, setEditingTableName] = useState('');

  const handleRenameTable = async (tableId) => {
      if (!editingTableName.trim()) {
          setEditingTableId(null);
          return;
      }
      try {
          await axios.patch(`/${cardroom}/api/tournaments/${id}/tables/${tableId}`, { name: editingTableName.trim() });
          setEditingTableId(null);
          loadData();
      } catch (err) {
          console.error(err);
          alert("Failed to rename table");
      }
  };

  const handleRegisterPlayer = async (playerId) => {
      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/register`, { playerId });
          setPlayerToRegister(null);
          loadData();
      } catch (err) {
          console.error(err);
          alert("Failed to register player");
      }
  };

  const handleRegisterPlayersBulk = async () => {
      if (playersToRegister.length === 0 || isSubmitting) return;
      setIsSubmitting(true);
      try {
          await Promise.all(
              playersToRegister.map((player) =>
                  axios.post(`/${cardroom}/api/tournaments/${id}/register`, { playerId: player.value })
              )
          );
          setPlayersToRegister([]);
          loadData();
      } catch (err) {
          console.error(err);
          alert("Failed to register players");
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleRemoveRegisteredPlayer = async (playerId) => {
      try {
          await axios.delete(`/${cardroom}/api/tournaments/${id}/register/${playerId}`);
          loadData();
      } catch (err) {
          console.error(err);
          alert("Failed to unregister player");
      }
  };

  const openUndoTournamentTransactionModal = (transaction) => {
      if (!transaction || isSubmitting) return;
      setPlayerHistoryModal({ isOpen: false, playerId: null, playerName: '' });
      setUndoTransactionModal({ isOpen: true, transaction });
  };

  const handleUndoTournamentTransaction = async (transactionId) => {
      if (!transactionId || isSubmitting) return;

      setIsSubmitting(true);
      try {
          const transaction = undoTransactionModal.transaction;
          await axios.delete(`/${cardroom}/api/tournaments/${id}/transactions/${transactionId}`);
          const bountyWinnerId =
              transaction?.transaction_type === 'cashout' &&
              Number(transaction?.chips || 0) > 0 &&
              Number(tournament?.bounty_enabled || 0) > 0
                  ? Number(transaction.chips)
                  : null;
          const bountyAmount = Number(tournament?.bounty_amount || 0);
          if (bountyWinnerId && bountyAmount > 0) {
              setTournament(prev => {
                  if (!prev) return prev;
                  return {
                      ...prev,
                      players: (prev.players || []).map(player =>
                          Number(player.player_id) === Number(bountyWinnerId)
                              ? {
                                  ...player,
                                  knocks: Math.max(0, Number(player.knocks || 0) - 1),
                                  tournament_credit: Number(player.tournament_credit || 0) + bountyAmount,
                              }
                              : player
                      )
                  };
              });
          }
          setUndoTransactionModal({ isOpen: false, transaction: null });
          loadData();
      } catch (err) {
          console.error(err);
          alert(err.response?.data?.error || "Failed to undo transaction");
      } finally {
          setIsSubmitting(false);
      }
  };

  function getWinnerTipPlayerOptions() {
      const recordsByPlayer = new Map((tournament?.player_records || []).map(record => [Number(record.player_id), record]));
      return (tournament?.players || [])
          .filter(player => recordsByPlayer.has(Number(player.player_id)))
          .map(player => {
              const record = recordsByPlayer.get(Number(player.player_id)) || {};
              return {
                  ...player,
                  place: record.place,
                  cash: Number(record.cash) || 0
              };
          })
          .sort((a, b) => {
              const aPlace = Number(a.place) || 9999;
              const bPlace = Number(b.place) || 9999;
              if (aPlace !== bPlace) return aPlace - bPlace;
              if ((b.cash || 0) !== (a.cash || 0)) return (b.cash || 0) - (a.cash || 0);
              return String(a.name || '').localeCompare(String(b.name || ''));
          });
  }

  const openWinnerTipModal = () => {
      const winnerOptions = getWinnerTipPlayerOptions();
      setWinnerTipModal({
          isOpen: true,
          playerId: winnerOptions[0]?.player_id ? String(winnerOptions[0].player_id) : '',
          amount: '',
          payment: 'cash'
      });
  };

  const submitWinnerTip = async (e) => {
      e.preventDefault();
      if (isSubmitting) return;
      const tipAmount = Number(winnerTipModal.amount) || 0;
      if (!winnerTipModal.playerId || tipAmount <= 0) return;

      const selectedWallet = tournamentWallets.find(w => String(w.id) === String(winnerTipModal.payment));
      setIsSubmitting(true);
      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/transactions`, {
              playerId: Number(winnerTipModal.playerId),
              type: 'winner_tip',
              amount: tipAmount,
              method: winnerTipModal.payment === 'cash' ? 'cash' : 'digital',
              walletId: selectedWallet ? selectedWallet.id : null
          });
          setWinnerTipModal({ isOpen: false, playerId: '', amount: '', payment: 'cash' });
          await loadData();
      } catch (err) {
          console.error('Record winner tip error:', err);
          alert(err.response?.data?.error || 'Failed to record winner tip');
      } finally {
          setIsSubmitting(false);
      }
  };

  const openCashRefillModal = () => {
      setCashRefillModal({
          isOpen: true,
          walletId: tournamentWallets[0]?.id ? String(tournamentWallets[0].id) : '',
          amount: ''
      });
  };

  const submitCashRefill = async (e) => {
      e.preventDefault();
      if (isSubmitting) return;
      const amount = Number(cashRefillModal.amount) || 0;
      if (!cashRefillModal.walletId || amount <= 0) return;

      setIsSubmitting(true);
      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/cash-refill`, {
              walletId: Number(cashRefillModal.walletId),
              amount
          });
          setCashRefillModal({ isOpen: false, walletId: '', amount: '' });
          await loadData();
      } catch (err) {
          console.error('Tournament cash refill error:', err);
          alert(err.response?.data?.error || 'Failed to refill tournament cash bag');
      } finally {
          setIsSubmitting(false);
      }
  };

  const submitTransaction = async (e) => {
      e.preventDefault();
      if (!selectedPlayer || isSubmitting) return;

      if (!isCredit && !isGiveaway && transactionMethod !== 'cash' && !transactionWalletId) {
          return alert("Please select a wallet for digital payment");
      }

      setIsSubmitting(true);
      try {
          let amount = 0; 
          
           if (transType === 'buyin') {
               amount = (tournament.buyin || 0); // User said rake/tips in buyin, but we send full buyin.
               if (isBonus) {
                   amount += (tournament.bonus_buyin || 0);
               }
               if (isAddOn) {
                   amount += (tournament.buyin_addon_cost || 0);
               }
           } else if (transType === 'rebuy') {
               amount = (tournament.rebuys_cost || 0);
           }
           
          await axios.post(`/${cardroom}/api/tournaments/${id}/transactions`, {
              playerId: selectedPlayer.value,
              type: transType,
              amount: isGiveaway ? 0 : amount,
              isBonus: isBonus,
              isAddOn: transType === 'buyin' ? isAddOn : false,
              isCredit: isCredit,
              isGiveaway: isGiveaway,
              method: isGiveaway ? 'promo' : (isCredit ? 'credit' : transactionMethod),
              walletId: (!isCredit && !isGiveaway && transactionMethod !== 'cash') ? transactionWalletId : null
          });
          
          setSelectedPlayer(null);
          setIsBonus(false);
          setIsAddOn(false);
          setIsCredit(false);
          setIsGiveaway(false);
          setTransactionMethod('cash');
          setTransactionWalletId('');
          loadData();
      } catch(err) {
          console.error(err);
          alert("Failed");
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleAddonToggle = (playerId) => {
      if (!isAddonMode) return;
      setSelectedAddonPlayers(prev => 
          prev.includes(playerId) 
              ? prev.filter(id => id !== playerId)
              : [...prev, playerId]
      );
  };

  const submitBulkAddons = async (e) => {
      e.preventDefault();
      if (selectedAddonPlayers.length === 0 || isSubmitting) return;

      setIsSubmitting(true);
      try {
          await axios.post(`/${cardroom}/api/tournaments/${id}/addons-bulk`, {
              playerIds: selectedAddonPlayers,
              isCredit: !!addonModal.isCredit
          });
          
          setAddonModal({ isOpen: false, isCredit: false });
          setIsAddonMode(false);
          setSelectedAddonPlayers([]);
          loadData();
      } catch (err) {
          console.error(err);
          alert("Failed to process add-ons");
      } finally {
          setIsSubmitting(false);
      }
  };

  if (loading || !tournament) return <div className="game-loading">Loading...</div>;

  


  // Find all players
  const allPlayers = tournament.players || [];
  const activePlayers = allPlayers.filter(p => p.status === 'playing');
  
  const tables = tournament.tables || [];
  const activeTableIds = tables.map(t => t.id);
  
  const unseatedPlayers = activePlayers.filter(p => !p.table_id || !activeTableIds.includes(p.table_id));

  const eliminatedPlayers = allPlayers.filter(p => p.status === 'eliminated');
  const registeredPlayers = allPlayers.filter(p => p.status === 'registered');
  const hasNoTournamentPlayers =
      registeredPlayers.length === 0 &&
      activePlayers.length === 0 &&
      eliminatedPlayers.length === 0;
  
  // Formatting Time
  const formatTime = (s) => {
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${m}:${sec < 10 ? '0' : ''}${sec}`;
  };

  const displaySettings = {
      ...DEFAULT_DISPLAY_SETTINGS,
      ...(config.display_settings || {})
  };

  const quickAddBtnDisabled = (() => {
      if (!qFirstName.trim()) return true;
      const enteredFirst = qFirstName.trim().toLowerCase();
      const enteredLast = qLastName.trim().toLowerCase();
      const enteredDisplay = qNickname.trim().toLowerCase();
      return (quickAddDuplicatePlayers.length ? quickAddDuplicatePlayers : players).some((p) => {
          if (enteredDisplay && p.name?.toLowerCase() === enteredDisplay) return true;
          return p.first_name?.toLowerCase() === enteredFirst && p.last_name?.toLowerCase() === enteredLast;
      });
  })();

  const openQuickAddModal = async (context = 'transaction') => {
      setQuickAddContext(context);
      setShowQuickAddModal(true);
      try {
          const { data } = await axios.get(`/${cardroom}/api/players`);
          setQuickAddDuplicatePlayers(data);
      } catch (err) {
          console.error('Load duplicate players failed', err);
          setQuickAddDuplicatePlayers(players);
      }
  };

  const closeQuickAddModal = () => {
      setShowQuickAddModal(false);
      setQuickAddContext('transaction');
      setQFirstName('');
      setQLastName('');
      setQNickname('');
      setQPhone('');
  };

  const handleQuickAddPlayer = async (e) => {
      e.preventDefault();
      if (isSubmitting) return;

      if (!qFirstName.trim()) {
          alert('Enter a first name');
          return;
      }

      const digitsOnlyPhone = qPhone.replace(/\D/g, '');
      if (digitsOnlyPhone.length !== 10 && digitsOnlyPhone.length !== 0) {
          alert('Enter a valid 10-digit phone number');
          return;
      }

      try {
          setIsSubmitting(true);
          const { data: player } = await axios.post(`/${cardroom}/api/players`, {
              firstName: qFirstName.trim(),
              lastName: qLastName.trim(),
              name: qNickname.trim(),
              phone: digitsOnlyPhone,
              bookId: null
          });
          setPlayers(prev => prev.some(p => Number(p.id) === Number(player.id)) ? prev : [...prev, player]);
          const createdOption = getPlayerOption(player, { showTournamentHistoryWarning: true });
          if (quickAddContext === 'register') {
              setPlayersToRegister(prev =>
                  prev.some(option => Number(option.value) === Number(createdOption.value))
                      ? prev
                      : [...prev, createdOption]
              );
          } else {
              setSelectedPlayer(createdOption);
          }
          closeQuickAddModal();
      } catch (err) {
          alert(err.response?.data?.error || 'Could not add player');
      } finally {
          setIsSubmitting(false);
      }
  };

  const renderTableModal = () => {
      if (!tableModal.isOpen) return null;

      const isBuyin = tableModal.type === 'buyin';
      const showBonusOption = isBuyin && Number(tournament.bonus_starting_chips || 0) > 0;
      const showBuyinAddonOption = isBuyin && Number(tournament.buyin_addon_chips || 0) > 0;
      const showRebuyAddonOption = !isBuyin && Number(tournament.rebuys_add_on_chips || 0) > 0;
      const effectiveIsBonus = showBonusOption && isBonus;
      const effectiveIsAddOn = (isBuyin ? showBuyinAddonOption : showRebuyAddonOption) && isAddOn;
      const defaultAmount = isBuyin ? (tournament.buyin || 0) : (tournament.rebuys_cost || 0);
      const modalAmount = isBuyin
          ? defaultAmount + (effectiveIsBonus ? (tournament.bonus_buyin || 0) : 0) + (effectiveIsAddOn ? (tournament.buyin_addon_cost || 0) : 0)
          : defaultAmount + (effectiveIsAddOn ? (tournament.rebuys_add_on_costs || 0) : 0);
      const chargeAmount = isGiveaway ? 0 : modalAmount;
      const modalChips = isBuyin
          ? (tournament.starting_chips || 0) + (effectiveIsBonus ? (tournament.bonus_starting_chips || 0) : 0) + (effectiveIsAddOn ? (tournament.buyin_addon_chips || 0) : 0)
          : (tournament.rebuys_chips || 0) + (effectiveIsAddOn ? (tournament.rebuys_add_on_chips || 0) : 0);
      const boughtInPlayerIds = new Set(
          (tournament.players || [])
              .filter(p => p.status === 'playing' || p.status === 'eliminated')
              .map(p => Number(p.player_id))
      );
      const modalPlayerOptions = isBuyin
          ? players
              .filter(p => !boughtInPlayerIds.has(Number(p.id)))
              .map(p => getPlayerOption(p, { showTournamentHistoryWarning: true }))
          : [...activePlayers, ...eliminatedPlayers].map(getPlayerOption);

      let submitModal = async (e) => {
          e.preventDefault();
          if (isSubmitting) return;
          setIsSubmitting(true);
          
          const targetPlayerId = selectedPlayer ? selectedPlayer.value : tableModal.playerId;
          if (!targetPlayerId) { setIsSubmitting(false); return alert("Select a player"); }

          if (!isCredit && !isGiveaway && tableModal.method !== 'cash' && !tableModal.walletId) {
              setIsSubmitting(false);
              return alert("Please select a wallet for digital payment");
          }

          try {
              let amount = chargeAmount;

              // 1. Transaction
              await axios.post(`/${cardroom}/api/tournaments/${id}/transactions`, {
                  playerId: targetPlayerId,
                  type: tableModal.type,
                  amount: amount,
                  isBonus: isBuyin ? effectiveIsBonus : false,
                  isAddOn: effectiveIsAddOn,
                  isCredit: isCredit,
                  isGiveaway: isGiveaway,
                  method: isGiveaway ? 'promo' : (isCredit ? 'credit' : tableModal.method),
                  walletId: (!isCredit && !isGiveaway && tableModal.method !== 'cash') ? tableModal.walletId : null,
                  eliminatedById: !isBuyin && rebuyEliminatedById ? Number(rebuyEliminatedById) : null
              });

              // 2. Seat if preset
              if (tableModal.presetTable && tableModal.presetSeat) {
                  await axios.post(`/${cardroom}/api/tournaments/${id}/seat`, {
                      playerId: targetPlayerId,
                      tableId: tableModal.presetTable,
                      seatNumber: tableModal.presetSeat
                  });
              }

              if (isBuyin) {
                  setBuyinPreset({
                      bonus: !!effectiveIsBonus,
                      addOn: !!effectiveIsAddOn,
                      credit: !!isCredit,
                      giveaway: !!isGiveaway
                  });
              } else {
                  setRebuyPreset({
                      addOn: !!effectiveIsAddOn,
                      credit: !!isCredit,
                      giveaway: !!isGiveaway
                  });
              }

              setTableModal({ ...tableModal, isOpen: false, method: 'cash', walletId: '' });
              setSelectedPlayer(null);
              setIsBonus(false);
              setIsAddOn(false);
              setIsCredit(false);
              setIsGiveaway(false);
              setRebuyEliminatedById('');
              loadData();
          } catch (err) {
              console.error(err);
              alert(err.response?.data?.error || "Transaction Failed");
          } finally {
              setIsSubmitting(false);
          }
      };

      // ... (rest of renderTableModal content)
      return (
          <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}} onClick={() => { setTableModal({ ...tableModal, isOpen: false }); setIsGiveaway(false); setRebuyEliminatedById(''); }}>
              <div className="game-card" style={{padding: '2rem', width: '400px', maxWidth: '92vw', background: 'var(--game-bg-secondary)'}} onClick={e => e.stopPropagation()}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem'}}>
                      <h3 style={{margin: 0}}>{isBuyin ? 'Process Buy-in' : 'Process Rebuy'}</h3>
                      <button type="button" className="game-icon-btn" onClick={() => { setTableModal({ ...tableModal, isOpen: false }); setIsGiveaway(false); setRebuyEliminatedById(''); }}><FaTimes /></button>
                  </div>
                  <form onSubmit={submitModal}>
                      <div style={{marginBottom: '1rem'}}>
                          <label htmlFor="player-select" style={{display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.5rem', color: 'var(--game-text-secondary)'}}>
                              Player
                          </label>
                          <div style={{display: 'grid', gridTemplateColumns: isBuyin ? 'minmax(0, 1fr) auto' : '1fr', gap: '0.75rem', alignItems: 'stretch'}}>
                              <Select 
                                  id="player-select"
                                  options={modalPlayerOptions}
                                  filterOption={filterPlayerOption}
                                  formatOptionLabel={formatPlayerOptionLabel}
                                  isSearchable
                                  placeholder={isBuyin ? "Select Player" : "Select Player"}
                                  onChange={setSelectedPlayer}
                                  value={selectedPlayer}
                                  className="game-select-container"
                                  autoFocus={!tableModal.playerId}
                                  classNamePrefix="game-select"
                                  styles={{
                                      control: (base) => ({
                                          ...base,
                                          backgroundColor: "#2d3748",
                                          borderColor: "#4a5568",
                                          color: "#fff",
                                          minHeight: "42px"
                                      }),
                                      menu: (base) => ({
                                          ...base,
                                          backgroundColor: "#2d3748",
                                      }),
                                      option: (base, { isFocused }) => ({
                                          ...base,
                                          backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                                          color: "#e2e8f0",
                                      }),
                                      singleValue: (base) => ({
                                          ...base,
                                          color: "#e2e8f0",
                                      }),
                                      input: (base) => ({
                                          ...base,
                                          color: "#e2e8f0",
                                      }),
                                  }}
                              />
                              {isBuyin && (
                                  <button
                                      type="button"
                                      className="game-icon-btn game-icon-btn--primary"
                                      onClick={openQuickAddModal}
                                      title="Quick Add Player"
                                      style={{ width: '42px', height: '42px', flexShrink: 0 }}
                                  >
                                      <FaPlus />
                                  </button>
                              )}
                          </div>
                      </div>
                      {(() => {
                          const selectedPlayerData = selectedPlayer
                              ? (tournament.players || []).find(p => p.player_id === selectedPlayer.value)
                              : null;
                          const playerCredit = selectedPlayerData ? (selectedPlayerData.tournament_credit || 0) : 0;
                          const bountyEnabled = Number(tournament?.bounty_enabled || 0) > 0 && Number(tournament?.bounty_amount || 0) > 0;
                          const rebuyBountyOptions = !isBuyin && bountyEnabled && selectedPlayerData?.status === 'playing'
                              ? activePlayers.filter(p => Number(p.player_id) !== Number(selectedPlayerData.player_id))
                              : [];
                          return (
                          <>
                              {playerCredit !== 0 && (
                                  <div style={{marginBottom: '0.75rem'}}>
<span className={`game-player-badge ${playerCredit > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{fontSize: '0.85rem'}}>
  <b>${Math.abs(playerCredit)}</b>&nbsp;{playerCredit > 0 ? 'Credit' : 'Balance'}
</span>
                                  </div>
                              )}
                              <div className="tournament-check-options">
                                  {showBonusOption && (
                                  <label className={`game-checkbox tournament-check-option ${isBonus ? 'tournament-check-option--active' : ''}`} htmlFor="isBonus">
                                      <input 
                                          type="checkbox" 
                                          id="isBonus" 
                                          checked={isBonus} 
                                          onChange={(e) => setIsBonus(e.target.checked)} 
                                      />
                                      <span>
                                          <strong>Buy-in Bonus</strong>
                                          <small>${tournament.bonus_buyin || 0} / {tournament.bonus_starting_chips || 0} chips</small>
                                      </span>
                                  </label>
                                  )}
                                  {showBuyinAddonOption && (
                                  <label className={`game-checkbox tournament-check-option ${isAddOn ? 'tournament-check-option--active' : ''}`} htmlFor="isBuyinAddOn">
                                      <input 
                                          type="checkbox" 
                                          id="isBuyinAddOn" 
                                          checked={isAddOn} 
                                          onChange={(e) => setIsAddOn(e.target.checked)} 
                                      />
                                      <span>
                                          <strong>Dealer Add-on</strong>
                                          <small>${tournament.buyin_addon_cost || 0} / {tournament.buyin_addon_chips || 0} chips</small>
                                      </span>
                                  </label>
                                  )}
                                  {showRebuyAddonOption && (
                                  <label className={`game-checkbox tournament-check-option ${isAddOn ? 'tournament-check-option--active' : ''}`} htmlFor="isAddOn">
                                      <input 
                                          type="checkbox" 
                                          id="isAddOn" 
                                          checked={isAddOn} 
                                          onChange={(e) => setIsAddOn(e.target.checked)} 
                                      />
                                      <span>
                                          <strong>Rebuy Dealer Add-on</strong>
                                          <small>${tournament.rebuys_add_on_costs || 0} / {tournament.rebuys_add_on_chips || 0} chips</small>
                                      </span>
                                  </label>
                                  )}
                                  <label className={`game-checkbox tournament-check-option tournament-check-option--credit ${isCredit ? 'tournament-check-option--active' : ''}`} htmlFor="isCredit">
                                      <input 
                                          type="checkbox" 
                                          id="isCredit" 
                                          checked={isCredit} 
                                          onChange={(e) => {
                                              setIsCredit(e.target.checked);
                                              if (e.target.checked) setIsGiveaway(false);
                                          }} 
                                      />
                                      <span>
                                          <strong>On Credit</strong>
                                          <small>Collect later</small>
                                      </span>
                                  </label>
                                  <label className={`game-checkbox tournament-check-option tournament-check-option--promo ${isGiveaway ? 'tournament-check-option--active' : ''}`} htmlFor="isGiveaway">
                                      <input 
                                          type="checkbox" 
                                          id="isGiveaway" 
                                          checked={isGiveaway} 
                                          onChange={(e) => {
                                              setIsGiveaway(e.target.checked);
                                              if (e.target.checked) setIsCredit(false);
                                          }} 
                                      />
                                      <span>
                                          <strong>On the House</strong>
                                          <small>Promo / giveaway</small>
                                      </span>
                                  </label>
                              </div>
                              {rebuyBountyOptions.length > 0 && (
                                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.35)', borderRadius: '6px' }}>
                                      <label style={{ display: 'block', marginBottom: '0.4rem', color: '#fb7185', fontSize: '0.85rem', fontWeight: 700 }}>
                                          Bounty Won By
                                      </label>
                                      <select
                                          className="game-input"
                                          value={rebuyEliminatedById}
                                          onChange={(e) => setRebuyEliminatedById(e.target.value)}
                                      >
                                          <option value="">No bounty awarded</option>
                                          {rebuyBountyOptions.map(p => (
                                              <option key={p.player_id} value={p.player_id}>
                                                  {p.name}
                                              </option>
                                          ))}
                                      </select>
                                      <div style={{ marginTop: '0.45rem', color: 'var(--game-text-secondary)', fontSize: '0.82rem' }}>
                                          Records this rebuy as a knockout first, then processes the rebuy.
                                      </div>
                                  </div>
                              )}
                          </>
                          );
                      })()}
                      <div style={{marginBottom: '1.5rem', display: 'flex', gap: '1rem'}}>
                          <div style={{flex: 1}}>
                              <label style={{display: 'block', marginBottom: '0.5rem', color: 'var(--game-text-secondary)'}}>Amount</label>
                              <div style={{background: '#1a202c', padding: '0.75rem', borderRadius: '4px', textAlign: 'center'}}>
                                  <div style={{color: '#48bb78', fontSize: '1.5rem', fontWeight: 'bold'}}>
                                      ${chargeAmount}
                                  </div>
                              </div>
                          </div>
                          <div style={{flex: 1}}>
                              <label style={{display: 'block', marginBottom: '0.5rem', color: 'var(--game-text-secondary)'}}>Chips</label>
                              <div style={{background: '#1a202c', padding: '0.75rem', borderRadius: '4px', textAlign: 'center'}}>
                                  <div style={{color: '#fff', fontSize: '1.5rem', fontWeight: 'bold'}}>
                                      {modalChips}
                                  </div>
                              </div>
                          </div>
                      </div>
                      
                      {isCredit || isGiveaway ? (
                          <div style={{marginBottom: '1.5rem', padding: '0.75rem', background: isGiveaway ? 'rgba(159,122,234,0.12)' : 'rgba(214,158,46,0.12)', border: `1px solid ${isGiveaway ? '#9f7aea' : '#d69e2e'}`, borderRadius: '6px'}}>
                              <span style={{color: isGiveaway ? '#c4b5fd' : '#d69e2e', fontWeight: 600, fontSize: '0.9rem'}}>
                                  {isGiveaway
                                      ? 'On the House: No payment will be collected from the player. Rake and tips will be counted as $0 for this promo entry.'
                                      : 'Credit Buy-In: No payment collected now. Player will owe the tournament.'}
                              </span>
                          </div>
                      ) : (
                      <div style={{marginBottom: '1.5rem', display: 'flex', gap: '1rem'}}>
                          <div style={{flex: 1}}>
                              <label style={{display: 'block', marginBottom: '0.5rem', color: 'var(--game-text-secondary)'}}>Method</label>
                              <select 
                                  className="game-input"
                                  value={tableModal.method}
                                  onChange={(e) => setTableModal({...tableModal, method: e.target.value})}
                              >
                              <option value="cash">Cash</option>
                              <option value="venmo">Venmo</option>
                              <option value="cashapp">CashApp</option>
                              <option value="bitcoin">Bitcoin</option>
                              <option value="zelle">Zelle</option>
                              <option value="applePay">Apple Pay</option>
                              <option value="paypal">PayPal</option>
                              </select>
                          </div>
                          {tableModal.method !== 'cash' && (
                              <div style={{flex: 1}}>
                                  <label style={{display: 'block', marginBottom: '0.5rem', color: 'var(--game-text-secondary)'}}>Wallet</label>
                                  <select 
                                      className="game-input"
                                      value={tableModal.walletId}
                                      onChange={(e) => setTableModal({...tableModal, walletId: e.target.value})}
                                      required
                                  >
                                      <option value="">Select Wallet</option>
                                      {tournamentWallets.map(w => (
                                          <option key={w.id} value={w.id}>{w.name}</option>
                                      ))}
                                  </select>
                              </div>
                          )}
                      </div>
                      )}

                      <div style={{display: 'flex', gap: '1rem'}}>
                          <button type="button" className="game-btn game-btn--secondary" style={{flex: 1}} onClick={() => { setTableModal({ ...tableModal, isOpen: false }); setIsCredit(false); setIsGiveaway(false); setIsBonus(false); setIsAddOn(false); setRebuyEliminatedById(''); }}>
                              Cancel
                          </button>
                          {(() => {
                              const isAlreadyActive = isBuyin && selectedPlayer && activePlayerIds.has(selectedPlayer.value);
                              const isEliminated = isBuyin && selectedPlayer && (tournament?.players || []).some(p => p.player_id === selectedPlayer.value && p.status === 'eliminated');
                              const isDisabled = isSubmitting || !selectedPlayer || isAlreadyActive || isEliminated;

                              let titleStr = "";
                              if (isAlreadyActive) titleStr = "Player has already entered tournament";
                              else if (isEliminated) titleStr = "Player has been eliminated";

                              return (
                                  <div style={{flex: 1}} title={titleStr}>
                                      <button type="submit" className={`game-btn ${isCredit || isGiveaway ? 'game-btn--warning' : 'game-btn--primary'}`} style={{width: '100%', height: '100%'}} disabled={isDisabled}>
                                          {isGiveaway ? `Promo ${isBuyin ? 'Buy-In' : 'Rebuy'}` : (isCredit ? `Credit ${isBuyin ? 'Buy-In' : 'Rebuy'}` : `Process ${isBuyin ? 'Buy-In' : 'Rebuy'}`)}
                                      </button>
                                  </div>
                              );
                          })()}
                      </div>
                  </form>
              </div>
          </div>
      );
  };

  const renderEliminationModal = () => {
      if (!eliminationModal.isOpen) return null;

      const { playerId, playerName, isCashout, removeOnly, payoutAmount, method, walletId, settleCredit, playerCredit, eliminatedById } = eliminationModal;
      const creditPayback = settleCredit ? Math.min(payoutAmount, playerCredit || 0) : 0;
      const cashRemainder = payoutAmount - creditPayback;
      const bountyEnabled = Number(tournament?.bounty_enabled || 0) > 0 && Number(tournament?.bounty_amount || 0) > 0;
      const bountyOptions = activePlayers.filter(p => Number(p.player_id) !== Number(playerId));
      const bountySelector = bountyEnabled && !removeOnly && bountyOptions.length > 0 ? (
          <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.35)', borderRadius: '6px' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', color: '#fb7185', fontSize: '0.85rem', fontWeight: 700 }}>
                  Bounty Won By
              </label>
              <select
                  className="game-input"
                  value={eliminatedById}
                  onChange={e => setEliminationModal(prev => ({ ...prev, eliminatedById: e.target.value }))}
              >
                  <option value="">No bounty awarded</option>
                  {bountyOptions.map(p => (
                      <option key={p.player_id} value={p.player_id}>
                          {p.name}
                      </option>
                  ))}
              </select>
              <div style={{ marginTop: '0.45rem', color: 'var(--game-text-secondary)', fontSize: '0.82rem' }}>
                  Awards ${Number(tournament.bounty_amount || 0).toLocaleString()} by reducing the winner's tournament balance.
              </div>
          </div>
      ) : null;

      const handleClose = () => setEliminationModal({ ...eliminationModal, isOpen: false });

      const handleConfirm = async () => {
          if (isSubmitting) return;
          setIsSubmitting(true);
          try {
              await axios.post(`/${cardroom}/api/tournaments/${id}/transactions`, {
                  playerId,
                  type: 'cashout',
                  amount: payoutAmount,
                  method: method || 'cash',
                  walletId: (method && method !== 'cash') ? walletId : null,
                  settleCredit: settleCredit && (playerCredit || 0) > 0,
                  eliminatedById: bountyEnabled && eliminatedById ? Number(eliminatedById) : null
              });
              setEliminationModal({ isOpen: false, playerId: null, playerName: '', isCashout: false, removeOnly: false, payoutAmount: 0, method: 'cash', walletId: '', settleCredit: false, playerCredit: 0, eliminatedById: '' });
              loadData();
          } catch (err) {
              console.error(err);
              alert(err.response?.data?.error || 'Failed to process cashout');
          } finally {
              setIsSubmitting(false);
          }
      };

      const handleEliminatePlain = async () => {
          if (isSubmitting) return;
          setIsSubmitting(true);
          try {
              await axios.post(`/${cardroom}/api/tournaments/${id}/transactions`, {
                  playerId, type: 'cashout', amount: 0, method: 'cash', walletId: null, settleCredit: false,
                  eliminatedById: bountyEnabled && eliminatedById ? Number(eliminatedById) : null
              });
              setEliminationModal({ isOpen: false, playerId: null, playerName: '', isCashout: false, removeOnly: false, payoutAmount: 0, method: 'cash', walletId: '', settleCredit: false, playerCredit: 0, eliminatedById: '' });
              loadData();
          } catch (err) {
              console.error(err);
              alert('Failed to eliminate player');
          } finally {
              setIsSubmitting(false);
          }
      };

      const handleRemoveOnly = async () => {
          if (isSubmitting) return;
          setIsSubmitting(true);
          try {
              await axios.delete(`/${cardroom}/api/tournaments/${id}/players/${playerId}`);
              setEliminationModal({ isOpen: false, playerId: null, playerName: '', isCashout: false, removeOnly: false, payoutAmount: 0, method: 'cash', walletId: '', settleCredit: false, playerCredit: 0, eliminatedById: '' });
              loadData();
          } catch (err) {
              console.error(err);
              alert(err.response?.data?.error || 'Failed to remove player');
          } finally {
              setIsSubmitting(false);
          }
      };

      return (
          <>
              <div className="game-modal-overlay" style={{ backdropFilter: 'blur(4px)' }} onClick={handleClose} />
              <div className="game-modal game-modal--transaction" style={{ zIndex: 3001 }}>
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">
                          {removeOnly ? `Remove ${playerName}` : (isCashout ? `Cashout ${playerName}` : `Eliminate ${playerName}`)}
                      </h3>
                      <button className="game-icon-btn" onClick={handleClose}><FaTimes /></button>
                  </div>
                  <div className="game-modal__body">
                      {(playerCredit || 0) !== 0 && (
                          <div style={{ marginBottom: '0.75rem' }}>
<span className={`game-player-badge ${playerCredit > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{ fontSize: '0.85rem' }}>
  <b>${Math.abs(playerCredit)}</b>&nbsp;{playerCredit > 0 ? 'Credit' : 'Balance'}
</span>
                          </div>
                      )}

                      {removeOnly ? (
                          <>
                              <p style={{ marginBottom: '1.5rem', color: 'var(--game-text-secondary)' }}>
                                  <strong style={{ color: '#fff' }}>{playerName}</strong> no longer has a tournament record. Since the player has no entries, deposits, prizes, or points left in this tournament, this will simply remove them from the tournament list.
                              </p>
                              <div style={{ display: 'flex', gap: '1rem' }}>
                                  <button type="button" className="game-btn game-btn--secondary" style={{ flex: 1 }} onClick={handleClose}>Cancel</button>
                                  <button type="button" className="game-btn game-btn--danger" style={{ flex: 1 }} disabled={isSubmitting} onClick={handleRemoveOnly}>
                                      {isSubmitting ? 'Removing...' : 'Remove From Tournament'}
                                  </button>
                              </div>
                          </>
                      ) : isCashout ? (
                          <>
                              <div style={{ background: 'var(--game-bg-tertiary)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
                                  <div style={{ fontSize: '0.8rem', color: 'var(--game-text-secondary)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Prize Payout</div>
                                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#48bb78' }}>${payoutAmount.toLocaleString()}</div>
                              </div>

                              {bountySelector}

                              {(playerCredit || 0) > 0 && (
                                  <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(214,158,46,0.1)', border: '1px solid #d69e2e', borderRadius: '6px' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600 }}>
                                          <input
                                              type="checkbox"
                                              checked={settleCredit}
                                              onChange={e => setEliminationModal(prev => ({ ...prev, settleCredit: e.target.checked }))}
                                          />
                                          <span style={{ color: '#d69e2e' }}>Settle Credit from Prize</span>
                                      </label>
                                      {settleCredit && (
                                          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--game-text-secondary)' }}>
                                              <div>Credit payback (from prize): <b style={{ color: '#d69e2e' }}>${creditPayback}</b></div>
                                              <div>Cash paid to player: <b style={{ color: '#48bb78' }}>${cashRemainder}</b></div>
                                          </div>
                                      )}
                                  </div>
                              )}

                              <div style={{ marginBottom: '1rem' }}>
                                  <label style={{ display: 'block', marginBottom: '0.4rem', color: 'var(--game-text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>Payout Method</label>
                                  <select className="game-input" value={method} onChange={e => setEliminationModal(prev => ({ ...prev, method: e.target.value, walletId: '' }))}>
                                      <option value="cash">Cash</option>
                                      <option value="venmo">Venmo</option>
                                      <option value="cashapp">CashApp</option>
                                      <option value="bitcoin">Bitcoin</option>
                                      <option value="zelle">Zelle</option>
                                      <option value="applePay">Apple Pay</option>
                                      <option value="paypal">PayPal</option>
                                      <option value="player_balance">Player Balance</option>
                                  </select>
                              </div>
                              {method !== 'cash' && method !== 'player_balance' && (
                                  <div style={{ marginBottom: '1rem' }}>
                                      <label style={{ display: 'block', marginBottom: '0.4rem', color: 'var(--game-text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>Wallet</label>
                                      <select className="game-input" value={walletId} onChange={e => setEliminationModal(prev => ({ ...prev, walletId: e.target.value }))} required>
                                          <option value="">Select Wallet</option>
                                          {tournamentWallets.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                      </select>
                                  </div>
                              )}

                              <div style={{ display: 'flex', gap: '1rem' }}>
                                  <button type="button" className="game-btn game-btn--secondary" style={{ flex: 1 }} onClick={handleClose}>Cancel</button>
                                  <button
                                      type="button"
                                      className="game-btn game-btn--success"
                                      style={{ flex: 1 }}
                                      disabled={isSubmitting || (method !== 'cash' && method !== 'player_balance' && !walletId)}
                                      onClick={handleConfirm}
                                  >
                                      {isSubmitting ? 'Processing...' : `Pay Out $${settleCredit ? cashRemainder : payoutAmount}`}
                                  </button>
                              </div>
                          </>
                      ) : (
                          <>
                              {bountySelector}
                              <p style={{ marginBottom: '1.5rem', color: 'var(--game-text-secondary)' }}>
                                  Eliminate <strong style={{ color: '#fff' }}>{playerName}</strong> from the tournament. No prize payout.
                              </p>
                              <div style={{ display: 'flex', gap: '1rem' }}>
                                  <button type="button" className="game-btn game-btn--secondary" style={{ flex: 1 }} onClick={handleClose}>Cancel</button>
                                  <button type="button" className="game-btn game-btn--danger" style={{ flex: 1 }} disabled={isSubmitting} onClick={handleEliminatePlain}>
                                      {isSubmitting ? 'Processing...' : 'Eliminate'}
                                  </button>
                              </div>
                          </>
                      )}
                  </div>
              </div>
          </>
      );
  };

  const renderDeleteTableModal = () => {
      if (!deleteTableModal.isOpen) return null;

      return (
          <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}} onClick={() => setDeleteTableModal({ ...deleteTableModal, isOpen: false })}>
              <div className="game-card" style={{padding: '2rem', width: '400px', background: 'var(--game-bg-secondary)', border: '1px solid #e53e3e'}} onClick={e => e.stopPropagation()}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                      <h3 style={{margin: 0, color: '#fc8181'}}>Delete Table</h3>
                      <button type="button" className="game-icon-btn" onClick={() => setDeleteTableModal({ ...deleteTableModal, isOpen: false })}><FaTimes />;</button>
                  </div>
                  <div style={{marginBottom: '1.5rem'}}>
                      <p style={{fontSize: '1.1rem', marginBottom: '1rem'}}>
                          Are you sure you want to remove <strong>{deleteTableModal.tableName}</strong>?
                      </p>
                  </div>
                  <div style={{display: 'flex', gap: '1rem'}}>
                      <button type="button" className="game-btn game-btn--secondary" style={{flex: 1}} onClick={() => setDeleteTableModal({ ...deleteTableModal, isOpen: false })}>
                          Cancel
                      </button>
                      <button type="button" className="game-btn game-btn--danger" style={{flex: 1}} onClick={() => handleRemoveTable(deleteTableModal.tableId)}>
                          Delete
                      </button>
                  </div>
              </div>
          </div>
      );
  };

  const renderUndoTransactionModal = () => {
      if (!undoTransactionModal.isOpen || !undoTransactionModal.transaction) return null;

      const transaction = undoTransactionModal.transaction;
      const isEliminationZero = transaction.transaction_type === "cashout" && parseFloat(transaction.amount || 0) === 0;
      const transactionTypeLabel = isEliminationZero
          ? "Eliminated"
          : formatTournamentTransactionType(transaction.transaction_type, transaction.chips);
      const amount = Number(transaction.amount || 0);

      return (
          <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}} onClick={() => setUndoTransactionModal({ isOpen: false, transaction: null })}>
              <div className="game-card" style={{padding: '2rem', width: '440px', maxWidth: 'calc(100vw - 2rem)', background: 'var(--game-bg-secondary)', border: '1px solid #e53e3e'}} onClick={e => e.stopPropagation()}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                      <h3 style={{margin: 0, color: '#fc8181'}}>Undo Transaction</h3>
                      <button type="button" className="game-icon-btn" onClick={() => setUndoTransactionModal({ isOpen: false, transaction: null })}><FaTimes /></button>
                  </div>
                  <div style={{marginBottom: '1.5rem'}}>
                      <p style={{fontSize: '1.05rem', marginBottom: '1rem', color: 'var(--game-text-primary)'}}>
                          Are you sure you want to undo and delete this transaction?
                      </p>
                      <div style={{padding: '0.9rem', borderRadius: '8px', background: 'rgba(229,62,62,0.08)', border: '1px solid rgba(229,62,62,0.28)', color: 'var(--game-text-secondary)'}}>
                          <div><strong style={{color: '#fff'}}>{transaction.player_name || 'Unknown Player'}</strong></div>
                          <div>{transactionTypeLabel} {amount > 0 ? `- $${amount}` : ''}</div>
                          <div style={{fontSize: '0.85rem', marginTop: '0.25rem'}}>
                              {new Date(transaction.timestamp).toLocaleString()}
                          </div>
                      </div>
                      <p style={{fontSize: '0.9rem', marginTop: '1rem', color: '#fc8181'}}>
                          This will reverse tournament stats, credit, wallet/cash movement, and the player record where applicable.
                      </p>
                  </div>
                  <div style={{display: 'flex', gap: '1rem'}}>
                      <button type="button" className="game-btn game-btn--secondary" style={{flex: 1}} onClick={() => setUndoTransactionModal({ isOpen: false, transaction: null })}>
                          Cancel
                      </button>
                      <button type="button" className="game-btn game-btn--danger" style={{flex: 1}} disabled={isSubmitting} onClick={() => handleUndoTournamentTransaction(transaction.id)}>
                          {isSubmitting ? 'Undoing...' : 'Undo Transaction'}
                      </button>
                  </div>
              </div>
          </div>
      );
  };

  const renderWinnerTipModal = () => {
      if (!winnerTipModal.isOpen) return null;
      const winnerOptions = getWinnerTipPlayerOptions();

      return (
          <>
              <div className="game-modal-overlay" onClick={() => setWinnerTipModal({ isOpen: false, playerId: '', amount: '', payment: 'cash' })} />
              <div className="game-modal game-modal--transaction">
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Record  Tip</h3>
                      <button className="game-icon-btn" onClick={() => setWinnerTipModal({ isOpen: false, playerId: '', amount: '', payment: 'cash' })}><FaTimes /></button>
                  </div>
                  <div className="game-modal__body">
                      <form className="game-form-vertical" onSubmit={submitWinnerTip}>
                          <label className="game-label">Player</label>
                          <select
                              className="game-input"
                              value={winnerTipModal.playerId}
                              onChange={e => setWinnerTipModal(prev => ({ ...prev, playerId: e.target.value }))}
                              required
                          >
                              <option value="">Select player</option>
                              {winnerOptions.map(player => (
                                  <option key={player.player_id} value={player.player_id}>
                                      {player.name}{player.place ? ` - ${player.place}${player.place === 1 ? 'st' : player.place === 2 ? 'nd' : player.place === 3 ? 'rd' : 'th'}` : ''}{player.cash ? ` - $${player.cash}` : ''}
                                  </option>
                              ))}
                          </select>

                          <label className="game-label">Tip Amount</label>
                          <input
                              type="number"
                              className="game-input"
                              min="1"
                              step="1"
                              value={winnerTipModal.amount}
                              onFocus={handleNumberFocus}
                              onChange={e => setWinnerTipModal(prev => ({ ...prev, amount: e.target.value }))}
                              required
                              autoFocus
                          />

                          <label className="game-label">Payment</label>
                          <select
                              className="game-input"
                              value={winnerTipModal.payment}
                              onChange={e => setWinnerTipModal(prev => ({ ...prev, payment: e.target.value }))}
                              required
                          >
                              <option value="cash">Cash</option>
                              {tournamentWallets.map(wallet => (
                                  <option key={wallet.id} value={wallet.id}>{wallet.name}</option>
                              ))}
                          </select>

                          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                              <button
                                  type="button"
                                  className="game-btn game-btn--secondary"
                                  style={{ flex: 1 }}
                                  onClick={() => setWinnerTipModal({ isOpen: false, playerId: '', amount: '', payment: 'cash' })}
                              >
                                  Cancel
                              </button>
                              <button
                                  type="submit"
                                  className="game-btn game-btn--success"
                                  style={{ flex: 1 }}
                                  disabled={isSubmitting || !winnerTipModal.playerId || !winnerTipModal.amount || Number(winnerTipModal.amount) <= 0}
                              >
                                  {isSubmitting ? 'Recording...' : 'Record Tip'}
                              </button>
                          </div>
                      </form>
                  </div>
              </div>
          </>
      );
  };

  const renderCashRefillModal = () => {
      if (!cashRefillModal.isOpen) return null;

      return (
          <>
              <div className="game-modal-overlay" onClick={() => setCashRefillModal({ isOpen: false, walletId: '', amount: '' })} />
              <div className="game-modal game-modal--transaction">
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Refill Tournament Cash Bag</h3>
                      <button className="game-icon-btn" onClick={() => setCashRefillModal({ isOpen: false, walletId: '', amount: '' })}><FaTimes /></button>
                  </div>
                  <div className="game-modal__body">
                      <form className="game-form-vertical" onSubmit={submitCashRefill}>
                          <label className="game-label">Wallet</label>
                          <select
                              className="game-input"
                              value={cashRefillModal.walletId}
                              onChange={e => setCashRefillModal(prev => ({ ...prev, walletId: e.target.value }))}
                              required
                          >
                              <option value="">Select Wallet</option>
                              {tournamentWallets.map(wallet => (
                                  <option key={wallet.id} value={wallet.id}>{wallet.name}</option>
                              ))}
                          </select>

                          <label className="game-label">Cash Amount</label>
                          <input
                              type="number"
                              className="game-input"
                              min="1"
                              step="1"
                              value={cashRefillModal.amount}
                              onFocus={handleNumberFocus}
                              onChange={e => setCashRefillModal(prev => ({ ...prev, amount: e.target.value }))}
                              required
                              autoFocus
                          />

                          <div style={{ padding: '0.75rem', borderRadius: '6px', background: 'rgba(66,153,225,0.1)', border: '1px solid rgba(66,153,225,0.3)', color: 'var(--game-text-secondary)', fontSize: '0.86rem' }}>
                              This moves money from the selected tournament wallet into this tournament's cash bag.
                          </div>

                          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                              <button
                                  type="button"
                                  className="game-btn game-btn--secondary"
                                  style={{ flex: 1 }}
                                  onClick={() => setCashRefillModal({ isOpen: false, walletId: '', amount: '' })}
                              >
                                  Cancel
                              </button>
                              <button
                                  type="submit"
                                  className="game-btn game-btn--primary"
                                  style={{ flex: 1 }}
                                  disabled={isSubmitting || !cashRefillModal.walletId || !cashRefillModal.amount || Number(cashRefillModal.amount) <= 0}
                              >
                                  {isSubmitting ? 'Refilling...' : 'Refill Cash'}
                              </button>
                          </div>
                      </form>
                  </div>
              </div>
          </>
      );
  };

  const renderFinalTableModal = () => {
      if (!finalTableModal.isOpen) return null;

      return (
          <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}} onClick={() => setFinalTableModal({ isOpen: false, tableId: null, tableName: '', isRandomizeOnly: false })}>
              <div className="game-card" style={{padding: '2rem', width: '440px', background: 'var(--game-bg-secondary)', border: '1px solid #d69e2e'}} onClick={e => e.stopPropagation()}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem'}}>
                      <h3 style={{margin: 0, color: '#d69e2e'}}>
                          {finalTableModal.isRandomizeOnly ? 'Randomize Seats' : 'Make Final Table'}
                      </h3>
                      <button type="button" className="game-icon-btn" onClick={() => setFinalTableModal({ isOpen: false, tableId: null, tableName: '', isRandomizeOnly: false })}><FaTimes /></button>
                  </div>
                  <p style={{ color: 'var(--game-text-secondary)', lineHeight: 1.5, marginBottom: '1.25rem' }}>
                      {finalTableModal.isRandomizeOnly
                          ? `This will randomize all remaining player seats at ${finalTableModal.tableName}.`
                          : `This will move all remaining players to ${finalTableModal.tableName}, randomize their seats, and close all other tables.`}
                  </p>
                  <div style={{display: 'flex', gap: '1rem'}}>
                      <button
                          type="button"
                          className="game-btn game-btn--secondary"
                          style={{flex: 1}}
                          onClick={() => setFinalTableModal({ isOpen: false, tableId: null, tableName: '', isRandomizeOnly: false })}
                      >
                          Cancel
                      </button>
                      <button
                          type="button"
                          className="game-btn game-btn--warning"
                          style={{flex: 1}}
                          disabled={isSubmitting}
                          onClick={confirmMakeFinalTable}
                      >
                          {isSubmitting ? 'Processing...' : 'Confirm'}
                      </button>
                  </div>
              </div>
          </div>
      );
  };

  const renderEndTournamentModal = () => {
      if (!endTournamentModal.isOpen) return null;

      const creditPlayers = (tournament.players || []).filter(p => (p.tournament_credit || 0) !== 0);
      const totalOutstanding = creditPlayers.reduce((sum, p) => sum + (p.tournament_credit || 0), 0);

      return (
          <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}} onClick={() => setEndTournamentModal({ isOpen: false })}>
              <div className="game-card" style={{padding: '2rem', width: '400px', background: 'var(--game-bg-secondary)', border: '1px solid #e53e3e'}} onClick={e => e.stopPropagation()}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                      <h3 style={{margin: 0, color: '#fc8181'}}>{hasNoTournamentPlayers ? 'Cancel Tournament' : 'End Tournament'}</h3>
                      <button type="button" className="game-icon-btn" onClick={() => setEndTournamentModal({ isOpen: false })}><FaTimes /></button>
                  </div>
                  <div style={{marginBottom: '1.5rem'}}>
                      <p style={{fontSize: '1.1rem', marginBottom: '1rem'}}>
                          Are you sure you want to <strong>{hasNoTournamentPlayers ? 'CANCEL' : 'END'}</strong> this tournament?
                      </p>
                      <p style={{color: '#a0aec0', marginTop: '0.5rem', fontSize: '0.9rem'}}>
                          {hasNoTournamentPlayers
                              ? 'No players have been registered, seated, or eliminated. This will simply delete the tournament.'
                              : 'This action cannot be undone. All active non-eliminated players and tables will be removed, and the tournament will be moved to history.'}
                      </p>
                      {creditPlayers.length > 0 && (
                          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'rgba(214,158,46,0.12)', border: '1px solid #d69e2e', borderRadius: '6px', fontSize: '0.875rem', color: '#d69e2e' }}>
                              <strong>Unsettled Balances: ${Math.abs(totalOutstanding)}</strong>
                              <div style={{ marginTop: '0.25rem', color: '#a0aec0' }}>
                                  {creditPlayers.length} player{creditPlayers.length !== 1 ? 's have' : ' has'} tournament balances that have not been settled. Positive and negative balances will be moved to their book when the tournament ends.
                              </div>
                          </div>
                      )}
                  </div>
                  <div style={{display: 'flex', gap: '1rem'}}>
                      <button type="button" className="game-btn game-btn--secondary" style={{flex: 1}} onClick={() => setEndTournamentModal({ isOpen: false })}>
                          Cancel
                      </button>
                      <button type="button" className="game-btn game-btn--danger" style={{flex: 1}} onClick={confirmEndTournament}>
                          {hasNoTournamentPlayers ? 'Delete' : 'Confirm'}
                      </button>
                  </div>
              </div>
          </div>
      );
  };

  const renderQuickAddModal = () => {
      if (!showQuickAddModal) return null;

      return (
          <>
              <div className="game-modal-overlay" onClick={closeQuickAddModal} />
              <div className="game-modal">
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Quick Add Player</h3>
                      <button className="game-icon-btn" onClick={closeQuickAddModal}>
                          <FaTimes />
                      </button>
                  </div>
                  <div className="game-modal__body">
                      <form className="game-form-vertical" onSubmit={handleQuickAddPlayer}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              <div>
                                  <label className="game-label">First Name *</label>
                                  <input
                                      className="game-input"
                                      type="text"
                                      value={qFirstName}
                                      onChange={(e) => setQFirstName(e.target.value)}
                                      placeholder="First name"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="game-label">Last Name</label>
                                  <input
                                      className="game-input"
                                      type="text"
                                      value={qLastName}
                                      onChange={(e) => setQLastName(e.target.value)}
                                      placeholder="Last name"
                                  />
                              </div>
                              <div>
                                  <label className="game-label">Nickname</label>
                                  <input
                                      className="game-input"
                                      type="text"
                                      value={qNickname}
                                      onChange={(e) => setQNickname(e.target.value)}
                                      placeholder={
                                          getDefaultPlayerNickname(qFirstName, qLastName)
                                      }
                                      maxLength={64}
                                  />
                              </div>
                              <div>
                                  <label className="game-label">Phone Number</label>
                                  <input
                                      className="game-input"
                                      type="text"
                                      value={qPhone}
                                      onChange={(e) => setQPhone(formatPhoneNumber(e.target.value))}
                                      placeholder="XXX-XXX-XXXX"
                                      maxLength={12}
                                  />
                              </div>
                          </div>
                          <div className="game-modal__footer">
                              <button type="button" className="game-btn game-btn--ghost" onClick={closeQuickAddModal}>
                                  Cancel
                              </button>
                              <button type="submit" className="game-btn game-btn--primary" disabled={isSubmitting || quickAddBtnDisabled}>
                                  {quickAddBtnDisabled && qFirstName.trim() ? "Name / Nickname Taken" : "Create Player"}
                              </button>
                          </div>
                      </form>
                  </div>
              </div>
          </>
      );
  };

  const renderAddonModal = () => {
      if (!addonModal.isOpen) return null;

      const numPlayers = selectedAddonPlayers.length;
      const addonCost = tournament.addons_cost || 0;
      const totalCost = addonCost * numPlayers;
      const addonChips = tournament.addons_chips || 0;
      const totalChips = addonChips * numPlayers;
      const isCreditAddon = !!addonModal.isCredit;

      return (
          <>
              <div className="game-modal-overlay" onClick={() => setAddonModal({ isOpen: false, isCredit: false })} />
              <div className="game-modal game-modal--transaction">
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Confirm {isCreditAddon ? 'Credit' : 'Cash'} Add-Ons</h3>
                      <button className="game-icon-btn" onClick={() => setAddonModal({ isOpen: false, isCredit: false })}>
                          <FaTimes />
                      </button>
                  </div>
                  <div className="game-modal__body">
                      <form className="game-form-vertical" onSubmit={submitBulkAddons}>
                          <div style={{background: 'var(--game-bg-tertiary)', padding: '1rem', borderRadius: '4px', marginBottom: '1rem'}}>
                              <p style={{margin: '0 0 0.5rem 0'}}><strong>Selected Players:</strong> {numPlayers}</p>
                              <p style={{margin: '0 0 0.5rem 0'}}><strong>Total Chips Issued:</strong> {totalChips.toLocaleString()}</p>
                              <p style={{margin: '0 0 0.5rem 0', color: isCreditAddon ? '#d69e2e' : 'var(--game-success)', fontWeight: 'bold'}}>
                                  <strong>{isCreditAddon ? 'Total Credit Added:' : 'Total Cash to Collect:'}</strong> ${totalCost.toLocaleString()}
                              </p>
                              <p style={{margin: 0, color: 'var(--game-text-secondary)', fontSize: '0.9rem'}}>
                                  {isCreditAddon ? 'These add-ons will be added to player tournament credit.' : 'These add-ons will be recorded as cash payments.'}
                              </p>
                          </div>

                          <button 
                              type="submit" 
                              className={`game-btn ${isCreditAddon ? 'game-btn--warning' : 'game-btn--primary'}`}
                              style={{width: '100%', marginTop: '1rem', padding: '1rem', fontSize: '1.1rem'}}
                              disabled={isSubmitting || numPlayers === 0}
                          >
                              {isSubmitting ? 'Processing...' : `Confirm ${numPlayers} ${isCreditAddon ? 'Credit' : 'Cash'} Add-On${numPlayers !== 1 ? 's' : ''}`}
                          </button>
                      </form>
                  </div>
              </div>
          </>
      );
  };

  const renderPayoutEditModal = () => {
      if (!payoutEditModal.isOpen) return null;

      // Locked places are the HIGH-numbered ones (9th, 8th, â€¦) because eliminations
      // happen from the bubble inward: the first person out gets the last money place.
      // A place is locked when its place number exceeds the current active player count
      // (i.e. that player has already been eliminated & paid out).
      // e.g. 9 places paid, 7 still active â†’ places 8 & 9 are locked.
      const lockedAbove = Math.min(earlyActivePlayers.length, placesPaid); // first N places still "live"
      // place > lockedAbove  â†’  already paid out (locked)

      // Sort ascending: place 1 at top, place N at bottom (locked rows at bottom)
      const sortedPayouts = [...editablePayouts].sort((a, b) => a.place - b.place);

      const ordinal = (n) => {
          const s = ['th','st','nd','rd'];
          const v = n % 100;
          return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };

      const editTotal = editablePayouts.reduce((sum, p) => sum + (parseInt(p.amount, 10) || 0), 0);
      const isValid = editTotal === prizepool;
      const diff = editTotal - prizepool;

      const handleAmountChange = (placeIdx, value) => {
          setEditablePayouts(prev => prev.map((p, i) =>
              i === placeIdx ? { ...p, amount: value === '' ? '' : parseInt(value, 10) || 0 } : p
          ));
      };

      const handleSave = async () => {
          if (!isValid || isSubmitting) return;
          setIsSubmitting(true);
          try {
              await axios.patch(`/${cardroom}/api/tournaments/${id}/payouts`, {
                  customPayouts: editablePayouts.map(p => ({
                      place: p.place,
                      amount: parseInt(p.amount, 10) || 0,
                      percentage: prizepool > 0 ? ((parseInt(p.amount, 10) || 0) / prizepool) * 100 : 0
                  }))
              });
              setPayoutEditModal({ isOpen: false });
              await loadData();
          } catch (err) {
              console.error('Save payouts error:', err);
          } finally {
              setIsSubmitting(false);
          }
      };

      const handleResetToAuto = () => {
          // Locked places (already paid out) keep their current amounts unchanged.
          // Only recalculate the editable (unlocked) places using the remaining pool.
          const lockedPayouts = editablePayouts.filter(p => p.place > lockedAbove);
          const unlockedPlaces = editablePayouts.filter(p => p.place <= lockedAbove);
          const lockedSum = lockedPayouts.reduce((sum, p) => sum + (parseInt(p.amount, 10) || 0), 0);
          const remainingPool = prizepool - lockedSum;
          const numUnlocked = unlockedPlaces.length;

          if (numUnlocked === 0) return; // nothing to reset

          // Recalculate for only the unlocked places (places 1..lockedAbove)
          const freshPayouts = calculatePayouts(remainingPool, numUnlocked, payoutExponent, buyin, payoutMinCashPercent);

          // Map back: freshPayouts[0] â†’ 1st place, freshPayouts[1] â†’ 2nd place, etc.
          setEditablePayouts(prev => prev.map(p => {
              if (p.place > lockedAbove) return p; // keep locked as-is
              const fresh = freshPayouts.find(fp => fp.place === p.place);
              return fresh ? { ...p, amount: fresh.amount, percentage: fresh.percentage } : p;
          }));
      };



      return (
          <>
              <div className="game-modal-overlay" onClick={() => setPayoutEditModal({ isOpen: false })} />
              <div className="game-modal game-modal--large" style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>

                  {/* Header */}
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Edit Payouts</h3>
                      <button className="game-icon-btn" onClick={() => setPayoutEditModal({ isOpen: false })}>
                          <FaTimes />
                      </button>
                  </div>

                  {/* Context info */}
                  <div style={{ padding: '0 1.5rem 0.75rem', fontSize: '0.85rem', color: 'var(--game-text-secondary)' }}>
                      Prize Pool: <strong style={{ color: '#48bb78' }}>${prizepool.toLocaleString()}</strong>
                      &nbsp;Â·&nbsp; Places Paid: <strong>{placesPaid}</strong>
                      &nbsp;Â·&nbsp; Active Players: <strong>{earlyActivePlayers.length}</strong>
                      {placesPaid - lockedAbove > 0 && (
                          <span style={{ color: '#f56565' }}>&nbsp;Â·&nbsp; {placesPaid - lockedAbove} place{placesPaid - lockedAbove !== 1 ? 's' : ''} already paid out (locked)</span>
                      )}
                  </div>

                  {/* Scrollable payout table */}
                  <div className="game-modal__body game-modal__body--scroll" style={{ flex: 1, padding: '0 1.5rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                          <thead>
                              <tr style={{ borderBottom: '2px solid #4a5568' }}>
                                  <th style={{ textAlign: 'left', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)', fontWeight: 600, width: '60px' }}>Place</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)', fontWeight: 600 }}>Prize ($)</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)', fontWeight: 600, width: '70px' }}>%</th>
                                  <th style={{ textAlign: 'center', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)', fontWeight: 600, width: '60px' }}>Status</th>
                              </tr>
                          </thead>
                          <tbody>
                              {sortedPayouts.map((p, i) => {
                                  const locked = p.place > lockedAbove;
                                  const pctDisplay = prizepool > 0
                                      ? (((parseInt(p.amount, 10) || 0) / prizepool) * 100).toFixed(1)
                                      : '0.0';
                                  // Find original index in editablePayouts for updates
                                  const origIdx = editablePayouts.findIndex(ep => ep.place === p.place);
                                  return (
                                      <tr
                                          key={p.place}
                                          style={{
                                              borderBottom: '1px solid #2d3748',
                                              background: locked ? 'rgba(245,101,101,0.05)' : 'transparent',
                                              opacity: locked ? 0.65 : 1
                                          }}
                                      >
                                          <td style={{ padding: '0.5rem 0.25rem', fontWeight: 700 }}>
                                              {ordinal(p.place)}
                                          </td>
                                          <td style={{ padding: '0.4rem 0.25rem', textAlign: 'right' }}>
                                              {locked ? (
                                                  <span style={{ fontWeight: 700, color: '#a0aec0' }}>
                                                      ${(parseInt(p.amount, 10) || 0).toLocaleString()}
                                                  </span>
                                              ) : (
                                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                                                      <span style={{ color: 'var(--game-text-secondary)' }}>$</span>
                                                      <input
                                                          type="number"
                                                          min="0"
                                                          step="1"
                                                          
                                                          value={p.amount}
                                                          onFocus={handleNumberFocus}
                                                          onChange={(e) => handleAmountChange(origIdx, e.target.value)}
                                                          onWheel={(e) => e.currentTarget.blur()} // prevent scroll change
                                                          style={{
                                                              width: '110px',
                                                              background: '#1a202c',
                                                              border: '1px solid #4a5568',
                                                              borderRadius: '4px',
                                                              color: '#e2e8f0',
                                                              padding: '0.3rem 0.5rem',
                                                              textAlign: 'right',
                                                              fontSize: '0.9rem'
                                                          }}
                                                      />
                                                  </div>
                                              )}
                                          </td>
                                          <td style={{ padding: '0.5rem 0.25rem', textAlign: 'right', color: 'var(--game-text-secondary)' }}>
                                              {pctDisplay}%
                                          </td>
                                          <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center' }}>
                                              {locked ? (
                                                  <span title="Already paid out" style={{ fontSize: '1rem' }}></span>
                                              ) : (
                                                  <span title="Upcoming â€” editable" style={{ fontSize: '1rem', color: '#48bb78' }}>âœ“</span>
                                              )}
                                          </td>
                                      </tr>
                                  );
                              })}
                          </tbody>
                      </table>
                  </div>

                  {/* Sticky footer */}
                  <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #4a5568', background: 'var(--game-bg-secondary)' }}>
                      {/* Total bar */}
                      <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.75rem',
                          padding: '0.6rem 1rem',
                          borderRadius: '6px',
                          background: isValid ? 'rgba(72,187,120,0.1)' : 'rgba(245,101,101,0.1)',
                          border: `1px solid ${isValid ? '#48bb78' : '#f56565'}`
                      }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Total Assigned</span>
                          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: isValid ? '#48bb78' : '#f56565' }}>
                              ${editTotal.toLocaleString()} / ${prizepool.toLocaleString()}
                              {!isValid && (
                                  <span style={{ fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                                      ({diff > 0 ? '+' : ''}{diff.toLocaleString()})
                                  </span>
                              )}
                          </span>
                      </div>

                      {/* Buttons row */}
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                          <button
                              type="button"
                              className="game-btn game-btn--secondary"
                              style={{ flex: 1 }}
                              onClick={handleResetToAuto}
                          >
                              â†º Reset to Auto
                          </button>

                          <button
                              type="button"
                              className="game-btn game-btn--primary"
                              style={{ flex: 1 }}
                              onClick={handleSave}
                              disabled={!isValid || isSubmitting}
                              title={!isValid ? `Total must equal $${prizepool.toLocaleString()}` : ''}
                          >
                              {isSubmitting ? 'Savingâ€¦' : 'âœ“ Save Payouts'}
                          </button>
                      </div>
                  </div>
              </div>
          </>
      );
  };

  const renderPayoutEditModalV2 = () => {
      if (!payoutEditModal.isOpen) return null;

      const normalizePlaces = (rows) => rows
          .sort((a, b) => (Number(a.place) || 0) - (Number(b.place) || 0))
          .map((p, idx) => ({ ...p, place: idx + 1 }));
      const rows = normalizePlaces(editablePayouts);
      const lockedAbove = Math.min(earlyActivePlayers.length, rows.length);
      const lockedCount = rows.filter(p => p.place > lockedAbove).length;
      const canChangePlaceCount = lockedCount === 0;
      const canAddPlace = canChangePlaceCount && rows.length < Math.max(1, earlyActivePlayers.length);

      const ordinal = (n) => {
          const s = ['th','st','nd','rd'];
          const v = n % 100;
          return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      const getPercentage = (p) => {
          if (p.percentage !== undefined && p.percentage !== '') return Number(p.percentage) || 0;
          return prizepool > 0 ? ((Number(p.amount) || 0) / prizepool) * 100 : 0;
      };
      const getEstimatedAmount = (p) => {
          const locked = p.place > lockedAbove;
          if (locked && p.amount !== undefined) return Number(p.amount) || 0;
          if (p.amount !== undefined && p.amount !== '') return Number(p.amount) || 0;
          return Math.round(prizepool * (getPercentage(p) / 100));
      };
      const roundPercent = (value) => Math.round((Number(value) || 0) * 100) / 100;
      const normalizePercentagesToTotal = (percentages, targetTotal) => {
          if (!percentages.length) return [];
          const targetCents = Math.round((Number(targetTotal) || 0) * 100);
          const rawCents = percentages.map(value => (Number(value) || 0) * 100);
          const roundedCents = rawCents.map(value => Math.round(value));
          let diff = targetCents - roundedCents.reduce((sum, value) => sum + value, 0);
          const order = rawCents
              .map((value, index) => ({
                  index,
                  fraction: value - Math.floor(value)
              }))
              .sort((a, b) => diff >= 0 ? b.fraction - a.fraction : a.fraction - b.fraction);

          let cursor = 0;
          while (diff !== 0 && order.length) {
              const target = order[cursor % order.length].index;
              if (diff > 0) {
                  roundedCents[target] += 1;
                  diff -= 1;
              } else if (roundedCents[target] > 0) {
                  roundedCents[target] -= 1;
                  diff += 1;
              }
              cursor += 1;
          }

          return roundedCents.map(value => value / 100);
      };

      const percentTotal = rows.reduce((sum, p) => sum + getPercentage(p), 0);
      const roundedPercentTotal = Math.round(percentTotal * 100) / 100;
      const isValid = rows.length > 0;
      const hasPercentageWarning = Math.abs(percentTotal - 100) >= 0.01;
      const unlockedRows = rows.filter(p => p.place <= lockedAbove);
      const lockedRows = rows.filter(p => p.place > lockedAbove);
      const lockedPercent = lockedRows.reduce((sum, p) => sum + getPercentage(p), 0);
      const availablePercent = Math.max(0, 100 - lockedPercent);
      const icmStacks = unlockedRows.map(p => Number(icmChopModal.stacks[p.place]) || 0);
      const exactIcmAllowed = unlockedRows.length > 0 && unlockedRows.length <= 16;
      const allIcmStacksValid = exactIcmAllowed && icmStacks.every(stack => stack > 0);
      const icmPrizeAmounts = unlockedRows.map(p => getEstimatedAmount(p));

      const calculateIcmEquities = (stacks, prizes) => {
          const n = stacks.length;
          if (!n || stacks.some(stack => stack <= 0)) return [];
          const maxMask = 1 << n;
          const totalStack = stacks.reduce((sum, stack) => sum + stack, 0);
          const dp = new Array(maxMask).fill(0);
          const usedStack = new Array(maxMask).fill(0);
          const equities = new Array(n).fill(0);

          for (let mask = 1; mask < maxMask; mask++) {
              const bit = mask & -mask;
              const idx = Math.log2(bit);
              usedStack[mask] = usedStack[mask ^ bit] + stacks[idx];
          }

          dp[0] = 1;
          for (let mask = 0; mask < maxMask; mask++) {
              const placeIdx = usedStack[mask] === totalStack ? n : countBits(mask);
              if (placeIdx >= n || dp[mask] <= 0) continue;
              const remainingStack = totalStack - usedStack[mask];
              if (remainingStack <= 0) continue;
              for (let i = 0; i < n; i++) {
                  if (mask & (1 << i)) continue;
                  const probability = dp[mask] * (stacks[i] / remainingStack);
                  equities[i] += probability * (Number(prizes[placeIdx]) || 0);
                  dp[mask | (1 << i)] += probability;
              }
          }

          return equities;
      };

      const icmEquities = allIcmStacksValid ? calculateIcmEquities(icmStacks, icmPrizeAmounts) : [];
      const icmRows = unlockedRows.map((p, idx) => {
          const amount = Math.round(icmEquities[idx] || 0);
          return {
              ...p,
              stack: icmChopModal.stacks[p.place] ?? '',
              icmAmount: amount,
              icmPercentage: prizepool > 0 ? (amount / prizepool) * 100 : 0
          };
      });

      function countBits(mask) {
          let count = 0;
          while (mask) {
              mask &= mask - 1;
              count++;
          }
          return count;
      }

      const handlePercentageChange = (place, value) => {
          setEditablePayouts(prev => normalizePlaces(prev).map(p =>
              p.place === place
                  ? { ...p, percentage: value, amount: Math.round(prizepool * ((Number(value) || 0) / 100)) }
                  : p
          ));
      };
      const handleAmountChange = (place, value) => {
          setEditablePayouts(prev => normalizePlaces(prev).map(p => {
              if (p.place !== place) return p;
              const amount = value === '' ? '' : Math.max(0, Number(value) || 0);
              const percentage = prizepool > 0 && amount !== ''
                  ? Math.round((amount / prizepool) * 10000) / 100
                  : '';
              return { ...p, amount, percentage };
          }));
      };
      const handleAddPlace = () => {
          if (!canAddPlace) return;
          setEditablePayouts(prev => {
              const nextRows = normalizePlaces(prev);
              const currentTotal = nextRows.reduce((sum, p) => sum + getPercentage(p), 0);
              const percentage = Math.max(0, Math.round((100 - currentTotal) * 100) / 100);
              return [...nextRows, { place: nextRows.length + 1, percentage, amount: Math.round(prizepool * (percentage / 100)) }];
          });
      };
      const handleRemovePlace = (place) => {
          const row = rows.find(p => p.place === place);
          if (!canChangePlaceCount || !row || row.place > lockedAbove || rows.length <= 1) return;
          setEditablePayouts(prev => normalizePlaces(prev.filter(p => (Number(p.place) || 0) !== place)));
      };
      const handleResetToAuto = () => {
          const lockedRows = rows.filter(p => p.place > lockedAbove);
          const unlockedRows = rows.filter(p => p.place <= lockedAbove);
          if (unlockedRows.length === 0) return;
          const lockedPercent = lockedRows.reduce((sum, p) => sum + getPercentage(p), 0);
          const availablePercent = roundPercent(Math.max(0, 100 - lockedPercent));
          const fresh = calculatePayouts(100, unlockedRows.length, payoutExponent, buyin, 0);
          const freshTotal = fresh.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) || 100;
          const normalizedPercents = normalizePercentagesToTotal(
              unlockedRows.map(p => {
                  const auto = fresh.find(fp => fp.place === p.place);
                  return auto ? (auto.amount / freshTotal) * availablePercent : 0;
              }),
              availablePercent
          );
          setEditablePayouts(rows.map(p => {
              if (p.place > lockedAbove) return p;
              const unlockedIndex = unlockedRows.findIndex(row => row.place === p.place);
              const percentage = normalizedPercents[unlockedIndex] ?? 0;
              return { ...p, percentage, amount: Math.round(prizepool * (percentage / 100)) };
          }));
      };
      const handleOpenIcmChop = () => {
          const nextStacks = {};
          unlockedRows.forEach(p => {
              nextStacks[p.place] = icmChopModal.stacks[p.place] ?? '';
          });
          setIcmChopModal({ isOpen: true, stacks: nextStacks });
      };
      const handleIcmStackChange = (place, value) => {
          setIcmChopModal(prev => ({
              ...prev,
              stacks: { ...prev.stacks, [place]: value }
          }));
      };
      const handleConfirmIcmChop = () => {
          if (!allIcmStacksValid) return;
          const lockedPercent = lockedRows.reduce((sum, p) => sum + getPercentage(p), 0);
          const targetPercent = roundPercent(Math.max(0, 100 - lockedPercent));
          const sortedIcmAmounts = icmRows
              .map(row => Number(row.icmAmount) || 0)
              .sort((a, b) => a - b);
          const orderedIcmAmounts = [...sortedIcmAmounts].reverse();
          const normalizedPercents = normalizePercentagesToTotal(
              orderedIcmAmounts.map(amount => prizepool > 0 ? (amount / prizepool) * 100 : 0),
              targetPercent
          );
          setEditablePayouts(prev => normalizePlaces(prev).map(p => {
              const unlockedIndex = unlockedRows.findIndex(row => row.place === p.place);
              if (unlockedIndex < 0) return p;
              return {
                  ...p,
                  percentage: normalizedPercents[unlockedIndex] ?? 0,
                  amount: orderedIcmAmounts[unlockedIndex] ?? 0
              };
          }));
          setIcmChopModal({ isOpen: false, stacks: icmChopModal.stacks });
      };
      const handleSave = async () => {
          if (!isValid || isSubmitting) return;
          setIsSubmitting(true);
          try {
              const normalized = normalizePlaces(editablePayouts);
              await axios.patch(`/${cardroom}/api/tournaments/${id}/payouts`, {
                  payoutMode: 'custom',
                  customPayouts: normalized.map(p => ({
                      place: p.place,
                      percentage: getPercentage(p),
                      amount: getEstimatedAmount(p)
                  }))
              });
              setPayoutEditModal({ isOpen: false });
              await loadData();
          } catch (err) {
              console.error('Save payouts error:', err);
          } finally {
              setIsSubmitting(false);
          }
      };

      return (
          <>
              <div className="game-modal-overlay" onClick={() => setPayoutEditModal({ isOpen: false })} />
              <div className="game-modal game-modal--large" style={{ maxWidth: '680px', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Edit Payouts</h3>
                      <button className="game-icon-btn" onClick={() => setPayoutEditModal({ isOpen: false })}>
                          <FaTimes />
                      </button>
                  </div>
                  <div style={{ padding: '0 1.5rem 0.75rem', fontSize: '0.85rem', color: 'var(--game-text-secondary)' }}>
                      Prize Pool: <strong style={{ color: '#48bb78' }}>${prizepool.toLocaleString()}</strong>
                      &nbsp;-&nbsp; Places Paid: <strong>{rows.length}</strong>
                      &nbsp;-&nbsp; Active Players: <strong>{earlyActivePlayers.length}</strong>
                      {lockedCount > 0 && (
                          <span style={{ color: '#f56565' }}>&nbsp;-&nbsp; {lockedCount} paid place{lockedCount !== 1 ? 's' : ''} locked</span>
                      )}
                  </div>
                  <div className="game-modal__body game-modal__body--scroll" style={{ flex: 1, padding: '0 1.5rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                          <thead>
                              <tr style={{ borderBottom: '2px solid #4a5568' }}>
                                  <th style={{ textAlign: 'left', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>Place</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>%</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>Est Prize</th>
                                  <th style={{ textAlign: 'center', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>Status</th>
                                  <th style={{ width: '44px' }}></th>
                              </tr>
                          </thead>
                          <tbody>
                              {rows.map((p) => {
                                  const locked = p.place > lockedAbove;
                                  const percentage = getPercentage(p);
                                  return (
                                      <tr key={p.place} style={{ borderBottom: '1px solid #2d3748', background: locked ? 'rgba(245,101,101,0.05)' : 'transparent', opacity: locked ? 0.65 : 1 }}>
                                          <td style={{ padding: '0.5rem 0.25rem', fontWeight: 700 }}>{ordinal(p.place)}</td>
                                          <td style={{ padding: '0.4rem 0.25rem', textAlign: 'right' }}>
                                              {locked ? (
                                                  <span style={{ color: '#a0aec0', fontWeight: 700 }}>{percentage.toFixed(2)}%</span>
                                              ) : (
                                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                                                      <input
                                                          type="number"
                                                          min="0"
                                                          step="0.01"
                                                          value={p.percentage ?? percentage}
                                                          onFocus={handleNumberFocus}
                                                          onChange={(e) => handlePercentageChange(p.place, e.target.value)}
                                                          onWheel={(e) => e.currentTarget.blur()} // prevent scroll change
                                                          style={{ width: '100px', background: '#1a202c', border: '1px solid #4a5568', borderRadius: '4px', color: '#e2e8f0', padding: '0.3rem 0.5rem', textAlign: 'right' }}
                                                      />
                                                      <span style={{ color: 'var(--game-text-secondary)' }}>%</span>
                                                  </div>
                                              )}
                                          </td>
                                          <td style={{ padding: '0.4rem 0.25rem', textAlign: 'right' }}>
                                              {locked ? (
                                                  <span style={{ color: '#a0aec0', fontWeight: 700 }}>${getEstimatedAmount(p).toLocaleString()}</span>
                                              ) : (
                                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                                                      <span style={{ color: 'var(--game-text-secondary)' }}>$</span>
                                                      <input
                                                          type="number"
                                                          min="0"
                                                          step="1"
                                                          value={p.amount ?? getEstimatedAmount(p)}
                                                          onFocus={handleNumberFocus}
                                                          onChange={(e) => handleAmountChange(p.place, e.target.value)}
                                                          onWheel={(e) => e.currentTarget.blur()}
                                                          style={{ width: '110px', background: '#1a202c', border: '1px solid #4a5568', borderRadius: '4px', color: '#e2e8f0', padding: '0.3rem 0.5rem', textAlign: 'right' }}
                                                      />
                                                  </div>
                                              )}
                                          </td>
                                          <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center', color: locked ? '#f56565' : '#48bb78' }}>{locked ? 'Locked' : 'Editable'}</td>
                                          <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center' }}>
                                              <button
                                                  type="button"
                                                  className="game-icon-btn game-icon-btn--danger"
                                                  onClick={() => handleRemovePlace(p.place)}
                                                  disabled={!canChangePlaceCount || locked || rows.length <= 1}
                                                  title={!canChangePlaceCount ? 'Places cannot be added or removed after payouts begin' : (locked ? 'Paid places cannot be removed' : 'Remove place')}
                                              >
                                                  <FaTrash />
                                              </button>
                                          </td>
                                      </tr>
                                  );
                              })}
                          </tbody>
                      </table>
                      <button
                          type="button"
                          className="game-btn game-btn--secondary"
                          style={{ width: '100%', justifyContent: 'center', marginTop: '1rem' }}
                          onClick={handleAddPlace}
                          disabled={!canAddPlace}
                          title={!canChangePlaceCount ? 'Places cannot be added or removed after payouts begin' : (!canAddPlace ? 'Cannot add a payout for already-eliminated players' : '')}
                      >
                          <FaPlus /> Add Prize
                      </button>
                  </div>
                  <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #4a5568', background: 'var(--game-bg-secondary)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', padding: '0.6rem 1rem', borderRadius: '6px', background: hasPercentageWarning ? 'rgba(245,101,101,0.1)' : 'rgba(72,187,120,0.1)', border: `1px solid ${hasPercentageWarning ? '#f56565' : '#48bb78'}` }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Total Percentage</span>
                          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: hasPercentageWarning ? '#f56565' : '#48bb78' }}>{roundedPercentTotal.toFixed(2)}% / 100%</span>
                      </div>
                      {hasPercentageWarning && (
                          <div style={{ marginBottom: '0.75rem', color: '#f56565', fontSize: '0.85rem', fontWeight: 700 }}>
                              Warning: payout percentages do not add up to 100%. You can still save this payout structure.
                          </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                          <button type="button" className="game-btn game-btn--secondary" style={{ flex: 1 }} onClick={handleResetToAuto}>Reset to Auto</button>
                          <button type="button" className="game-btn game-btn--secondary" style={{ flex: 1 }} onClick={handleOpenIcmChop} disabled={unlockedRows.length === 0}>
                              ICM Chop
                          </button>
                          <button type="button" className="game-btn game-btn--primary" style={{ flex: 1 }} onClick={handleSave} disabled={!isValid || isSubmitting}>
                              {isSubmitting ? 'Saving...' : 'Save Payouts'}
                          </button>
                      </div>
                  </div>
              </div>
              {icmChopModal.isOpen && (
                  <>
                      <div className="game-modal-overlay" style={{ zIndex: 4000 }} onClick={() => setIcmChopModal(prev => ({ ...prev, isOpen: false }))} />
                      <div className="game-modal game-modal--large" style={{ maxWidth: '760px', display: 'flex', flexDirection: 'column', maxHeight: '85vh', zIndex: 4001 }}>
                          <div className="game-modal__header">
                              <h3 className="game-modal__title">ICM Chop</h3>
                              <button className="game-icon-btn" onClick={() => setIcmChopModal(prev => ({ ...prev, isOpen: false }))}>
                                  <FaTimes />
                              </button>
                          </div>
                          <div style={{ padding: '0 1.5rem 0.75rem', fontSize: '0.85rem', color: 'var(--game-text-secondary)' }}>
                              Enter chip stacks for the unpaid payout places. Confirming will update the editable percentages in the payout modal.
                              {lockedRows.length > 0 && (
                                  <span style={{ color: '#f56565' }}> Locked paid places keep their existing {lockedPercent.toFixed(2)}%.</span>
                              )}
                          </div>
                          {!exactIcmAllowed && (
                              <div style={{ margin: '0 1.5rem 0.75rem', padding: '0.75rem', borderRadius: '6px', background: 'rgba(245,101,101,0.1)', border: '1px solid #f56565', color: '#f56565', fontSize: '0.85rem', fontWeight: 700 }}>
                                  Exact ICM is available for up to 16 unpaid payout places.
                              </div>
                          )}
                          <div className="game-modal__body game-modal__body--scroll" style={{ flex: 1, padding: '0 1.5rem' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                  <thead>
                                      <tr style={{ borderBottom: '2px solid #4a5568' }}>
                                          <th style={{ textAlign: 'left', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>Place</th>
                                          <th style={{ textAlign: 'right', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>Current Prize</th>
                                          <th style={{ textAlign: 'right', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>Chip Stack</th>
                                          <th style={{ textAlign: 'right', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>ICM %</th>
                                          <th style={{ textAlign: 'right', padding: '0.5rem 0.25rem', color: 'var(--game-text-secondary)' }}>ICM Prize</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                      {icmRows.map((p) => (
                                          <tr key={p.place} style={{ borderBottom: '1px solid #2d3748' }}>
                                              <td style={{ padding: '0.5rem 0.25rem', fontWeight: 700 }}>{ordinal(p.place)}</td>
                                              <td style={{ padding: '0.5rem 0.25rem', textAlign: 'right', fontWeight: 700 }}>${getEstimatedAmount(p).toLocaleString()}</td>
                                              <td style={{ padding: '0.4rem 0.25rem', textAlign: 'right' }}>
                                                  <input
                                                      type="number"
                                                      min="1"
                                                      step="1"
                                                      value={p.stack}
                                                      onFocus={handleNumberFocus}
                                                      onChange={(e) => handleIcmStackChange(p.place, e.target.value)}
                                                      onWheel={(e) => e.currentTarget.blur()}
                                                      style={{ width: '130px', background: '#1a202c', border: '1px solid #4a5568', borderRadius: '4px', color: '#e2e8f0', padding: '0.3rem 0.5rem', textAlign: 'right' }}
                                                  />
                                              </td>
                                              <td style={{ padding: '0.5rem 0.25rem', textAlign: 'right', color: allIcmStacksValid ? '#48bb78' : 'var(--game-text-secondary)', fontWeight: 700 }}>
                                                  {allIcmStacksValid ? `${p.icmPercentage.toFixed(2)}%` : '-'}
                                              </td>
                                              <td style={{ padding: '0.5rem 0.25rem', textAlign: 'right', color: allIcmStacksValid ? '#48bb78' : 'var(--game-text-secondary)', fontWeight: 700 }}>
                                                  {allIcmStacksValid ? `$${p.icmAmount.toLocaleString()}` : '-'}
                                              </td>
                                          </tr>
                                      ))}
                                  </tbody>
                              </table>
                          </div>
                          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #4a5568', background: 'var(--game-bg-secondary)' }}>
                              <div style={{ display: 'flex', gap: '0.75rem' }}>
                                  <button type="button" className="game-btn game-btn--secondary" style={{ flex: 1 }} onClick={() => setIcmChopModal(prev => ({ ...prev, isOpen: false }))}>
                                      Cancel
                                  </button>
                                  <button type="button" className="game-btn game-btn--primary" style={{ flex: 1 }} onClick={handleConfirmIcmChop} disabled={!allIcmStacksValid}>
                                      Confirm ICM Chop
                                  </button>
                              </div>
                          </div>
                      </div>
                  </>
              )}
          </>
      );
  };

  const renderStructureModal = () => {
      if (!structureModal.isOpen) return null;

      const handleToggleMatchAnte = (checked) => {
          setMatchAnteToBB(checked);
          if (checked) {
              setEditLevels(prev => prev.map(l => ({
                  ...l,
                  ante: l.isBreak ? 0 : l.bb
              })));
          }
      };

      const clearAllAntes = () => {
          setMatchAnteToBB(false);
          setEditLevels(prev => prev.map(l => ({ ...l, ante: 0 })));
      };

      const minutesMatchFirstRow =
          editLevels.length <= 1 ||
          editLevels.every((level) => Number(level.duration) === Number(editLevels[0]?.duration));

      const matchAllMinutesToFirstRow = () => {
          if (editLevels.length === 0) return;
          const firstDuration = Number(editLevels[0]?.duration) || 0;
          setEditLevels(prev => prev.map(level => ({ ...level, duration: firstDuration })));
      };

      const gameTypesMatchFirstRow =
          editLevels.length <= 1 ||
          editLevels.every(level => (level.gameType || "") === (editLevels[0]?.gameType || ""));

      const matchAllGameTypesToFirstRow = () => {
          const firstGameType = editLevels[0]?.gameType || "";
          setEditLevels(prev => prev.map(level => ({ ...level, gameType: firstGameType })));
      };

      const alternateGameTypes = () => {
          if (editLevels.length === 0) return;
          const firstGameType = editLevels[0]?.gameType || "";
          setEditLevels(prev => prev.map((level, index) => ({
              ...level,
              gameType: index % 2 === 0 ? firstGameType : (GAME_TYPE_OPTIONS.find(gt => gt !== firstGameType) || "")
          })));
      };

      const addLevel = () => {
          let lastPlayableLevel = null;
          for (let i = editLevels.length - 1; i >= 0; i--) {
              if (!editLevels[i].isBreak) {
                  lastPlayableLevel = editLevels[i];
                  break;
              }
          }

          const roundBlind = (value) => {
              if (value > 2000) return Math.round(value / 500) * 500;
              if (value < 100) return Math.round(value / 25) * 25;
              if (value < 1000) return Math.round(value / 50) * 50;
              if (value < 5000) return Math.round(value / 100) * 100;
              return Math.round(value / 500) * 500;
          };

          const newSb = lastPlayableLevel ? roundBlind(lastPlayableLevel.sb * 1.5) : 50;
          const newBb = lastPlayableLevel ? roundBlind(lastPlayableLevel.bb * 1.5) : 100;
          const newAnte = matchAnteToBB ? newBb : (lastPlayableLevel ? lastPlayableLevel.ante : 0);
          const duration = lastPlayableLevel ? lastPlayableLevel.duration : 20;

          setEditLevels([...editLevels, { sb: newSb, bb: newBb, ante: newAnte, duration, isBreak: false, gameType: mixedGames ? (lastPlayableLevel?.gameType || "") : undefined }]);
      };

      const addBreak = () => {
          setEditLevels([...editLevels, { sb: 0, bb: 0, ante: 0, duration: 10, isBreak: true, gameType: mixedGames ? "" : undefined }]);
      };

      const removeLevel = (idx) => {
          setEditLevels(prev => prev.filter((_, i) => i !== idx));
      };

      const updateLevel = (idx, field, val) => {
          setEditLevels(prev => {
              const newLevels = [...prev];
              newLevels[idx] = { ...newLevels[idx], [field]: val };
              return newLevels;
          });
      };

      const getDropInsertIndex = (fromIdx, toIdx) => {
          if (fromIdx === null || toIdx === null || fromIdx === toIdx) return null;
          const insertIdx = fromIdx < toIdx ? toIdx - 1 : toIdx;
          return insertIdx === fromIdx ? null : insertIdx;
      };

      const moveLevelOnDrop = (toIdx) => {
          const fromIdx = draggingLevelIndexRef.current;
          const insertIdx = getDropInsertIndex(fromIdx, toIdx);
          if (insertIdx === null) return;
          if (!editLevels[fromIdx] || !editLevels[toIdx]) return;
          const newLevels = [...editLevels];
          const [level] = newLevels.splice(fromIdx, 1);
          newLevels.splice(insertIdx, 0, level);
          setEditLevels(newLevels);
      };

      const stopLevelDrag = () => {
          draggingLevelIndexRef.current = null;
          setDraggingLevelIndex(null);
          setDragOriginIndex(null);
          setDragOverLevelIndex(null);
      };

      const closeStructureModal = () => {
          stopLevelDrag();
          setStructureModal({ isOpen: false });
      };

      const handleSaveStructure = async () => {
          if (isSubmitting) return;
          setIsSubmitting(true);
          try {
              const savedLevels = mixedGames
                  ? editLevels
                  : editLevels.map(({ gameType, ...level }) => level);
              await axios.patch(`/${cardroom}/api/tournaments/${id}/structure`, { levels: savedLevels, mixedGames });
              closeStructureModal();
              await loadData();
          } catch (err) {
              console.error('Save structure error:', err);
          } finally {
              setIsSubmitting(false);
          }
      };

      const rebuyCutoff = tournament.rebuys_cutoff_level || config.rebuyCutoffLevel || 0;

      return (
          <>
              <div className="game-modal-overlay" onClick={closeStructureModal} />
              <div className="game-modal game-modal--large" style={{ width: 'min(1100px, 96vw)', maxWidth: '1100px', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Edit Structure</h3>
                      <button className="game-icon-btn" onClick={closeStructureModal}><FaTimes /></button>
                  </div>

                  <div className="game-modal__body game-modal__body--scroll" style={{ flex: 1, padding: '1.5rem' }}>
                      <div className="tournament-structure-actions" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
                          <label className="game-filter-chip" style={{ cursor: 'pointer', margin: 0 }}>
                              <input type="checkbox" checked={matchAnteToBB} onChange={(e) => handleToggleMatchAnte(e.target.checked)} />
                              <span>Match Ante to BB</span>
                          </label>
                          <label className="game-filter-chip" style={{ cursor: 'pointer', margin: 0 }}>
                              <input type="checkbox" checked={mixedGames} onChange={(e) => setMixedGames(e.target.checked)} />
                              <span>Mixed Games</span>
                          </label>
                          {editLevels.some(l => l.ante > 0) && (
                              <button type="button" className="game-btn game-btn--sm game-btn--secondary" onClick={clearAllAntes} style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}>
                                  Clear All Antes
                              </button>
                          )}
                          {!minutesMatchFirstRow && (
                              <button type="button" className="game-btn game-btn--sm game-btn--secondary" onClick={matchAllMinutesToFirstRow} style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}>
                                  Match All Mins
                              </button>
                          )}
                          {mixedGames && !gameTypesMatchFirstRow && (
                              <button type="button" className="game-btn game-btn--sm game-btn--secondary" onClick={matchAllGameTypesToFirstRow} style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}>
                                  Match All Game Types
                              </button>
                          )}
                          {mixedGames && (
                              <button type="button" className="game-btn game-btn--sm game-btn--secondary" onClick={alternateGameTypes} style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}>
                                  Alternate Game Types
                              </button>
                          )}
                      </div>

                      <div className="game-table-container tournament-structure-table-wrap">
                          <table className="game-table tournament-structure-table">
                              <thead>
                                  <tr>
                                      <th>Lvl</th>
                                      {mixedGames && <th>Game Type</th>}
                                      <th>SB</th>
                                      <th>BB</th>
                                      <th>Ante</th>
                                      <th>Mins</th>
                                      <th></th>
                                  </tr>
                              </thead>
                              <tbody
                                  onDragOver={e => {
                                      if (dragOriginIndex === null) return;
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                  }}
                                  onDrop={e => {
                                      if (dragOriginIndex === null) return;
                                      e.preventDefault();
                                      moveLevelOnDrop(dragOverLevelIndex);
                                      stopLevelDrag();
                                  }}
                              >
                                  {editLevels.map((l, i) => {
                                      const levelNum = editLevels.slice(0, i + 1).filter(level => !level.isBreak).length;
                                      const isCutoff = !l.isBreak && levelNum === rebuyCutoff;
                                      const isDragging = draggingLevelIndex === i;
                                      const isDragOrigin = dragOriginIndex === i;
                                      const dropInsertIndex = getDropInsertIndex(dragOriginIndex, dragOverLevelIndex);
                                      const draggedLevel = dropInsertIndex !== null ? editLevels[dragOriginIndex] : null;
                                      const showInsertPreview = draggedLevel && dragOverLevelIndex === i;
                                      const rowStyle = {
                                          background: isDragOrigin
                                              ? 'rgba(250, 204, 21, 0.14)'
                                              : l.isBreak
                                              ? 'rgba(255,255,255,0.05)'
                                              : (isCutoff ? 'rgba(239, 68, 68, 0.15)' : 'transparent'),
                                          borderLeft: isCutoff ? '3px solid var(--game-danger)' : 'none',
                                          cursor: isDragging ? 'grabbing' : 'grab',
                                          outline: isDragOrigin ? '1px dashed rgba(250, 204, 21, 0.85)' : 'none',
                                          boxShadow: isDragOrigin ? 'inset 0 0 0 1px rgba(250, 204, 21, 0.25)' : 'none',
                                          opacity: 1,
                                          transition: 'background 120ms ease, box-shadow 120ms ease, opacity 120ms ease'
                                      };
                                      const insertPreviewStyle = {
                                          background: 'rgba(250, 204, 21, 0.18)',
                                          outline: '1px solid rgba(250, 204, 21, 0.85)',
                                          boxShadow: '0 0 0 1px rgba(250, 204, 21, 0.25)',
                                          pointerEvents: 'none'
                                      };

                                      return (
                                          <Fragment key={i}>
                                              {showInsertPreview && (
                                                  <tr key={`insert-preview-${i}`} style={insertPreviewStyle}>
                                                      <td>
                                                          {draggedLevel.isBreak ? <FaCoffee title="Break" /> : editLevels.slice(0, dragOriginIndex + 1).filter(level => !level.isBreak).length}
                                                          <span style={{fontSize: '0.65em', display:'block', color: 'rgba(250, 204, 21, 0.95)', fontWeight: 700}}>INSERT</span>
                                                      </td>
                                                      {mixedGames && (
                                                          <td style={{textAlign: 'center', opacity: 0.85}}>{draggedLevel.gameType || 'None'}</td>
                                                      )}
                                                      {draggedLevel.isBreak ? (
                                                          <td colSpan="3" style={{textAlign: 'center', fontStyle: 'italic', opacity: 0.85}}>BREAK</td>
                                                      ) : (
                                                          <>
                                                              <td style={{textAlign: 'center'}}>{draggedLevel.sb}</td>
                                                              <td style={{textAlign: 'center'}}>{draggedLevel.bb}</td>
                                                              <td style={{textAlign: 'center'}}>{draggedLevel.ante}</td>
                                                          </>
                                                      )}
                                                      <td style={{textAlign: 'center'}}>{draggedLevel.duration}</td>
                                                      <td></td>
                                                  </tr>
                                              )}
                                          <tr
                                              key={i}
                                              style={rowStyle}
                                              draggable
                                              onDragStart={e => {
                                                  draggingLevelIndexRef.current = i;
                                                  setDraggingLevelIndex(i);
                                                  setDragOriginIndex(i);
                                                  setDragOverLevelIndex(null);
                                                  e.dataTransfer.effectAllowed = 'move';
                                                  e.dataTransfer.setData('text/plain', String(i));
                                              }}
                                              onDragEnter={() => setDragOverLevelIndex(i)}
                                              onDragOver={e => {
                                                  e.preventDefault();
                                                  e.dataTransfer.dropEffect = 'move';
                                              }}
                                              onDrop={e => {
                                                  e.preventDefault();
                                                  moveLevelOnDrop(dragOverLevelIndex ?? i);
                                                  stopLevelDrag();
                                              }}
                                              onDragEnd={stopLevelDrag}
                                          >
                                              <td>
                                                  {l.isBreak ? <FaCoffee title="Break" /> : levelNum}
                                                  {isDragOrigin && <span style={{fontSize: '0.65em', display:'block', color: 'rgba(250, 204, 21, 0.95)', fontWeight: 700}}>DRAGGING</span>}
                                                  {isCutoff && <span style={{fontSize: '0.7em', display:'block', color: 'var(--game-danger)'}}>CUTOFF</span>}
                                              </td>
                                              {mixedGames && (
                                                  <td>
                                                      <select
                                                          className="game-input"
                                                          value={l.gameType || ""}
                                                          onChange={e => updateLevel(i, 'gameType', e.target.value)}
                                                          style={{ width: '100%', minWidth: 0 }}
                                                      >
                                                          <option value=""></option>
                                                          {GAME_TYPE_OPTIONS.map(gameType => (
                                                              <option key={gameType} value={gameType}>{gameType}</option>
                                                          ))}
                                                      </select>
                                                  </td>
                                              )}
                                              {l.isBreak ? (
                                                  <td colSpan="3" style={{textAlign: 'center', fontStyle: 'italic', opacity: 0.7}}>BREAK</td>
                                              ) : (
                                                  <>
                                                      <td><input type="number" style={{width: '60px'}} className="game-input" value={l.sb} onFocus={handleNumberFocus} onChange={e => updateLevel(i, 'sb', Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} /></td>
                                                      <td><input type="number" style={{width: '60px'}} className="game-input" value={l.bb} onFocus={handleNumberFocus} onChange={e => updateLevel(i, 'bb', Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} /></td>
                                                      <td><input type="number" style={{width: '50px'}} className="game-input" value={l.ante} onFocus={handleNumberFocus} onChange={e => updateLevel(i, 'ante', Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()} /></td>
                                                  </>
                                              )}
                                              <td><input type="number" style={{width: '50px'}} className="game-input" value={l.duration} onFocus={handleNumberFocus} onChange={e => updateLevel(i, 'duration', Number(e.target.value))} onWheel={(e) => e.currentTarget.blur()}/></td>
                                              <td><button className="game-notification__close" onClick={() => removeLevel(i)}><FaTrash /></button></td>
                                          </tr>
                                          </Fragment>
                                      );
                                  })}
                              </tbody>
                          </table>
                          <div style={{display: 'flex', gap: '0.5rem', marginTop: '1rem'}}>
                              <button className="game-btn game-btn--secondary" onClick={addLevel} style={{flex: 1}}>+ Add Level</button>
                              <button className="game-btn game-btn--secondary" onClick={addBreak} style={{flex: 1}}>+ Add Break</button>
                          </div>
                      </div>
                  </div>

                  <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--game-border)', background: 'var(--game-bg-secondary)' }}>
                      <button className="game-btn game-btn--primary" style={{ width: '50%' }} onClick={handleSaveStructure} disabled={isSubmitting}>
                          {isSubmitting ? 'Saving...' : 'Save Structure'}
                      </button>
                  </div>
              </div>
          </>
      );
  };

  const renderSettingsModal = () => {
      if (!settingsModal.isOpen) return null;

      const handleSettingChange = (field, value) => {
          setSettingValues(prev => ({ ...prev, [field]: value === '' ? '' : Number(value) }));
      };

      const settingsCustomAllocations = parseCustomAllocations(settingValues.customAllocations);

      const handleCustomAllocationChange = (allocationId, action, value) => {
          setSettingValues(prev => {
              const allocations = parseCustomAllocations(prev.customAllocations).map(allocation => (
                  allocation.id === allocationId
                      ? {
                          ...allocation,
                          amounts: {
                              ...allocation.amounts,
                              [action]: value === '' ? '' : Number(value)
                          }
                      }
                      : allocation
              ));
              return { ...prev, customAllocations: allocations };
          });
      };

      const handleSaveSettings = async () => {
          if (isSubmitting) return;
          setIsSubmitting(true);
          try {
              // Ensure no empty strings sent to DB
              const s = Object.keys(settingValues).reduce((acc, key) => {
                  if (Array.isArray(settingValues[key]) || (settingValues[key] && typeof settingValues[key] === 'object')) {
                      acc[key] = settingValues[key];
                      return acc;
                  }
                  acc[key] = settingValues[key] === '' ? 0 : Number(settingValues[key]);
                  return acc;
              }, {});

              const payload = {
                  buyin: s.buyinCost,
                  rake: s.buyinRake,
                  tips: s.buyinTips,
                  starting_chips: s.buyinChips,
                  bonus_buyin: s.bonusCost,
                  bonus_rake: s.bonusRake,
                  bonus_tips: s.bonusTips,
                  bonus_starting_chips: s.bonusChips,
                  buyin_addon_cost: s.buyinAddonCost,
                  buyin_addon_rake: s.buyinAddonRake,
                  buyin_addon_tips: s.buyinAddonTips,
                  buyin_addon_chips: s.buyinAddonChips,
                  rebuys_cost: s.rebuysCost,
                  rebuys_rake: s.rebuysRake,
                  rebuys_tips: s.rebuysTips,
                  rebuys_chips: s.rebuysChips,
                  rebuys_cutoff_level: s.rebuys_cutoff_level,
                  addons_cost: s.addonsCost,
                  addons_rake: s.addonsRake,
                  addons_tips: s.addonsTips,
                  addons_chips: s.addonsChips,
                  rebuys_add_on_costs: s.rebuysAddOnCost,
                  rebuys_add_on_rake: s.rebuysAddOnRake,
                  rebuys_add_on_tips: s.rebuysAddOnTips,
                  rebuys_add_on_chips: s.rebuysAddOnChips,
                  bounty_enabled: s.bountyEnabled ? 1 : 0,
                  bounty_amount: s.bountyEnabled ? s.bountyAmount : 0,
                  custom_allocations_json: parseCustomAllocations(settingValues.customAllocations)
              };

              await axios.patch(`/${cardroom}/api/tournaments/${id}/settings`, payload);
              setSettingsModal({ isOpen: false });
              await loadData();
          } catch (err) {
              console.error('Save settings error:', err);
          } finally {
              setIsSubmitting(false);
          }
      };

      return (
          <>
              <div className="game-modal-overlay" onClick={() => setSettingsModal({ isOpen: false })} />
              <div className="game-modal game-modal--large" style={{ maxWidth: '750px', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Tournament Settings</h3>
                      <button className="game-icon-btn" onClick={() => setSettingsModal({ isOpen: false })}><FaTimes /></button>
                  </div>

                  <div className="game-modal__body game-modal__body--scroll" style={{ flex: 1, padding: '1.5rem' }}>
                      <p style={{ fontSize: '0.85rem', color: 'var(--game-text-secondary)', marginBottom: '1.5rem' }}>
                          These settings control the costs, rake, tips, and chips distributed for each type of transaction.
                      </p>

                      <div style={{ marginBottom: '1.5rem', padding: '1rem', borderRadius: '8px', border: `1px solid ${settingValues.bountyEnabled ? 'rgba(244,63,94,0.35)' : 'rgba(255,255,255,0.08)'}`, background: settingValues.bountyEnabled ? 'rgba(244,63,94,0.08)' : 'rgba(255,255,255,0.03)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                              <div style={{ minWidth: 0, flex: '1 1 280px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                      <div style={{ color: settingValues.bountyEnabled ? '#fb7185' : 'var(--game-text-secondary)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bounty Tournament</div>
                                      <button
                                          type="button"
                                          onClick={() => setSettingValues(prev => ({
                                              ...prev,
                                              bountyEnabled: !prev.bountyEnabled,
                                              bountyAmount: prev.bountyEnabled ? 0 : (Number(prev.bountyAmount) || Number(tournament?.bounty_amount) || 0)
                                          }))}
                                          aria-pressed={Boolean(settingValues.bountyEnabled)}
                                          style={{
                                              width: '54px',
                                              height: '28px',
                                              borderRadius: '999px',
                                              border: `1px solid ${settingValues.bountyEnabled ? 'rgba(251,113,133,0.65)' : 'var(--game-border)'}`,
                                              background: settingValues.bountyEnabled ? 'rgba(251,113,133,0.28)' : 'rgba(255,255,255,0.08)',
                                              position: 'relative',
                                              cursor: 'pointer',
                                              flex: '0 0 auto'
                                          }}
                                          title={settingValues.bountyEnabled ? 'Turn bounty off' : 'Turn bounty on'}
                                      >
                                          <span
                                              style={{
                                                  position: 'absolute',
                                                  top: '3px',
                                                  left: settingValues.bountyEnabled ? '28px' : '4px',
                                                  width: '20px',
                                                  height: '20px',
                                                  borderRadius: '50%',
                                                  background: settingValues.bountyEnabled ? '#fb7185' : '#9ca3af',
                                                  transition: 'left 0.15s ease'
                                              }}
                                          />
                                      </button>
                                      <span style={{ color: settingValues.bountyEnabled ? '#fb7185' : 'var(--game-text-secondary)', fontSize: '0.85rem', fontWeight: 700 }}>
                                          {settingValues.bountyEnabled ? 'On' : 'Off'}
                                      </span>
                                  </div>
                                  <div style={{ color: 'var(--game-text-secondary)', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                                      {settingValues.bountyEnabled
                                          ? 'This amount is withheld from each buy-in and rebuy and does not go to the prize pool.'
                                          : 'Turn this on to withhold a bounty amount from each buy-in and rebuy.'}
                                  </div>
                              </div>
                              {Boolean(settingValues.bountyEnabled) && (
                                  <div style={{ width: '160px', maxWidth: '100%', minWidth: 0, flex: '0 1 160px' }}>
                                      <label className="game-label" style={{ fontSize: '0.75rem' }}>Bounty Amount ($)</label>
                                      <input
                                          type="number"
                                          className="game-input"
                                          style={tournamentSettingInputStyle}
                                          min="0"
                                          value={settingValues.bountyAmount ?? 0}
                                          onFocus={handleNumberFocus}
                                          onWheel={preventNumberInputWheel}
                                          onChange={e => handleSettingChange('bountyAmount', e.target.value)}
                                      />
                                  </div>
                              )}
                          </div>
                      </div>

                      <SettingRow title="Initial Buy-In" prefix="buyin" action="buyin" settingValues={settingValues} handleSettingChange={handleSettingChange} customAllocations={settingsCustomAllocations} handleCustomAllocationChange={handleCustomAllocationChange} bountyAmount={settingValues.bountyEnabled ? settingValues.bountyAmount : 0} />
                      <SettingRow title="Buy-in Bonus" prefix="bonus" action="bonus" settingValues={settingValues} handleSettingChange={handleSettingChange} customAllocations={settingsCustomAllocations} handleCustomAllocationChange={handleCustomAllocationChange} />
                      <SettingRow title="Buy-in Add-on" prefix="buyinAddon" action="buyin_addon" settingValues={settingValues} handleSettingChange={handleSettingChange} customAllocations={settingsCustomAllocations} handleCustomAllocationChange={handleCustomAllocationChange} />
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--game-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rebuys</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '250px' }}>
                              <label className="game-label" style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>Cutoff Level:</label>
                              <input type="number" className="game-input" style={{ width: '80px', padding: '0.3rem', boxSizing: 'border-box' }} value={settingValues.rebuys_cutoff_level ?? ''} onFocus={handleNumberFocus} onWheel={preventNumberInputWheel} onChange={e => handleSettingChange('rebuys_cutoff_level', e.target.value)} />
                          </div>
                      </div>
                      <SettingRow title="" prefix="rebuys" action="rebuy" settingValues={settingValues} handleSettingChange={handleSettingChange} customAllocations={settingsCustomAllocations} handleCustomAllocationChange={handleCustomAllocationChange} bountyAmount={settingValues.bountyEnabled ? settingValues.bountyAmount : 0} /> {/* Title hidden, using header above instead */}
                      
                      <SettingRow title="Rebuy Add-on" prefix="rebuysAddOn" action="rebuy_addon" settingValues={settingValues} handleSettingChange={handleSettingChange} customAllocations={settingsCustomAllocations} handleCustomAllocationChange={handleCustomAllocationChange} />
                      <SettingRow title="Normal Add-on" prefix="addons" action="addon" settingValues={settingValues} handleSettingChange={handleSettingChange} customAllocations={settingsCustomAllocations} handleCustomAllocationChange={handleCustomAllocationChange} />
                  </div>

                  <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--game-border)', background: 'var(--game-bg-secondary)' }}>
                      <button className="game-btn game-btn--primary" style={{ width: '50%' }} onClick={handleSaveSettings} disabled={isSubmitting}>
                          {isSubmitting ? 'Saving...' : 'Save Settings'}
                      </button>
                  </div>
              </div>
          </>
      );
  };

  const renderDashboard = () => {
      // Helper: pill-style text toggle


      // Stat card â€” entire card clickable, border = green (on) or red (off)
      const StatCard = ({ settingKey, label, value }) => {
          const on = displaySettings[settingKey] !== false;
          return (
              <div
                  onClick={() => handleToggleDisplay(settingKey)}
                  style={{
                      background: 'var(--game-bg-tertiary)',
                      borderRadius: '8px',
                      padding: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      border: `2px solid ${on ? '#48bb78' : '#f56565'}`,
                      transition: 'border-color 0.2s ease'
                  }}
                  title={on ? 'Click to hide on screen' : 'Click to show on screen'}
              >
                  <div style={{ fontSize: '0.8rem', color: on ? '#48bb78' : '#f56565', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
              </div>
          );
      };

      const walletTotals = tournament.wallet_totals || [];
      const walletTransactions = (() => {
          if (!walletHistoryModal.walletId) return [];
          const txns = (tournament.transactions || [])
              .filter(t => t.tournament_wallet_id === walletHistoryModal.walletId)
              .map(t => ({
                  ...t,
                  _type: 'transaction',
                  _sort: new Date(t.timestamp).getTime(),
                  amount: t.transaction_type === 'cash_refill' ? -(Number(t.amount) || 0) : t.amount
              }));
          const costTxns = costs
              .filter(c => c.wallet_id === walletHistoryModal.walletId)
              .map(c => ({ ...c, _type: 'cost', _sort: new Date(c.completed_at).getTime(), transaction_type: 'cost', timestamp: c.completed_at, amount: -c.amount }));
          return [...txns, ...costTxns].sort((a, b) => b._sort - a._sort);
      })();

      const avgStack = tournament.players_left > 0 
          ? Math.round((tournament.total_chips || 0) / tournament.players_left) 
          : 0;

      return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

              {/* â”€â”€ SECTION 1: DASHBOARD TOP ROW â”€â”€ */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1.5rem', alignItems: 'stretch' }}>
                  
                  {/* CLOCK CARD */}
                  <div className="game-card" style={{ padding: '1.5rem 2rem', background: 'var(--game-bg-secondary)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '1.5rem' }}>

                          {/* LEFT â€” Rebuy cutoff */}
                          <div style={{ textAlign: 'left', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '1rem' }}>
                              <div>
                                  <div style={{ fontSize: '0.75rem', color: 'var(--game-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.35rem' }}>Rebuy Cutoff</div>
                                  <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                                      {tournament.rebuys_cutoff_level ? `Level ${tournament.rebuys_cutoff_level}` : 'No Cutoff'}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: tournament.current_level >= (tournament.rebuys_cutoff_level || Infinity) ? '#f56565' : 'var(--game-text-secondary)', marginTop: '0.2rem' }}>
                                      {tournament.rebuys_cutoff_level && tournament.current_level >= tournament.rebuys_cutoff_level ? 'Rebuys Closed' : 'Rebuys Open'}
                                  </div>
                              </div>
                              <button
                                  className="game-btn game-btn--secondary"
                                  onClick={() => handleManualLevelChange(0)}
                                  title="Reset Level"
                                  style={{ width: 'fit-content' }}
                              >
                                  <FaHistory /> Reset Level
                              </button>
                          </div>

                          {/* CENTRE â€” Clock & controls */}
                          <div style={{ textAlign: 'center' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', alignItems: 'center', gap: '1rem' }}>
                                  <button
                                      className="game-icon-btn game-icon-btn--secondary"
                                      onClick={() => handleManualLevelChange(-1)}
                                      title="Previous Level"
                                      disabled={currentLevelNumber < 1}
                                      style={{ width: '42px', height: '42px' }}
                                  >
                                      <FaBackward />
                                  </button>
                                  <div style={{ fontSize: '3.5rem', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.05em', color: timeLeft < 60 && tournament.state === 'running' ? '#f56565' : 'inherit', lineHeight: 1 }}>
                                      {formatTime(timeLeft)}
                                  </div>
                                  <button
                                      className="game-icon-btn game-icon-btn--secondary"
                                      onClick={() => handleManualLevelChange(1)}
                                      title="Next Level"
                                      style={{ width: '42px', height: '42px' }}
                                  >
                                      <FaForward />
                                  </button>
                              </div>
                              <div style={{ fontSize: '1.1rem', color: 'var(--game-text-secondary)', margin: '0.5rem 0 1rem' }}>
                                  {currentLevelLabel}
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                                  {tournament.state === 'running' ? (
                                      <button className="game-btn game-btn--danger" onClick={() => handleControl('pause')}>
                                          <FaPause /> Pause
                                      </button>
                                  ) : (
                                      <button className="game-btn game-btn--success" onClick={() => handleControl('start')}>
                                          <FaPlay /> {isCountdownLevel ? 'Start Countdown' : 'Start'}
                                      </button>
                                  )}
                              </div>
                          </div>

                          {/* RIGHT â€” Next blinds */}
                          <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '0.75rem', color: 'var(--game-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.35rem' }}>Next Level</div>
                              {nextLevelData ? (
                                  nextLevelData.isBreak ? (
                                      <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>BREAK</div>
                                  ) : (
                                      <>
                                          <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{nextLevelData.sb} / {nextLevelData.bb}</div>
                                          {nextLevelData.ante > 0 && <div style={{ fontSize: '0.85rem', color: 'var(--game-text-secondary)' }}>Ante: {nextLevelData.ante}</div>}
                                      </>
                                  )
                              ) : (
                                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--game-text-secondary)' }}>Final Level</div>
                              )}
                          </div>

                      </div>
                  </div>

                  {/* CONFIG BUTTONS CARD */}
                  <div className="game-card" style={{ padding: '1.5rem', background: 'var(--game-bg-secondary)', display: 'flex', flexDirection: 'column', gap: '0.75rem', justifyContent: 'center', width: '220px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--game-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem', textAlign: 'center' }}>
                          Configuration
                      </div>
                      <button
                          className="game-btn game-btn--secondary"
                          style={{ width: '100%', justifyContent: 'center', fontSize: '0.85rem', padding: '0.5rem' }}
                          onClick={() => {
                              setEditablePayouts(payouts.map(p => ({ ...p })));
                              setPayoutEditModal({ isOpen: true });
                          }}
                      >
                          Edit Payouts
                      </button>
                      <button
                          className="game-btn game-btn--secondary"
                          style={{ width: '100%', justifyContent: 'center', fontSize: '0.85rem', padding: '0.5rem' }}
                          onClick={() => {
                              const config = typeof tournament.config_json === 'string' ? JSON.parse(tournament.config_json) : (tournament.config_json || {});
                              const levels = config.levels || [];
                              setEditLevels(JSON.parse(JSON.stringify(levels))); // Deep copy
                              setMixedGames(Boolean(config.mixedGames || levels.some(level => level.gameType)));
                              draggingLevelIndexRef.current = null;
                              setDraggingLevelIndex(null);
                              setDragOriginIndex(null);
                              setDragOverLevelIndex(null);
                              setStructureModal({ isOpen: true });
                          }}
                      >
                          Edit Structure
                      </button>
                      <button
                          className="game-btn game-btn--secondary"
                          style={{ width: '100%', justifyContent: 'center', fontSize: '0.85rem', padding: '0.5rem' }}
                          onClick={() => {
                              const config = typeof tournament.config_json === 'string' ? JSON.parse(tournament.config_json) : (tournament.config_json || {});
                              setSettingValues({
                                  buyinCost: tournament.buyin || 0,
                                  buyinRake: tournament.rake || 0,
                                  buyinTips: tournament.tips || 0,
                                  buyinChips: config.startingChips || tournament.starting_chips || 0,

                                  bonusCost: tournament.bonus_buyin || 0,
                                  bonusRake: tournament.bonus_rake || 0,
                                  bonusTips: tournament.bonus_tips || 0,
                                  bonusChips: config.bonusChips || tournament.bonus_starting_chips || 0,
                                  buyinAddonCost: config.buyinAddonCost || tournament.buyin_addon_cost || 0,
                                  buyinAddonRake: config.buyinAddonRake || tournament.buyin_addon_rake || 0,
                                  buyinAddonTips: config.buyinAddonTips || tournament.buyin_addon_tips || 0,
                                  buyinAddonChips: config.buyinAddonChips || tournament.buyin_addon_chips || 0,

                                  rebuysCost: config.rebuysCost || tournament.rebuys_cost || 0,
                                  rebuysRake: tournament.rebuys_rake || 0,
                                  rebuysTips: tournament.rebuys_tips || 0,
                                  rebuysChips: config.rebuysChips || tournament.rebuys_chips || 0,
                                  rebuys_cutoff_level: config.rebuyCutoffLevel || tournament.rebuys_cutoff_level || 0,

                                  addonsCost: tournament.addons_cost || 0,
                                  addonsRake: tournament.addons_rake || 0,
                                  addonsTips: tournament.addons_tips || 0,
                                  addonsChips: config.addonChips || tournament.addons_chips || 0,

                                  rebuysAddOnCost: tournament.rebuys_add_on_costs || 0,
                                  rebuysAddOnRake: tournament.rebuys_add_on_rake || 0,
                                  rebuysAddOnTips: tournament.rebuys_add_on_tips || 0,
                                  rebuysAddOnChips: config.rebuysAddOnChips || tournament.rebuys_add_on_chips || 0,
                                  bountyEnabled: Number(tournament.bounty_enabled || 0) > 0,
                                  bountyAmount: tournament.bounty_amount || 0,
                                  customAllocations: parseCustomAllocations(tournament.custom_allocations_json)
                              });
                              setSettingsModal({ isOpen: true });
                          }}
                      >
                          Tournament Settings
                      </button>
                  </div>
              </div>

              {/* â”€â”€ SECTION 2: DISPLAY SETTINGS â”€â”€ */}
              <div className="game-card" style={{ padding: '1.25rem', background: 'var(--game-bg-secondary)' }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--game-text-secondary)' }}>
                      Screen Display Settings toggle to show/hide on display screen
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
                      <StatCard settingKey="playersLeft" label="Players Left" value={tournament.players_left || 0} />
                      <StatCard settingKey="avgStack" label="Avg Stack" value={(avgStack).toLocaleString()} />
                      <StatCard settingKey="entries" label="Total Entries" value={tournament.total_entries || 0} />
                      <StatCard settingKey="buyinsNum" label="Buy-Ins" value={tournament.buyins_num || 0} />
                      <StatCard settingKey="rebuysNum" label="Rebuys" value={tournament.rebuys_num || 0} />
                      <StatCard settingKey="addonsNum" label="Add-Ons" value={tournament.addons_num || 0} />
                      <StatCard settingKey="totalChips" label="Total Chips" value={(tournament.total_chips || 0).toLocaleString()} />
                      <StatCard settingKey="totalPot" label="Prize Pool" value={`$${(tournament.prizepool || 0).toLocaleString()}`} />
                      <StatCard settingKey="placesPaid" label="Places Paid" value={placesPaid} />
                      <StatCard settingKey="prizes" label="Prizes" value={displaySettings.prizes !== false ? 'On' : 'Off'} />
                  </div>
              </div>

              {/* â”€â”€ SECTION 3: FINANCIALS â”€â”€ */}
              <div className="game-card" style={{ padding: '1.25rem', background: 'var(--game-bg-secondary)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ margin: 0, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--game-text-secondary)' }}>Financials</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                      <div style={{ background: 'var(--game-bg-tertiary)', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.8rem', color: 'var(--game-text-secondary)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Rake</div>
                          <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>${(tournament.total_rake || 0).toLocaleString()}</div>
                      </div>
                      <div style={{ background: 'var(--game-bg-tertiary)', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.8rem', color: 'var(--game-text-secondary)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Tips</div>
                          <div style={{ fontSize: '1.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                              <span>${(tournament.total_tips || 0).toLocaleString()}</span>
                              <button
                                  type="button"
                                  className="game-icon-btn game-icon-btn--success"
                                  onClick={openWinnerTipModal}
                                  title="Record winner tip"
                                  style={{ flexShrink: 0 }}
                              >
                                  <FaDollarSign />
                              </button>
                          </div>
                      </div>
<div style={{ background: 'var(--game-bg-tertiary)', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
    <div style={{ fontSize: '0.8rem', color: 'var(--game-text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>
        Cash Bag
    </div>
    <div style={{ 
        fontSize: '1.8rem', 
        fontWeight: 700, 
        color: (tournament.cash_total || 0) < 0 ? 'var(--game-danger)' : 'var(--game-success)' 
    }}>
        ${(tournament.cash_total || 0).toLocaleString()} 
        <button
            style={{ marginLeft: '0.5rem' }}
            className="game-icon-btn game-icon-btn--success"
            onClick={openCashRefillModal}
            title="Refill Cash Bag"
            disabled={tournamentWallets.length === 0}
        >
            <FaDollarSign />
        </button>
        <button 
            style={{ marginLeft: '0.5rem' }}
            className="game-icon-btn game-icon-btn--info" 
            onClick={() => setIsCashBagHistoryOpen(true)} 
            title="Cash Bag History"
        >
            <FaInfo />
        </button>
    </div>
</div>
                  </div>
              </div>

              {/* â”€â”€ SECTION 3b: OUTSTANDING CREDIT â”€â”€ */}
              {(() => {
                const customFees = parseCustomAllocations(tournament.custom_allocations_json);
                if (customFees.length === 0) return null;
                return (
                  <div className="game-card" style={{ padding: '1.25rem', background: 'var(--game-bg-secondary)' }}>
                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--game-text-secondary)' }}>
                      Custom Fees
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                      {customFees.map(allocation => (
                        <div key={allocation.id || allocation.name} style={{ background: 'var(--game-bg-tertiary)', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.8rem', color: 'var(--game-text-secondary)', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                            {allocation.name}
                          </div>
                          <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>${(Number(allocation.total) || 0).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const creditPlayers = (tournament.players || []).filter(p => (p.tournament_credit || 0) !== 0);
                if (creditPlayers.length === 0) return null;
                const totalCredit = creditPlayers.reduce((sum, p) => sum + (p.tournament_credit || 0), 0);
                return (
                  <div className="game-card" style={{ padding: '1.25rem', background: 'var(--game-bg-secondary)', border: '1px solid #d69e2e' }}>
                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#d69e2e' }}>
                      ${Math.abs(totalCredit)} Balances ({creditPlayers.length} player{creditPlayers.length !== 1 ? 's' : ''})
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      {creditPlayers.map(p => (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--game-bg-tertiary)', borderRadius: '8px', padding: '0.6rem 0.75rem' }}>
                          <span style={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
<span className={`game-player-badge ${p.tournament_credit > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{ fontSize: '0.78rem', flexShrink: 0 }}>
  <b>${Math.abs(p.tournament_credit)}</b>&nbsp;{p.tournament_credit > 0 ? 'Credit' : 'Balance'}
</span>
                                                             <button
                                     className="game-icon-btn game-icon-btn--info"
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
                                     }}
                                     title="Player History"
                                   >
                                     <FaInfo />
                                   </button>
                          <button
                            className="game-icon-btn game-icon-btn--success"
                            title={p.tournament_credit > 0 ? "Collect Credit Payment" : "Cash Out Balance"}
                            style={{ flexShrink: 0 }}
                            onClick={() => setCreditPaybackModal({
                              isOpen: true,
                              playerId: p.player_id,
                              playerName: p.name,
                              maxCredit: Math.abs(p.tournament_credit),
                              currentCredit: p.tournament_credit,
                              amount: Math.abs(p.tournament_credit),
                              method: 'cash',
                              walletId: ''
                            })}
                          >
                            <FaDollarSign />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/*SECTION 4: WALLETS + COSTS */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

                  {/* WALLETS */}
                  <div className="game-card" style={{ padding: '1.25rem', background: 'var(--game-bg-secondary)' }}>
                      <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--game-text-secondary)' }}>Wallets (This Tournament)</h3>
                      {walletTotals.length === 0 ? (
                          <p className="game-empty">No wallet transactions yet.</p>
                      ) : (
                          <ul className="game-list">
                              {walletTotals.map(w => {
                                  const bal = parseFloat(w.balance);
                                  return (
                                      <li key={w.wallet_id} className="game-list-item">
                                          <span className="game-list-name" onClick={() => setWalletHistoryModal({ isOpen: true, walletId: w.wallet_id, walletName: w.wallet_name })} style={{ cursor: 'pointer' }}>
                                              {w.wallet_name}
                                          </span>
                                          {bal !== 0 && (
<span className={`game-player-badge ${bal > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{ fontSize: '1rem' }}>
  {bal > 0 ? `Owes $${Math.abs(bal).toLocaleString()}` : `Owed $${Math.abs(bal).toLocaleString()}`}
</span>
                                          )}
                                          <div className="game-player-actions" style={{ marginLeft: 'auto' }}>
                                              <button className="game-icon-btn game-icon-btn--info" onClick={() => setWalletHistoryModal({ isOpen: true, walletId: w.wallet_id, walletName: w.wallet_name })} title="View tournament transactions">
                                                  <FaInfo />
                                              </button>
                                          </div>
                                      </li>
                                  );
                              })}
                          </ul>
                      )}
                  </div>

                  {/* COSTS */}
                  <div className="game-card" style={{ padding: '1.25rem', background: 'var(--game-bg-secondary)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                          <h3 style={{ margin: 0, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--game-text-secondary)', paddingBottom: 0, border: 'none' }}>Costs</h3>
                          <button className="game-icon-btn game-icon-btn--info" onClick={() => setShowCostsModal(true)} title="View Costs"><FaInfo /></button>
                      </div>

                      <form className="game-form-vertical" onSubmit={async (e) => {
                          e.preventDefault();
                          if (isSubmitting) return;
                          setIsSubmitting(true);
                          try {
                              await axios.post(`/${cardroom}/api/tournaments/${id}/costs`, {
                                  amount: costAmount,
                                  notes: costDescription,
                                  walletId: costPayment === 'cash' ? null : costPayment,
                                  sessionId: currSession?.id || null
                              });
                              setCostAmount('');
                              setCostDescription('');
                              setCostPayment('cash');
                              await loadData();
                          } catch (err) {
                              console.error('Add cost error', err);
                          } finally {
                              setIsSubmitting(false);
                          }
                          }}>
                          <input type="number" className="game-input" step="1" min="0" placeholder="Amount" value={costAmount} onFocus={handleNumberFocus} onChange={e => setCostAmount(e.target.value)} required />
                          <input type="text" className="game-input" placeholder="Notes" value={costDescription} onChange={e => setCostDescription(e.target.value)} required />
                          <select className="game-input" value={costPayment} onChange={e => setCostPayment(e.target.value)} required>
                              <option value="cash">Cash</option>
                              {tournamentWallets.map(w => (
                                  <option key={w.id} value={w.id}>{w.name}</option>
                              ))}
                              {tournamentWallets.length === 0 && (
                                  <option disabled>No tournament wallets</option>
                              )}
                          </select>
                          <button type="submit" className="game-btn game-btn--primary" disabled={isSubmitting || !costAmount || costAmount <= 0 || !costDescription.trim() || !costPayment}>
                              Add Cost
                          </button>
                      </form>
                  </div>

              </div>

              {/* â”€â”€ WALLET HISTORY MODAL â”€â”€ */}
              {walletHistoryModal.isOpen && (
                  <>
                      <div className="game-modal-overlay" onClick={() => setWalletHistoryModal({ isOpen: false, walletId: null, walletName: '' })} />
                      <div className="game-modal game-modal--large">
                          <div className="game-modal__header">
                              <h3 className="game-modal__title">{walletHistoryModal.walletName} History</h3>
                              <button className="game-icon-btn" onClick={() => setWalletHistoryModal({ isOpen: false, walletId: null, walletName: '' })}><FaTimes /></button>
                          </div>
                          <div className="game-modal__body game-modal__body--scroll">
                              {walletTransactions.length === 0 ? (
                                  <p className="game-empty">No transactions found</p>
                              ) : (
                                  <div className="game-transaction-list">
                                      {walletTransactions.map(t => {
                                          const amt = parseFloat(t.amount || 0).toFixed(0);
                                          const isCost = t._type === 'cost';
                                          return (
                                              <div key={`${t._type}-${t.id}`} className="game-transaction-item" style={isCost ? { background: 'rgba(245,101,101,0.08)', border: '1px solid rgba(245,101,101,0.25)' } : {}}>
                                                  <span className="game-transaction-time">{new Date(t.timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
                                                  <span className="game-transaction-amount">${Math.abs(amt)}</span>
                                                  <strong className="game-transaction-player">{isCost ? t.notes : t.player_name}</strong>
                                                  <span className="game-transaction-type">{t.transaction_type.toUpperCase()}</span>
                                                  <span className="game-transaction-date" style={{ marginLeft: 'auto' }}>{new Date(t.timestamp).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}</span>
                                              </div>
                                          );
                                      })}
                                  </div>
                              )}
                          </div>
                      </div>
                  </>
              )}

              {/* â”€â”€ COSTS HISTORY MODAL â”€â”€ */}
              {showCostsModal && (
                  <>
                      <div className="game-modal-overlay" onClick={() => setShowCostsModal(false)} />
                      <div className="game-modal game-modal--large">
                          <div className="game-modal__header">
                              <h3 className="game-modal__title">Tournament Costs</h3>
                              <button className="game-icon-btn" onClick={() => setShowCostsModal(false)}><FaTimes /></button>
                          </div>
                          <div className="game-modal__body game-modal__body--scroll">
                              {costs.length === 0 ? (
                                  <p className="game-empty">No costs recorded yet</p>
                              ) : (
                                  <div className="game-transaction-list">
                                      {costs.map(c => (
                                          <div key={c.id} className="game-transaction-item">
                                              <span className="game-transaction-time">{new Date(c.completed_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>
                                              <span className="game-transaction-amount">${parseFloat(c.amount).toFixed(0)}</span>
                                              <span className="game-transaction-type">{c.notes}</span>
                                              <span className="game-transaction-method">{c.wallet_name ? `Wallet: ${c.wallet_name}` : 'Cash'}</span>
                                              <span className="game-transaction-date">{new Date(c.completed_at).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}</span>
                                              <button
                                                  className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                                                  onClick={async () => {
                                                      try {
                                                          await axios.delete(`/${cardroom}/api/tournaments/${id}/costs/${c.id}`);
                                                          await loadData();
                                                      } catch (err) { console.error(err); }
                                                  }}
                                                  title="Delete cost"
                                              >
                                                  <FaRegTrashAlt />
                                              </button>
                                          </div>
                                      ))}
                                  </div>
                              )}
                          </div>
                      </div>
                  </>
              )}

          {/* â”€â”€ PAYOUT EDIT MODAL â”€â”€ */}
          {renderPayoutEditModalV2()}

          </div>
      );
  };



  const submitElimination = async (e) => {
      e.preventDefault();
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
          if (eliminationModal.removeOnly) {
              await axios.delete(`/${cardroom}/api/tournaments/${id}/players/${eliminationModal.playerId}`);
          } else
          if (eliminationModal.isCashout) {
              await axios.post(`/${cardroom}/api/tournaments/${id}/transactions`, {
                  playerId: eliminationModal.playerId,
                  type: 'cashout',
                  amount: eliminationModal.payoutAmount,
                  isBonus: false,
                  method: eliminationModal.method || 'cash',
                  walletId: eliminationModal.walletId || null,
                  eliminatedById: eliminationModal.eliminatedById ? Number(eliminationModal.eliminatedById) : null
              });
          } else {
              await axios.post(`/${cardroom}/api/tournaments/${id}/eliminate`, { playerId: eliminationModal.playerId });
          }
          setEliminationModal({ ...eliminationModal, isOpen: false });
          loadData();
      } catch (err) {
          console.error(err);
          alert(err.response?.data?.error || "Elimination Failed");
      } finally {
          setIsSubmitting(false);
      }
  };




  const renderPlayerHistoryModal = () => {
      if (!playerHistoryModal.isOpen) return null;

      const playerTxns = (tournament?.transactions || []).filter(t => t.player_id === playerHistoryModal.playerId);

      return (
          <>
              <div className="game-modal-overlay" onClick={() => setPlayerHistoryModal({ isOpen: false, playerId: null, playerName: '' })} />
              <div className="game-modal game-modal--large">
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">{playerHistoryModal.playerName} History</h3>
                      <button className="game-icon-btn" onClick={() => setPlayerHistoryModal({ isOpen: false, playerId: null, playerName: '' })}><FaTimes /></button>
                  </div>
                  <div className="game-modal__body game-modal__body--scroll">
                      <TournamentActivityList
                          transactions={playerTxns}
                          emptyText="No transactions found"
                          onUndo={openUndoTournamentTransactionModal}
                          isSubmitting={isSubmitting}
                      />
                  </div>
              </div>
          </>
      );
  };

  const renderTransactions = () => (
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
          {/* LEFT: Process Trans & History */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
              <div className="game-card" style={{padding: '1.5rem', background: 'var(--game-bg-secondary)'}}>
                  <h3 className="game-section__title"><FaMoneyBillWave /> Process Transaction</h3>
                  <div style={{display: 'flex', gap: '1rem', flexDirection: 'column'}}>
                      <button 
                          className="game-btn game-btn--primary" 
                          style={{padding: '1.5rem', fontSize: '1.2rem', justifyContent: 'center'}}
onClick={() => {
    openTransactionModal({ type: 'buyin' });
}}
                      >
                          <FaPlus /> Process Buy-In (${tournament.buyin || 0})
                      </button>
                      <button 
                          className="game-btn game-btn--success" 
                          style={{padding: '1.5rem', fontSize: '1.2rem', justifyContent: 'center'}}
onClick={() => {
    openTransactionModal({ type: 'rebuy' });
}}
                          disabled={(activePlayers.length + eliminatedPlayers.length) === 0}
                          title={(activePlayers.length + eliminatedPlayers.length) === 0 ? "No eligible players for a rebuy" : ""}
                      >
                          <FaDollarSign /> Process Rebuy (${tournament.rebuys_cost || 0})
                      </button>
                  </div>
              </div>
              
              <div>
                  <h2 className="game-section__title">Recent Activity</h2>
                  <TournamentActivityList
                      transactions={tournament.transactions || []}
                      emptyText="No transactions yet."
                      onUndo={openUndoTournamentTransactionModal}
                      isSubmitting={isSubmitting}
                  />
              </div>
          </div>

          {/* RIGHT: Active Players / Eliminated */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
               <div className="game-card" style={{padding: '1.5rem', background: 'var(--game-bg-secondary)'}}>
                   <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                       <h3 className="game-section__title" style={{color: 'var(--game-success)', margin: 0}}>
                           <FaUsers /> Active Players ({activePlayers.length})
                       </h3>
                   </div>
                   
                   <div style={{marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#1a202c', padding: '0.5rem 1rem', borderRadius: '8px'}}>
                       <FaSearch style={{color: '#a0aec0'}} />
                       <input 
                           type="text" 
                           placeholder="Search active players..." 
                           value={playerSearch}
                           onChange={(e) => setPlayerSearch(e.target.value)}
                           style={{background: 'none', border: 'none', color: '#fff', width: '100%', outline: 'none', fontSize: '1rem'}}
                       />
                   </div>

                   <div className="game-list">
                       {filteredActivePlayers.map(p => {
                           const potentialRank = activePlayers.length;
                           const payout = payouts.find(pay => pay.place === potentialRank);
                           
                           return (
                               <div key={p.id} className="game-list-item">

<div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
  <span
    style={{ fontWeight: 'bold', fontSize: '1.1rem' }}
    className="game-player-name"
    onClick={(e) => {
      e.stopPropagation();
      setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
    }}
  >
    {p.name}
  </span>
  {(p.tournament_credit || 0) !== 0 && (
<span className={`game-player-badge ${p.tournament_credit > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{ fontSize: '0.75rem', flexShrink: 0 }}>
  <b>${Math.abs(p.tournament_credit)}</b>&nbsp;{p.tournament_credit > 0 ? 'Credit' : 'Balance'}
</span>
  )}
</div>

                                  <button onClick={() => openTransactionModal({ type: 'rebuy', playerId: p.player_id }, { label: p.name, value: p.player_id })} 
                                   className="game-icon-btn game-icon-btn--success"
                                   title="Rebuy">
                                       <FaDollarSign />
                                   </button>
                                   <button
                                     className="game-icon-btn game-icon-btn--info"
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
                                     }}
                                     title="Player History"
                                   >
                                     <FaInfo />
                                   </button>
                                   {payout ? (
                                        <button className="game-icon-btn game-icon-btn--warning" onClick={() => handleEliminate(p.player_id)}>
                                           ${payout.amount}
                                       </button>
                                   ) : (
                                       <button className="game-icon-btn game-icon-btn--danger" onClick={() => handleEliminate(p.player_id)}>
                                           <FaTimes />
                                       </button>
                                   )}
                               </div>
                           );
                       })}
                       {filteredActivePlayers.length === 0 && <p>No active players found.</p>}
                   </div>
               </div>

              

              {/* REGISTERED PLAYERS */}
              <div className="game-card" style={{padding: '1.5rem', background: 'var(--game-bg-secondary)'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                      <h3 className="game-section__title" style={{margin: 0}}>
                          Registered ({registeredPlayers.length})
                      </h3>
                  </div>
                  
{(() => {
    const unavailableIds = new Set((tournament.players || []).map(p => Number(p.player_id)));
    const registerOptions = players
        .filter(p => !unavailableIds.has(Number(p.id)))
        .map(p => getPlayerOption(p, { showTournamentHistoryWarning: true }));

    return (
<div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', maxWidth: '540px' }}>
    <div style={{ flex: 1, minWidth: '240px' }}>
        <Select
            isMulti
            options={registerOptions}
            filterOption={filterPlayerOption}
            formatOptionLabel={formatPlayerOptionLabel}
            isSearchable
            placeholder="Select players..."
            value={playersToRegister}
            onChange={(value) => setPlayersToRegister(value || [])}
            className="game-select-container"
            classNamePrefix="game-select"
            styles={{
                control: (base) => ({
                    ...base,
                    backgroundColor: "#1a202c",
                    borderColor: "#4a5568",
                    color: "#fff",
                    minHeight: '38px', // Standard height
                }),
                menu: (base) => ({
                    ...base,
                    backgroundColor: "#2d3748",
                }),
                option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                    color: "#e2e8f0",
                }),
                singleValue: (base) => ({
                    ...base,
                    color: "#e2e8f0",
                }),
                input: (base) => ({
                    ...base,
                    color: "#e2e8f0",
                }),
                multiValue: (base) => ({
                    ...base,
                    backgroundColor: "#4a5568",
                }),
                multiValueLabel: (base) => ({
                    ...base,
                    color: "#e2e8f0",
                }),
            }}
        />
    </div>
    <button
        type="button"
        className="game-icon-btn game-icon-btn--primary"
        onClick={() => openQuickAddModal('register')}
        title="Quick Add Player"
        disabled={isSubmitting}
        style={{ width: '38px', height: '38px', flexShrink: 0 }}
    >
        <FaPlus />
    </button>
    <button
        className="game-btn game-btn--primary game-btn--sm"
        style={{ flexShrink: 0 }}
        onClick={handleRegisterPlayersBulk}
        disabled={isSubmitting || playersToRegister.length === 0}
        title="Register selected players"
    >
        Confirm{playersToRegister.length > 0 && ` (${playersToRegister.length})`}
    </button>
</div>
    );
})()}

                  <div className="game-list">
                      {registeredPlayers.map(p => (
                          <div key={p.id} className="game-list-item">
                              <span
                                style={{fontWeight: 'bold', fontSize: '1.1rem', flex: 1}}
                                className="game-player-name"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
                                }}
                              >
                                {p.name}
                              </span>
                              <button onClick={() => openTransactionModal({ type: 'buyin', playerId: p.player_id }, { label: p.name, value: p.player_id })} 
                              className="game-icon-btn game-icon-btn--success" title="Buy In">
                                  <FaDollarSign />
                              </button>
                              <button onClick={() => handleRemoveRegisteredPlayer(p.player_id)} 
                              className="game-icon-btn game-icon-btn--danger" title="Remove">
                                  <FaTimes />
                              </button>
                          </div>
                      ))}
                      {registeredPlayers.length === 0 && <p>No registered players.</p>}
                  </div>
              </div>
              <div className="game-card" style={{padding: '1.5rem', background: 'var(--game-bg-secondary)'}}>
                  <h3 className="game-section__title">
                      Eliminated ({eliminatedPlayers.length})
                  </h3>
                  <div className="game-list">
                      {eliminatedPlayers.map(p => (
                          <div key={p.id} className="game-list-item">
<div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
  <span
    style={{ fontWeight: 'bold', fontSize: '1.1rem' }}
    className="game-player-name"
    onClick={(e) => {
      e.stopPropagation();
      setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
    }}
  >
    {p.name}
  </span>
  {(p.tournament_credit || 0) !== 0 && (
<span className={`game-player-badge ${p.tournament_credit > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{ fontSize: '0.75rem', flexShrink: 0 }}>
  <b>${Math.abs(p.tournament_credit)}</b>&nbsp;{p.tournament_credit > 0 ? 'Credit' : 'Balance'}
</span>
  )}
</div>

                              <span style={{fontSize: '0.9rem', color: '#a0aec0'}}>
                                  {p.elimination_time 
                                      ? new Date(p.elimination_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                                      : ''}
                              </span>
                              <button onClick={() => openTransactionModal({ type: 'rebuy', playerId: p.player_id }, { label: p.name, value: p.player_id })} 
                              className="game-icon-btn game-icon-btn--success" title="Rebuy">
                                  <FaDollarSign />
                              </button>
                               <button
                                 className="game-icon-btn game-icon-btn--info"
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
                                 }}
                                 title="Player History"
                               >
                                 <FaInfo />
                               </button>
                          </div>
                      ))}
                      {eliminatedPlayers.length === 0 && <p>No eliminated players.</p>}
                  </div>
              </div>
          </div>
      </div>
  );

  const renderTables = () => {
      const creditPlayers = (tournament.players || []).filter(p => (p.tournament_credit || 0) !== 0);
      const totalCredit = creditPlayers.reduce((sum, p) => sum + (p.tournament_credit || 0), 0);
      const nextEliminationPayout = payouts.find(pay => pay.place === activePlayers.length);
      const showTournamentAddonButtons = Number(tournament.addons_chips || 0) > 0;

      return (
          <div
              ref={scrollContainerRef}
              onScroll={updateTableScrollAvailability}
              style={{display: 'flex', gap: '2rem', overflowX: 'auto', paddingBottom: '1rem', minHeight: '600px', position: 'relative'}}
          >
              {hasTableHorizontalScroll && (
                  <button
                      type="button"
                      className="game-icon-btn tournament-scroll-arrow tournament-scroll-arrow--left"
                      onClick={() => scrollTablesBy(-320)}
                      title="Scroll left"
                  >
                      <FaBackward />
                  </button>
              )}
              {hasTableHorizontalScroll && (
                  <button
                      type="button"
                      className="game-icon-btn tournament-scroll-arrow tournament-scroll-arrow--right"
                      onClick={() => scrollTablesBy(320)}
                      title="Scroll right"
                  >
                      <FaForward />
                  </button>
              )}

              {/* BALANCES COLUMN */}
              {creditPlayers.length > 0 && (
                  <div
                      className="game-card"
                      style={{minWidth: '280px', background: 'var(--game-bg-secondary)', padding: '1rem', display: 'flex', flexDirection: 'column', border: '1px solid #d69e2e'}}
                  >
                      <h3 style={{margin: '0 0 0.35rem 0', color: '#d69e2e'}}>Balances</h3>
                      <div style={{color: 'var(--game-text-secondary)', fontSize: '0.85rem', marginBottom: '1rem'}}>
                          ${Math.abs(totalCredit)} unsettled balance
                      </div>
                      <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', overflowY: 'auto'}}>
                          {creditPlayers.map(p => (
                          <div
                              key={p.player_id}
                              className="game-unseated-player tournament-balance-row"
                              style={{
                                  border: '1px solid rgba(214,158,46,0.35)',
                                  background: 'rgba(214,158,46,0.08)',
                                  cursor: 'default',
                                  userSelect: 'text'
                              }}
                          >
                              <span style={{fontWeight: 'bold', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{p.name}</span>
<span className={`game-player-badge ${p.tournament_credit > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{ fontSize: '0.78rem', flexShrink: 0 }}>
  <b>${Math.abs(p.tournament_credit)}</b>&nbsp;{p.tournament_credit > 0 ? 'Credit' : 'Balance'}
</span>
                              <div style={{display: 'flex', gap: '0.5rem', pointerEvents: 'auto'}}>
                                  <button
                                      className="game-icon-btn game-icon-btn--info"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
                                      }}
                                      title="Player History"
                                  >
                                      <FaInfo />
                                  </button>
                                  <button
                                      className="game-icon-btn game-icon-btn--success"
                                      title={p.tournament_credit > 0 ? "Collect Credit Payment" : "Cash Out Balance"}
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          setCreditPaybackModal({
                                              isOpen: true,
                                              playerId: p.player_id,
                                              playerName: p.name,
                                              maxCredit: Math.abs(p.tournament_credit),
                                              currentCredit: p.tournament_credit,
                                              amount: Math.abs(p.tournament_credit),
                                              method: 'cash',
                                              walletId: ''
                                          });
                                      }}
                                  >
                                      <FaDollarSign />
                                  </button>
                              </div>
                          </div>
                          ))}
                      </div>
                  </div>
              )}

              {/* REGISTERED COLUMN (Conditional) */}
              {registeredPlayers.length > 0 && (
                  <div 
                      className="game-card" 
                      style={{minWidth: '250px', background: 'var(--game-bg-secondary)', padding: '1rem', display: 'flex', flexDirection: 'column'}}
                  >
                      <h3 style={{margin: '0 0 1rem 0', color: '#a0aec0'}}>Registered ({registeredPlayers.length})</h3>
                      <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', overflowY: 'auto'}}>
                          {registeredPlayers.map(p => (
                              <div 
                                  key={p.player_id}
                                  draggable={!isAddonMode}
                                  onDragStart={(e) => !isAddonMode && handleDragStart(e, p.player_id, true)}
                                  onDragEnd={!isAddonMode ? stopScrolling : undefined}
                                  className="game-unseated-player"
                                  style={{
                                      border: '1px dashed #718096',
                                      background: 'rgba(26, 32, 44, 0.5)'
                                  }}
                              >
                                  <span style={{fontWeight: 'bold', flex: 1}}>{p.name}</span>
                                  <div style={{display: 'flex', gap: '0.5rem', pointerEvents: 'auto'}}>
                                      <button onClick={(e) => { e.stopPropagation(); openTransactionModal({ type: 'buyin', playerId: p.player_id }, { label: p.name, value: p.player_id }); }} 
                                      className="game-icon-btn game-icon-btn--success" title="Buy In">
                                          <FaDollarSign />
                                      </button>
                                      <button onClick={(e) => { e.stopPropagation(); handleRemoveRegisteredPlayer(p.player_id); }} 
                                      className="game-icon-btn game-icon-btn--danger" title="Remove">
                                          <FaTimes />
                                      </button>
                                  </div>
                              </div>
                          ))}
                      </div>
                  </div>
              )}

              {/* UNSEATED COLUMN */}
              <div 
                  className="game-card" 
                  style={{minWidth: '250px', background: 'var(--game-bg-secondary)', padding: '1rem', display: 'flex', flexDirection: 'column'}}
                  onDrop={tablesPlayerListMode === 'unseated' ? (e) => handleDrop(e, null, null) : undefined}
                  onDragOver={tablesPlayerListMode === 'unseated' ? handleDragOver : undefined}
              >
                <div style={{marginBottom: '1rem', borderBottom: '1px solid #4a5568', paddingBottom: '1rem'}}>
<div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
    <button
        className="game-btn game-btn--secondary game-btn--sm"
        style={{ marginTop: '1rem' }}
        onClick={openQuickAddModal}
        disabled={isAddonMode}
    >
        <FaUser /> New Player
    </button>
    <button 
        className="game-btn game-btn--secondary game-btn--sm" 
        onClick={() => {
            openTransactionModal({ type: 'buyin' });
        }}
        disabled={isAddonMode}
    >
        <FaPlus /> Buy In
    </button>
    <button
        className="game-btn game-btn--secondary game-btn--sm"
        style={{ width: '100%' }}
        onClick={() => openTransactionModal({ type: 'rebuy' })}
        disabled={isAddonMode || (activePlayers.length + eliminatedPlayers.length) === 0}
        title={(activePlayers.length + eliminatedPlayers.length) === 0 ? "No eligible players for a rebuy" : ""}
    >
        <FaDollarSign /> Rebuys
    </button>
    
    {!isAddonMode ? (
        showTournamentAddonButtons && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <button 
                className="game-btn game-btn--secondary game-btn--sm" 
                style={{ width: '100%', justifyContent: 'center' }} 
                onClick={() => {
                    setAddonModal({ isOpen: false, isCredit: false });
                    setSelectedAddonPlayers([]);
                    setIsAddonMode(true);
                }}
            >
                Cash Add Ons
            </button>
            <button 
                className="game-btn game-btn--secondary game-btn--sm" 
                style={{ width: '100%', justifyContent: 'center' }} 
                onClick={() => {
                    setAddonModal({ isOpen: false, isCredit: true });
                    setSelectedAddonPlayers([]);
                    setIsAddonMode(true);
                }}
            >
                Credit Add Ons
            </button>
        </div>
        )
    ) : (
        <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
            <button 
                className="game-icon-btn game-icon-btn--danger" 
                style={{ flex: 1 }} 
                onClick={() => { setIsAddonMode(false); setSelectedAddonPlayers([]); }}
            >
                Cancel
            </button>
            <button 
                className="game-icon-btn game-icon-btn--success" 
                style={{ flex: 1 }} 
                onClick={() => setAddonModal(prev => ({ ...prev, isOpen: true }))}
            >
                Confirm ({selectedAddonPlayers.length})
            </button>
        </div>
    )}
</div>
                </div>
<button 
    className="game-btn game-btn--secondary game-btn--sm" 
    style={{ width: '100%', marginBottom: '0.5rem' }} 
    onClick={() => window.open(`/#/${cardroom}/tournaments/${id}/tables`, '_blank')}
>
    <FaUsers /> Seating Chart
</button>
                  <div style={{display: 'flex', marginBottom: '1rem'}}>
                    <div style={{display: 'flex', width: '100%', border: '1px solid #4a5568', borderRadius: '6px', overflow: 'hidden'}}>
                      <button
                        type="button"
                        onClick={() => setTablesPlayerListMode('unseated')}
                        style={{
                          flex: 1,
                          border: 'none',
                          padding: '0.45rem 0.65rem',
                          background: tablesPlayerListMode === 'unseated' ? '#4299e1' : '#1a202c',
                          color: tablesPlayerListMode === 'unseated' ? '#fff' : '#a0aec0',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Unseated
                      </button>
                      <button
                        type="button"
                        onClick={() => setTablesPlayerListMode('eliminated')}
                        style={{
                          flex: 1,
                          border: 'none',
                          borderLeft: '1px solid #4a5568',
                          padding: '0.45rem 0.65rem',
                          background: tablesPlayerListMode === 'eliminated' ? '#4299e1' : '#1a202c',
                          color: tablesPlayerListMode === 'eliminated' ? '#fff' : '#a0aec0',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Eliminated
                      </button>
                    </div>
                  </div>
                  <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', overflowY: 'auto'}}>
                    {tablesPlayerListMode === 'unseated' ? (
                      <>
                      {unseatedPlayers.map(p => (
                          <div 
                              key={p.player_id}
                              draggable={!isAddonMode}
                              onDragStart={(e) => !isAddonMode && handleDragStart(e, p.player_id)}
                              onDragEnd={!isAddonMode ? stopScrolling : undefined}
                              onClick={() => isAddonMode && handleAddonToggle(p.player_id)}
                              className="game-unseated-player"
                              style={{
                                  ...(isAddonMode ? { cursor: 'pointer' } : {}),
                                  ...(selectedAddonPlayers.includes(p.player_id) ? { borderColor: 'var(--game-success)', background: 'rgba(72, 187, 120, 0.2)' } : {})
                              }}
                          >
                              <span style={{fontWeight: 'bold'}}>{p.name}</span>
                              <div style={{display: 'flex', gap: '0.5rem', marginLeft: 'auto', pointerEvents: isAddonMode ? 'none' : 'auto', opacity: isAddonMode ? 0.3 : 1}}>

                                  <button onClick={(e) => { e.stopPropagation(); openTransactionModal({ type: 'rebuy', playerId: p.player_id }, { label: p.name, value: p.player_id }); }} 
                                  className="game-icon-btn game-icon-btn--success" title="Rebuy">
                                      <FaDollarSign />
                                  </button>
                                  <button
                                    className="game-icon-btn game-icon-btn--info"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
                                    }}
                                    title="Player History"
                                  >
                                    <FaInfo />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleEliminate(p.player_id); }}
                                    className={`game-icon-btn ${nextEliminationPayout ? 'game-icon-btn--warning' : 'game-icon-btn--danger'}`}
                                    title={nextEliminationPayout ? `Pay out $${nextEliminationPayout.amount}` : "Eliminate"}
                                  >
                                      {nextEliminationPayout ? `$${nextEliminationPayout.amount}` : <FaTimes />}
                                  </button>
                              </div>
                          </div>
                      ))}
                      {unseatedPlayers.length === 0 && <div style={{color: '#718096', fontStyle: 'italic', padding: '1rem', textAlign: 'center'}}>Drop here to unseat</div>}
                      </>
                    ) : (
                      <>
                        {eliminatedPlayers.map(p => (
                          <div key={p.id || p.player_id} className="game-list-item">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
                              <span
                                style={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                                className="game-player-name"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
                                }}
                              >
                                {p.name}
                              </span>
                              {(p.tournament_credit || 0) !== 0 && (
                                <span className={`game-player-badge ${p.tournament_credit > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{ fontSize: '0.75rem', flexShrink: 0 }}>
                                  <b>${Math.abs(p.tournament_credit)}</b>&nbsp;{p.tournament_credit > 0 ? 'Credit' : 'Balance'}
                                </span>
                              )}
                            </div>
                            <span style={{fontSize: '0.9rem', color: '#a0aec0'}}>
                              {p.elimination_time
                                ? new Date(p.elimination_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                                : ''}
                            </span>
                            <button
                              onClick={() => openTransactionModal({ type: 'rebuy', playerId: p.player_id }, getPlayerOption(p))}
                              className="game-icon-btn game-icon-btn--success"
                              title="Rebuy"
                            >
                              <FaDollarSign />
                            </button>
                            <button
                              className="game-icon-btn game-icon-btn--info"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPlayerHistoryModal({ isOpen: true, playerId: p.player_id, playerName: p.name });
                              }}
                              title="Player History"
                            >
                              <FaInfo />
                            </button>
                          </div>
                        ))}
                        {eliminatedPlayers.length === 0 && <p style={{color: '#718096', fontStyle: 'italic', padding: '1rem', textAlign: 'center'}}>No eliminated players.</p>}
                      </>
                    )}
                  </div>
              </div>

              {/* TABLES */}
              {tables.map(table => {
                  const tablePlayers = activePlayers.filter(p => p.table_id === table.id);
                  const seats = Array.from({length: 10}, (_, i) => i + 1);

                  return (
                      <div key={table.id} className="game-card" style={{minWidth: '300px', background: 'var(--game-bg-secondary)', padding: '1rem', height: 'fit-content'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                              {editingTableId === table.id ? (
                                  <input
                                      autoFocus
                                      type="text"
                                      value={editingTableName}
                                      onChange={(e) => setEditingTableName(e.target.value)}
                                      onBlur={() => handleRenameTable(table.id)}
                                      onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleRenameTable(table.id);
                                          if (e.key === 'Escape') setEditingTableId(null);
                                      }}
                                      style={{margin: 0, background: '#1a202c', border: '1px solid #4299e1', color: '#e2e8f0', padding: '0.3rem 0.5rem', borderRadius: '4px', fontSize: '1rem', fontWeight: 700, width: '100%', outline: 'none'}}
                                  />
                              ) : (
                                  <h3
                                      style={{margin: 0, color: '#e2e8f0', cursor: 'pointer'}}
                                      title="Click to rename"
                                      onClick={() => { setEditingTableId(table.id); setEditingTableName(table.name || `Table ${table.table_number}`); }}
                                  >
                                      {table.name || `Table ${table.table_number}`}
                                  </h3>
                              )}
                              {tablePlayers.length === 0 && (
                                  <button className="game-icon-btn game-icon-btn--danger" onClick={() => setDeleteTableModal({ isOpen: true, tableId: table.id, tableName: table.name || `Table ${table.table_number}` })}><FaTimes /></button>
                              )}
                          </div>
                          {activePlayers.length > 0 && activePlayers.length < 10 && (
                              <button
                                  className="game-btn game-btn--secondary game-btn--sm"
                                  style={{ width: '100%', marginBottom: '1rem', justifyContent: 'center' }}
                                  onClick={() => openFinalTableModal(table)}
                                  disabled={isSubmitting || isAddonMode}
                              >
                                  {tables.length === 1 ? 'Randomize' : 'Make Final Table'}
                              </button>
                          )}
                          
                          <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                              {seats.map(seatNumber => {
                                  const seatedPlayer = tablePlayers.find(p => p.seat_number === seatNumber);
                                  return (
                                      <div 
                                          key={seatNumber}
                                          draggable={!!seatedPlayer && !isAddonMode}
                                          onDragStart={(e) => seatedPlayer && !isAddonMode && handleDragStart(e, seatedPlayer.player_id)}
                                          onDragEnd={seatedPlayer && !isAddonMode ? stopScrolling : undefined}
                                          onDrop={(e) => handleDrop(e, table.id, seatNumber)}
                                          onDragOver={handleDragOver}
                                          onClick={() => seatedPlayer && isAddonMode && handleAddonToggle(seatedPlayer.player_id)}
                                          style={{
                                              display: 'flex', alignItems: 'center', padding: '0.5rem', 
                                              background: seatedPlayer && selectedAddonPlayers.includes(seatedPlayer.player_id) ? 'rgba(72, 187, 120, 0.25)' : (seatedPlayer ? '#4a5568' : '#1a202c'), 
                                              border: seatedPlayer && selectedAddonPlayers.includes(seatedPlayer.player_id) ? '1px solid #48bb78' : '1px dashed #4a5568',
                                              borderRadius: '4px', minHeight: '40px', transition: 'background 0.15s ease, border-color 0.15s ease',
                                              cursor: seatedPlayer ? (isAddonMode ? 'pointer' : 'grab') : 'default'
                                          }}
                                      >
                                          <div style={{width: '20px', fontWeight: 'bold', color: '#a0aec0'}}>{seatNumber}</div>
                                          {seatedPlayer ? (
                                              <div 
                                                  style={{
                                                      flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                      padding: '0', borderRadius: '4px'
                                                  }}
                                              >
                                                <div style={{display: 'flex', alignItems: 'center', gap: '0.3rem'}}>
                                                    <button
                                                    onClick={(e) => {
                                                        if (isAddonMode) return;
                                                        e.stopPropagation();
                                                            handleUnseat(seatedPlayer.player_id);
                                                    }}
                                                    className="game-icon-btn game-icon-btn--sm"
                                                    title={isAddonMode ? "Select row for add-on" : "Unseat Player"}
                                                    style={isAddonMode ? { opacity: 0.3, pointerEvents: 'none' } : undefined}>
                                                        <FaArrowCircleUp />
                                                    </button>
                                                    <span style={{fontWeight: 'bold'}}>{seatedPlayer.name}</span>
                                                    {(seatedPlayer.tournament_credit || 0) !== 0 && (
<span className={`game-player-badge ${seatedPlayer.tournament_credit > 0 ? 'game-player-badge--session-yellow' : 'game-player-badge--credit'}`} style={{ fontSize: '0.75rem', flexShrink: 0 }}>
  <b>${Math.abs(seatedPlayer.tournament_credit)}</b>
</span>
                                                    )}
                                                </div>

                                                  <div style={{display: 'flex', gap: '0.5rem', pointerEvents: isAddonMode ? 'none' : 'auto', opacity: isAddonMode ? 0.3 : 1}}>
                                                      <button onClick={(e) => { e.stopPropagation(); openTransactionModal({ type: 'rebuy', playerId: seatedPlayer.player_id }, { label: seatedPlayer.name, value: seatedPlayer.player_id }); }} 
                                                      className="game-icon-btn game-icon-btn--success" title="Rebuy">
                                                          <FaDollarSign />
                                                      </button>
                                                      <button
                                                        className="game-icon-btn game-icon-btn--info"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setPlayerHistoryModal({ isOpen: true, playerId: seatedPlayer.player_id, playerName: seatedPlayer.name });
                                                        }}
                                                        title="Player History"
                                                      >
                                                        <FaInfo />
                                                      </button>
                                                      <button
                                                      onClick={(e) => { e.stopPropagation(); handleEliminate(seatedPlayer.player_id); }}
                                                      className={`game-icon-btn ${nextEliminationPayout ? 'game-icon-btn--warning' : 'game-icon-btn--danger'}`}
                                                      title={nextEliminationPayout ? `Pay out $${nextEliminationPayout.amount}` : "Eliminate"}>
                                                          {nextEliminationPayout ? `$${nextEliminationPayout.amount}` : <FaTimes />}
                                                      </button>
                                                  </div>
                                              </div>
                                          ) : (
                                            <div 
                                                onClick={() => {
                                                    openTransactionModal({
                                                        type: 'buyin',
                                                        presetTable: table.id,
                                                        presetSeat: seatNumber
                                                    });
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.color = '#3182ce';
                                                    e.currentTarget.style.textDecoration = 'underline';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.color = '#4a5568';
                                                    e.currentTarget.style.textDecoration = 'none';
                                                }}
                                                style={{ flex: 1, color: '#4a5568', fontStyle: 'italic', cursor: 'pointer' }}
                                            >
                                                Empty Seat
                                            </div>
                                          )}
                                      </div>
                                  );
                              })}
                          </div>
                      </div>
                  );
              })}

              <div className="game-card" style={{minWidth: '300px', background: 'var(--game-bg-secondary)', padding: '1rem', height: 'fit-content'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem'}}>
                      <input
                          type="text"
                          placeholder={`Table ${tables.length + 1}`}
                          value={newTableName}
                          onChange={(e) => setNewTableName(e.target.value)}
                          style={{
                              margin: 0,
                              background: '#1a202c',
                              border: '1px solid #4a5568',
                              color: '#e2e8f0',
                              padding: '0.3rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '1rem',
                              fontWeight: 700,
                              width: '100%',
                              outline: 'none',
                              boxSizing: 'border-box',
                          }}
                      />
                      <button
                          className="game-icon-btn game-icon-btn--primary"
                          onClick={handleAddTable}
                          title="Add Table"
                          disabled={isSubmitting || isAddonMode}
                      >
                          <FaPlus />
                      </button>
                  </div>
              </div>

          </div>
      );
  };

  const renderCashBagHistoryModal = () => {
      if (!isCashBagHistoryOpen) return null;

      const cashBagTransactions = [
          ...(tournament?.transactions || [])
              .filter(t => t.method === 'cash')
              .map(t => ({
                  id: `tx-${t.id}`,
                  completed_at: t.timestamp,
                  amount: parseFloat(t.amount),
                  type: t.transaction_type,
                  name: t.player_name,
                  wallet_name: t.wallet_name,
                  table_name: t.table_name
              })),
          ...(costs || [])
              .filter(c => c.method === 'cash' || !c.wallet_id)
              .map(c => ({
                  id: `cost-${c.id}`,
                  completed_at: c.completed_at,
                  amount: parseFloat(c.amount),
                  type: 'cost',
                  name: c.notes
              }))
      ].sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

      return (
          <>
              <div className="game-modal-overlay" onClick={() => setIsCashBagHistoryOpen(false)} />
              <div className="game-modal game-modal--large">
                  <div className="game-modal__header">
                      <h3 className="game-modal__title">Tournament Cash Bag History</h3>
                      <button className="game-icon-btn" onClick={() => setIsCashBagHistoryOpen(false)}><FaTimes /></button>
                  </div>
                  <div className="game-modal__body game-modal__body--scroll">
                      {cashBagTransactions.length === 0 ? (
                          <p className="game-empty">No cash bag transactions found.</p>
                      ) : (
                          <div className="game-transaction-list">
                              {cashBagTransactions.map((t) => (
                                  <div key={t.id} className="game-transaction-item">
                                      <span className="game-transaction-time">
                                          {new Date(t.completed_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                                      </span>
                                      
                                      <span className={`game-transaction-amount ${
                                          t.type === 'cashout' ? "game-text-danger" :
                                          (t.amount > 0 && t.type !== 'cost') ? "game-text-success" : 
                                          "game-text-warning"
                                      }`}>
                                          ${t.amount.toFixed(0)}
                                      </span>

                                      <span className="game-transaction-type" style={{ marginLeft: "1rem", marginRight: "1rem", minWidth: "100px" }}>
                                          {t.type.toUpperCase()}
                                      </span>

                                      {t.name && (
                                          <span className="game-transaction-method" style={{ flex: 1 }}>
                                              {t.name}
                                          </span>
                                      )}
                                      {!t.name && t.wallet_name && (
                                          <span className="game-transaction-method" style={{ flex: 1 }}>
                                              {t.wallet_name}
                                          </span>
                                      )}

                                      <span className="game-transaction-date" style={{ marginLeft: "auto" }}>
                                          {new Date(t.completed_at).toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}
                                      </span>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
              </div>
          </>
      );
  };

  return (
    <div className="game" onWheelCapture={preventNumberInputWheel}>
       <div className="game-container">
           <div style={{display: 'flex', justifyContent: 'center', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem'}}>
               <button className={`game-btn ${activeTab === 'dashboard' ? 'game-btn--primary' : 'game-btn--secondary'}`} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
               <button className={`game-btn ${activeTab === 'transactions' ? 'game-btn--primary' : 'game-btn--secondary'}`} onClick={() => setActiveTab('transactions')}>Transactions</button>
               <button className={`game-btn ${activeTab === 'tables' ? 'game-btn--primary' : 'game-btn--secondary'}`} onClick={() => setActiveTab('tables')}>Tables</button>
               <div style={{width: '2px', height: '2rem', background: '#4a5568', margin: '0 0.25rem'}}></div>
               <button className="game-btn game-btn--secondary" onClick={() => window.open(`/#/${cardroom}/tournaments/${id}/display`, '_blank')}>Display</button>
               {activePlayers.length === 0 && tournament.state !== 'finished' && (
                   <button className="game-btn game-btn--danger" onClick={handleEndTournament}>
                       {hasNoTournamentPlayers ? 'Cancel' : 'End Tournament'}
                   </button>
               )}
           </div>
           {renderTableModal()}
           {renderEliminationModal()}
           {renderDeleteTableModal()}
           {renderUndoTransactionModal()}
           {renderWinnerTipModal()}
           {renderCashRefillModal()}
           {renderFinalTableModal()}
           {renderEndTournamentModal()}
           {renderQuickAddModal()}
           {renderAddonModal()}
           {renderPlayerHistoryModal()}
           {renderStructureModal()}
           {renderSettingsModal()}
           {renderCashBagHistoryModal()}
           {renderEndTournamentModal()}

           {/* CREDIT PAYBACK MODAL  */}
           {creditPaybackModal.isOpen && (
             <>
               <div className="game-modal-overlay" onClick={() => setCreditPaybackModal({ ...creditPaybackModal, isOpen: false })} />
               <div className="game-modal">
                 <div className="game-modal__header">
                   <h3 className="game-modal__title">{Number(creditPaybackModal.currentCredit || 0) < 0 ? 'Cash Out Balance' : 'Collect Credit Payment'}</h3>
                   <div className="game-modal__header-badges">
<span className={`game-badge ${Number(creditPaybackModal.currentCredit || 0) < 0 ? 'game-badge--credit' : 'game-badge--session-yellow'}`}>
  <b>${creditPaybackModal.maxCredit}</b>&nbsp;{Number(creditPaybackModal.currentCredit || 0) < 0 ? 'Balance' : 'Credit'}
</span>
                   </div>
                 </div>
                 <div className="game-modal__body">
                   <form
                     className="game-form-vertical"
                     onSubmit={async (e) => {
                       e.preventDefault();
                       if (isSubmitting) return;
                       setIsSubmitting(true);
                       try {
                         await axios.post(`/${cardroom}/api/tournaments/${id}/credit-payback`, {
                           playerId: creditPaybackModal.playerId,
                           amount: Number(creditPaybackModal.amount),
                           method: creditPaybackModal.method,
                           walletId: creditPaybackModal.method !== 'cash' ? creditPaybackModal.walletId : null
                         });
                         setCreditPaybackModal({ isOpen: false, playerId: null, playerName: '', maxCredit: 0, currentCredit: 0, amount: '', method: 'cash', walletId: '' });
                         loadData();
                       } catch (err) {
                         alert(err.response?.data?.error || 'Payment failed');
                       } finally {
                         setIsSubmitting(false);
                       }
                     }}
                   >
                     <p style={{ margin: '0 0 1rem', color: 'var(--game-text-secondary)' }}>
                       {Number(creditPaybackModal.currentCredit || 0) < 0 ? 'Paying tournament balance to ' : 'Collecting credit payment from '}
                       <b style={{ color: '#fff' }}>{creditPaybackModal.playerName}</b>.
                       Enter the amount {Number(creditPaybackModal.currentCredit || 0) < 0 ? 'to cash out' : 'they are paying back'} (max <b>${creditPaybackModal.maxCredit}</b>).
                     </p>

                     <label style={{ color: 'var(--game-text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>Amount</label>
                     <input
                       type="number"
                       className="game-input"
                       step="1"
                       min="1"
                       max={creditPaybackModal.maxCredit}
                       placeholder={`Max $${creditPaybackModal.maxCredit}`}
                       value={creditPaybackModal.amount}
                       onFocus={handleNumberFocus}
                       onChange={e => setCreditPaybackModal({ ...creditPaybackModal, amount: e.target.value })}
                       required
                       autoFocus
                     />

                     <label style={{ color: 'var(--game-text-secondary)', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.5rem' }}>Payment Method</label>
                     <select
                       className="game-input"
                       value={creditPaybackModal.method}
                       onChange={e => setCreditPaybackModal({ ...creditPaybackModal, method: e.target.value, walletId: '' })}
                     >
                       <option value="cash">Cash</option>
                       <option value="venmo">Venmo</option>
                       <option value="cashapp">CashApp</option>
                       <option value="bitcoin">Bitcoin</option>
                       <option value="zelle">Zelle</option>
                       <option value="applePay">Apple Pay</option>
                       <option value="paypal">PayPal</option>
                     </select>

                     {creditPaybackModal.method !== 'cash' && (
                       <>
                         <label style={{ color: 'var(--game-text-secondary)', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.5rem' }}>Wallet</label>
                         <select
                           className="game-input"
                           value={creditPaybackModal.walletId}
                           onChange={e => setCreditPaybackModal({ ...creditPaybackModal, walletId: e.target.value })}
                           required
                         >
                           <option value="">Select Wallet</option>
                           {tournamentWallets.map(w => (
                             <option key={w.id} value={w.id}>{w.name}</option>
                           ))}
                         </select>
                       </>
                     )}

                     <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                       <button
                         type="button"
                         className="game-btn game-btn--secondary"
                         style={{ flex: 1 }}
                         onClick={() => setCreditPaybackModal({ isOpen: false, playerId: null, playerName: '', maxCredit: 0, currentCredit: 0, amount: '', method: 'cash', walletId: '' })}
                       >
                         Cancel
                       </button>
                       <button
                         type="submit"
                         className="game-btn game-btn--success"
                         style={{ flex: 1 }}
                         disabled={
                           isSubmitting ||
                           !creditPaybackModal.amount ||
                           Number(creditPaybackModal.amount) <= 0 ||
                           Number(creditPaybackModal.amount) > creditPaybackModal.maxCredit ||
                           (creditPaybackModal.method !== 'cash' && !creditPaybackModal.walletId)
                         }
                       >
                         {Number(creditPaybackModal.currentCredit || 0) < 0 ? 'Cash Out' : 'Collect'} ${creditPaybackModal.amount || 0}
                       </button>
                     </div>
                   </form>
                 </div>
               </div>
             </>
           )}

           {/* ADD-ON MODE BANNER */}
{isAddonMode && (
    <div style={{
        position: 'fixed', 
        bottom: '2rem', 
        left: '50%', 
        transform: 'translateX(-50%)',
        background: 'var(--game-primary)', 
        color: '#fff', 
        padding: '1rem 2rem', 
        borderRadius: '30px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.3)', 
        zIndex: 1000, 
        fontWeight: 'bold', 
        display: 'flex', 
        flexDirection: 'column', // Stacks the lines vertically
        alignItems: 'center',    // Centers the flex items horizontally
        textAlign: 'center',     // Centers the text inside each line
        gap: '0.25rem'           // Optional: adds a small space between lines
    }}>
        Add-On Mode Activated: Click players to select them. <br /> 
        Press ENTER to confirm <br /> 
        Press ESC to cancel.
    </div>
)}

           {activeTab === 'dashboard' && renderDashboard()}
           {activeTab === 'transactions' && renderTransactions()}
           {activeTab === 'tables' && renderTables()}
       </div>
    </div>
  );
}

